import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import OrgModuleLayout from '../OrgModuleLayout';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useCursorPagination, type CursorPaginationState } from '@app/hooks/useCursorPagination';
import {
  useSitesApi,
  useConnectorClustersApi,
  useConnectorsApi,
  useVisibilityApi,
  useDevicesApi,
  useCMDBReconciliationApi,
  useDiscoveryApi,
  useDashboardsApi,
  useInventoryApi,
  useIntegrationsApi,
  useTopologyApi
} from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useGrafanaEmbedSessionKeepalive } from '@app/hooks/useGrafanaEmbedSessionKeepalive';
import ShortcutButton from '@app/components/ShortcutButton';
import ListWorkspace, { type ListWorkspaceColumn, type ListWorkspaceProps } from '@app/components/ListWorkspace';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import Tabs from '@app/components/Tabs';
import CopyField from '@app/components/CopyField';
import AwsTelemetryLauncher from '@app/components/AwsTelemetryLauncher';
import {
  ChartIcon,
  CloseIcon,
  CompressIcon,
  DownloadIcon,
  ExpandIcon,
  EyeIcon,
  ForkIcon,
  GraphIcon,
  LayersIcon,
  NetworkIcon,
  PauseIcon,
  PencilIcon,
  RadarIcon,
  SaveIcon,
  ShieldIcon,
  UserIcon
} from '@app/components/icons';
import GrafanaIframeCard from '@app/components/GrafanaIframeCard';
import SignalsSummary from '@app/components/SignalsSummary';
import { formatDeviceOS, getDeviceOsName } from '@app/utils/deviceDisplay';
import { formatDeviceRoleLabel } from '@app/utils/deviceRoles';
import { formatDateTime, formatDuration, formatNumber } from '@app/utils/format';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth, resolveBasePath } from '@app/utils/api';
import { navigatePendingTab, openPendingTab } from '@app/utils/aws-launch';
import { getConfidenceBadgeClass } from '@app/utils/confidence';
import { resolveErrorMessage } from '@app/utils/errors';
import {
  describeProviderEdgeEvidence,
  formatProviderEdgeConfidence,
  formatProviderEdgeLabel,
  normalizeProviderEdgePorts,
  providerEdgeEffectiveRole,
  providerEdgeClassificationTone,
  shouldDisplayProviderEdgeLink,
  type ProviderEdgePortRecord
} from '@app/utils/providerEdge';
import { computeTimeRange, type TimeRangePreset } from '@app/utils/time';
import { useOrgChangefeedEffect } from '@app/context/org-changefeed';
import { useConfig } from '@config/provider';
import DiscoveryJobDrawer from '../discovery/DiscoveryJobDrawer';
import DiscoveryRunDrawer from '../discovery/DiscoveryRunDrawer';
import DiscoveryRunDetailDrawer, { inferDeviceFamily } from '../discovery/DiscoveryRunDetailDrawer';
import TopologyV2Page from '../topology/TopologyV2Page';
import { buildGrafanaSoloUrl } from '../GrafanaExplorerPage';
import PingMeshEndpointAutocomplete, { isPingMeshIPAddress } from '@app/components/topology/PingMeshEndpointAutocomplete';
import { selectDiscoveryJobState, selectDiscoveryRunsForJob } from './discoverySelection';
import {
  computeV1TopologyLayout,
  v1TopologyEdgePresentation,
  type V1TopologyClassification,
  type V1TopologyDevice,
  type V1TopologyDeviceClass
} from './v1TopologyLayout';
import type {
  CMDBDiscrepancyReport,
  Site,
  SiteUpdate,
  ConnectorCluster,
  ClusterSitesAttachRequest,
  ClusterSitesDetachRequest,
  Connector,
  Device,
  InventoryCMDBConnection,
  IntegrationConnection,
  TopologyGraph,
  DiscoveryJobsListResponse,
  DiscoveryRunsListResponse,
  DiscoveryJobschema,
  DiscoveryRun,
  DiscoveryJob,
  DashboardsRecommendedResponse,
  DashboardWidgetSpec
} from '@app/types/sdk';
import { ResponseError, SiteSiteTypeEnum, TopologyPathModeValues, TopologyPathRequestContextschemaLaunchSurfaceEnum } from '@app/types/sdk';
import type { TopologyPathEndpointSearchRequest, TopologyPathEndpointSearchResponse } from '@app/types/sdk';
import type { CloudAccessConnection } from '@app/types/sdk';
import cytoscape, { type Core, type ElementDefinition, type CytoscapeOptions } from 'cytoscape';
import { describeDiscoverySchedule, formatDiscoveryTriggerScope } from '../discovery/helpers';
import {
  SITE_TYPE_OPTIONS,
  inferSiteType,
  labelPairsToRecord,
  siteTopologyV2ViewId,
  toSiteCreatePayload,
  toSiteUpdatePayload,
  validateSiteLocationFields,
  type KeyValuePair,
  type SiteFormValues,
  type SiteLocationFieldErrors,
  type SiteType
} from './siteForm';
import {
  buildCloudAccessRows,
  cloudAccessRowKey,
  cloudAccessSetupAction,
  cloudProviderLabel,
  type CloudAccessView
} from './cloudAccess';

import { loadCloudAccessCatalog, pollCloudAccessVerification } from './cloudAccessRequests';

type ConnectivityTab = 'sites' | 'clusters' | 'connectors' | 'cloud-access';
interface ListState<T> {
  items: T[];
  loading: boolean;
  error: string | null;
}

type SiteView = Omit<Site, 'providerEdgePorts' | 'connectorCount' | 'deviceCounts'> & {
  labels?: Record<string, string>;
  connectorCount?: number | null;
  health?: string | null;
  deviceCounts?: { total?: number | null; active?: number | null; offline?: number | null } | null;
  deviceTypeCounts?: SiteDeviceTypeSummary[] | null;
  vendorCounts?: SiteVendorSummary[] | null;
  providerEdgePorts?: ProviderEdgePortRecord[] | null;
};

type ClusterView = Omit<ConnectorCluster, 'connectorsOnline' | 'connectorsTotal'> & {
  labels?: Record<string, string>;
  connectorsOnline?: number | null;
  connectorsTotal?: number | null;
  haState?: string | null;
  health?: string | null;
  notes?: string | null;
  settings?: {
    internalInspection?: {
      enabled?: boolean | null;
    } | null;
  } | null;
};

type ConnectorView = Connector & {
  labels?: Record<string, string>;
  siteHints?: string[];
  locationHint?: string;
};

type SiteDetailTab = 'details' | 'cockpit' | 'topology' | 'clusters' | 'connectors' | 'ping-mesh' | 'discover' | 'inventory';
type ClusterDetailTab = 'details' | 'sites' | 'connectors';
type ConnectorDetailTab = 'details' | 'bootstrap';
type SiteDeviceTypeIconKind = ReturnType<typeof inferDeviceFamily>['iconKind'];

interface SiteDeviceTypeSummary {
  key: string;
  label: string;
  count: number;
  iconKind: SiteDeviceTypeIconKind;
  detail?: string;
}

interface SiteVendorSummary {
  key: string;
  label: string;
  count: number;
}

interface ConnectivitySummaryCard {
  key: string;
  label: string;
  value: number;
  detail: string;
  iconKind: SiteDeviceTypeIconKind;
  tone?: 'success' | 'info' | 'warning' | 'danger';
}

interface SiteCMDBState {
  connections: InventoryCMDBConnection[];
  loadingConnections: boolean;
  report: CMDBDiscrepancyReport | null;
  loadingReport: boolean;
  error: string | null;
}

type BulkNetBoxImportRow = {
  netBoxSite: { id: string; name: string };
  reapSiteId?: string;
  run?: { id: string; status: string };
  logs?: string[];
  error?: string;
};

type CSVImportDraft = {
  /** Immutable upload/run ID used by preview and confirm endpoints. */
  runId: string;
  fileName: string;
  entityType: string;
  headers: string[];
  mapping: Record<string, string>;
  rows: Array<Record<string, string>>;
  errors: Array<{ row?: number; field?: string; reason?: string }>;
  run?: { id?: string; status?: string };
};

type CSVImportSchema = { key: string; label: string; entityType: string; description: string; requiredHeaders: string[]; columnMapping: Record<string, string>; mappingEditable: boolean };

type InventorySourceCatalogItem = { key: string; label: string; description: string; workflow: 'incremental_file_import' | 'provider_site_import' | 'informational'; provider?: string };
const CSV_SITE_FIELDS = [
  { key: 'external_id', label: 'Source site key' },
  { key: 'site_name', label: 'Site name', required: true }
];


const DISCOVERY_CHANGE_BATCH_MS = 1200;
const DISCOVERY_CHANGE_MIN_REFRESH_GAP_MS = 4000;
const DISCOVERY_ACTIVE_RUN_POLL_MS = 7000;
const SITE_DATA_CHANGE_BATCH_MS = 1500;
const SITE_DATA_CHANGE_MIN_REFRESH_GAP_MS = 5000;

const createInitialSiteCMDBState = (): SiteCMDBState => ({
  connections: [],
  loadingConnections: false,
  report: null,
  loadingReport: false,
  error: null
});

interface SiteDetailState {
  loading: boolean;
  error: string | null;
  record: SiteView | null;
  etag: string | null;
  clusters: ClusterView[];
  connectors: ConnectorView[];
  connectorsNext: string | null;
  connectorsLoadingMore: boolean;
  connectorsError: string | null;
  activeTab: SiteDetailTab;
  saving: boolean;
  topology: TopologyGraph | null;
  topologyLoading: boolean;
  topologyExpandInterfaces: boolean;
}

interface ClusterDetailState {
  loading: boolean;
  error: string | null;
  record: ClusterView | null;
  etag: string | null;
  sites: SiteView[];
  sitesNext: string | null;
  sitesLoadingMore: boolean;
  sitesError: string | null;
  connectors: ConnectorView[];
  connectorsNext: string | null;
  connectorsLoadingMore: boolean;
  connectorsError: string | null;
  activeTab: ClusterDetailTab;
  saving: boolean;
}

interface ConnectorDetailState {
  loading: boolean;
  error: string | null;
  record: ConnectorView | null;
  etag: string | null;
  activeTab: ConnectorDetailTab;
  saving: boolean;
  userdataStatus: 'idle' | 'loading' | 'success' | 'error';
  userdataError?: string | null;
  resetStatus: 'idle' | 'loading' | 'success' | 'error';
  resetError?: string | null;
}

interface ClusterFormValues {
  name: string;
  description?: string;
  labels: KeyValuePair[];
  internalInspectionEnabled: boolean;
}

interface ConnectorFormValues {
  name: string;
  clusterId: string;
  description?: string;
  labels: KeyValuePair[];
}

type WorkspaceProps<T> = Omit<ListWorkspaceProps<T>, 'tabs' | 'activeTabId' | 'onTabChange'>;

const hasStableId = <T extends { id?: string | null }>(item: T): item is T & { id: string } => typeof item.id === 'string' && item.id.length > 0;

export const appendUniqueById = <T extends { id?: string | null }>(current: T[], incoming: T[]): T[] => {
  const knownIds = new Set(current.flatMap((item) => (item.id ? [item.id] : [])));
  return current.concat(
    incoming.filter((item) => {
      if (!item.id || !knownIds.has(item.id)) {
        if (item.id) {
          knownIds.add(item.id);
        }
        return true;
      }
      return false;
    })
  );
};

export const normalizeConnectivityQuery = (query: string): string => query.trim();

export const shouldResetConnectivityPagination = (currentQuery: string, pendingQuery: string): boolean =>
  currentQuery !== normalizeConnectivityQuery(pendingQuery);

export const isLatestConnectivityRequest = (requestID: number, latestRequestID: number): boolean =>
  requestID === latestRequestID;

export const isActiveConnectivityOrg = (requestOrgId: string, activeOrgId: string | null): boolean =>
  requestOrgId === activeOrgId;

const toLabelPairs = (labels?: Record<string, string> | null): KeyValuePair[] => {
  if (!labels) {
    return [];
  }
  return Object.entries(labels).map(([key, value]) => ({ key, value: String(value ?? '') }));
};

const getSiteAwsRegion = (site?: Pick<SiteView, 'awsRegion'> | null): string =>
  typeof site?.awsRegion === 'string' ? site.awsRegion.trim() : '';

const getSiteTypeForForm = (
  site?: Pick<SiteView, 'siteType' | 'awsRegion' | 'awsAccountId' | 'azureSubscriptionId' | 'azureLocation'> | null
): SiteType | null => inferSiteType(site);

const getSiteLocationOrScope = (site: SiteView): string => {
  const siteType = getSiteTypeForForm(site);
  if (siteType === SiteSiteTypeEnum.AwsCloud) {
    return getSiteAwsRegion(site) || '—';
  }
  if (siteType === SiteSiteTypeEnum.AzureCloud) {
    return site.azureSubscriptionId?.trim() || '—';
  }
  if (siteType === SiteSiteTypeEnum.OnPrem) {
    return [site.address?.city, site.address?.country].filter(Boolean).join(', ') || '—';
  }
  return 'Unsupported Site type';
};

const siteToFormValues = (site?: SiteView | null): SiteFormValues | null => {
  const siteType = getSiteTypeForForm(site);
  if (!siteType) {
    return null;
  }
  return {
    name: site?.name ?? '',
    siteType,
    awsRegion: getSiteAwsRegion(site),
    awsAccountId: site?.awsAccountId ?? '',
    azureSubscriptionId: site?.azureSubscriptionId ?? '',
    azureLocation: site?.azureLocation ?? '',
    notes: site?.notes ?? '',
    address: {
      line1: site?.address?.line1 ?? '',
      line2: site?.address?.line2 ?? '',
      city: site?.address?.city ?? '',
      state: site?.address?.state ?? '',
      postalCode: site?.address?.postalCode ?? '',
      country: site?.address?.country ?? ''
    },
    labels: toLabelPairs(site?.labels)
  };
};

const toSdkSite = (site: SiteView): Site => {
  const { providerEdgePorts: _providerEdgePorts, ...rest } = site;
  return rest as Site;
};

const toSdkCluster = (cluster: ClusterView): ConnectorCluster => ({
  ...cluster,
  connectorsTotal: cluster.connectorsTotal ?? undefined,
  connectorsOnline: cluster.connectorsOnline ?? undefined
});

const clusterToFormValues = (cluster?: ClusterView | null): ClusterFormValues => ({
  name: cluster?.name ?? '',
  description: cluster?.description ?? '',
  labels: toLabelPairs(cluster?.labels),
  internalInspectionEnabled: Boolean(cluster?.settings?.internalInspection?.enabled)
});

const connectorToFormValues = (connector?: Partial<ConnectorView> | null): ConnectorFormValues => {
  const description =
    connector && typeof connector === 'object' && 'description' in connector ? ((connector as { description?: string }).description ?? '') : '';

  return {
    name: connector?.name ?? '',
    clusterId: connector?.clusterId ?? '',
    description,
    labels: toLabelPairs(connector?.labels)
  };
};

const toClusterCreatePayload = (values: ClusterFormValues) => ({
  name: values.name,
  description: values.description && values.description.trim().length > 0 ? values.description.trim() : undefined,
  labels: labelPairsToRecord(values.labels),
  settings: {
    internalInspection: {
      enabled: values.internalInspectionEnabled
    }
  }
});

const toClusterUpdatePayload = (values: ClusterFormValues) => ({
  name: values.name,
  description: values.description && values.description.trim().length > 0 ? values.description.trim() : undefined,
  labels: labelPairsToRecord(values.labels),
  settings: {
    internalInspection: {
      enabled: values.internalInspectionEnabled
    }
  }
});

const toConnectorCreatePayload = (values: ConnectorFormValues) => ({
  name: values.name,
  clusterId: values.clusterId,
  description: values.description && values.description.trim().length > 0 ? values.description.trim() : undefined,
  labels: labelPairsToRecord(values.labels)
});

const toConnectorUpdatePayload = (values: ConnectorFormValues) => ({
  name: values.name,
  clusterId: values.clusterId,
  description: values.description && values.description.trim().length > 0 ? values.description.trim() : undefined,
  labels: labelPairsToRecord(values.labels)
});

const createInitialSiteDetailState = (): SiteDetailState => ({
  loading: false,
  error: null,
  record: null,
  etag: null,
  clusters: [],
  connectors: [],
  connectorsNext: null,
  connectorsLoadingMore: false,
  connectorsError: null,
  activeTab: 'details',
  saving: false,
  topology: null,
  topologyLoading: false,
  topologyExpandInterfaces: false
});

const createInitialClusterDetailState = (): ClusterDetailState => ({
  loading: false,
  error: null,
  record: null,
  etag: null,
  sites: [],
  sitesNext: null,
  sitesLoadingMore: false,
  sitesError: null,
  connectors: [],
  connectorsNext: null,
  connectorsLoadingMore: false,
  connectorsError: null,
  activeTab: 'details',
  saving: false
});

const createInitialConnectorDetailState = (): ConnectorDetailState => ({
  loading: false,
  error: null,
  record: null,
  etag: null,
  activeTab: 'details',
  saving: false,
  userdataStatus: 'idle',
  userdataError: null,
  resetStatus: 'idle',
  resetError: null
});

const defaultListState = <T,>(): ListState<T> => ({
  items: [],
  loading: false,
  error: null
});

interface SiteConnectorStats {
  total: number;
  online: number;
  offline: number;
  provisioning: number;
  registered: number;
  decommissioned: number;
  unknown: number;
}

const firstValidCount = (...values: Array<number | null | undefined>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      return value;
    }
  }
  return undefined;
};

const deriveSiteConnectorCount = (site: SiteView): number | undefined => {
  const siteWithTotals = site as SiteView & {
    connectorsTotal?: number | null;
    connectorsCount?: number | null;
    connectorTotal?: number | null;
  };
  return firstValidCount(site.connectorCount, siteWithTotals.connectorsTotal, siteWithTotals.connectorsCount, siteWithTotals.connectorTotal);
};

const defaultSiteConnectorStats = (): SiteConnectorStats => ({
  total: 0,
  online: 0,
  offline: 0,
  provisioning: 0,
  registered: 0,
  decommissioned: 0,
  unknown: 0
});

const incrementConnectorStats = (stats: SiteConnectorStats, status?: string | null) => {
  stats.total += 1;
  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'online' || normalized === 'connected') {
    stats.online += 1;
  } else if (normalized === 'offline' || normalized === 'disconnected') {
    stats.offline += 1;
  } else if (normalized === 'provisioning') {
    stats.provisioning += 1;
  } else if (normalized === 'registered') {
    stats.registered += 1;
  } else if (normalized === 'decommissioned') {
    stats.decommissioned += 1;
  } else {
    stats.unknown += 1;
  }
};

const summarizeConnectorStats = (connectors: Array<Pick<ConnectorView, 'status'>>): SiteConnectorStats => {
  const stats = defaultSiteConnectorStats();
  connectors.forEach((connector) => {
    incrementConnectorStats(stats, connector.status);
  });
  return stats;
};

const connectorReadyCount = (stats: SiteConnectorStats): number => stats.online + stats.registered;

const buildConnectorStatsByCluster = (
  connectors: Array<Pick<ConnectorView, 'clusterId' | 'status'>>
): Map<string, SiteConnectorStats> => {
  const byClusterId = new Map<string, SiteConnectorStats>();
  connectors.forEach((connector) => {
    const clusterId = connector.clusterId;
    if (!clusterId) {
      return;
    }
    const stats = byClusterId.get(clusterId) ?? defaultSiteConnectorStats();
    incrementConnectorStats(stats, connector.status);
    byClusterId.set(clusterId, stats);
  });
  return byClusterId;
};

const normalizeHealthLabel = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase();
  if (!trimmed || trimmed === '—' || trimmed === '-' || normalized === 'unknown') {
    return undefined;
  }
  return trimmed;
};

export const deriveSiteHealth = (site: SiteView, options?: { connectorStats?: SiteConnectorStats | null }): string => {
  const explicitHealth = normalizeHealthLabel(site.health);
  if (explicitHealth) {
    return explicitHealth;
  }

  const deviceTotal = firstValidCount(site.deviceCounts?.total);
  const deviceActive = firstValidCount(site.deviceCounts?.active) ?? 0;
  const deviceOffline = firstValidCount(site.deviceCounts?.offline) ?? 0;
  const deviceHealthKnown = deviceActive > 0 || deviceOffline > 0;
  if ((deviceTotal ?? 0) > 0) {
    if (deviceActive === deviceTotal) {
      return 'Healthy';
    }
    if (deviceActive === 0 && deviceOffline > 0) {
      return 'Offline';
    }
    if (deviceHealthKnown) {
      return 'Degraded';
    }
  }

  const connectorStats = options?.connectorStats ?? (site.connectorCounts ? {
    total: site.connectorCounts.total,
    online: site.connectorCounts.online,
    offline: site.connectorCounts.offline,
    provisioning: site.connectorCounts.provisioning,
    registered: site.connectorCounts.registered,
    decommissioned: site.connectorCounts.decommissioned,
    unknown: site.connectorCounts.unknown
  } : undefined);
  if (connectorStats && connectorStats.total > 0) {
    const readyCount = connectorReadyCount(connectorStats);
    if (readyCount === connectorStats.total) {
      return 'Healthy';
    }
    if (readyCount === 0 && connectorStats.provisioning === connectorStats.total) {
      return 'Provisioning';
    }
    if (readyCount === 0 && connectorStats.offline > 0 && connectorStats.provisioning === 0) {
      return 'Offline';
    }
    if (readyCount > 0 || connectorStats.offline > 0 || connectorStats.provisioning > 0) {
      return 'Degraded';
    }
    return 'Unknown';
  }

  return 'Unknown';
};

const toCleanString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toTitleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => (part.length <= 3 && part === part.toUpperCase() ? part : `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`))
    .join(' ');

const normalizeDateString = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
};

const formatCMDBMaturityLabel = (value?: string | null): string => {
  switch ((value ?? '').trim()) {
    case 'well_maintained':
      return 'Well maintained';
    case 'hybrid':
      return 'Hybrid';
    default:
      return 'Not maintained';
  }
};

const normalizeDeviceVendorLabel = (vendor: string | undefined): string | undefined => {
  if (!vendor) {
    return undefined;
  }
  const normalized = vendor.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!normalized) {
    return undefined;
  }
  if (normalized.includes('palo alto')) {
    return 'Palo Alto';
  }
  if (normalized.includes('juniper')) {
    return 'Juniper';
  }
  if (normalized.includes('cisco')) {
    return 'Cisco';
  }
  if (normalized.includes('extreme')) {
    return 'Extreme';
  }
  if (normalized.includes('arista')) {
    return 'Arista';
  }
  if (normalized.includes('fortinet')) {
    return 'Fortinet';
  }
  if (normalized.includes('aruba')) {
    return 'Aruba';
  }
  if (normalized.includes('f5')) {
    return 'F5';
  }
  return toTitleCase(normalized);
};

const getDeviceObjectField = (record: Record<string, unknown>, key: string): unknown => {
  const value = record[key];
  if (value !== undefined && value !== null) {
    return value;
  }
  const attributes = record.attributes;
  if (attributes && typeof attributes === 'object') {
    return (attributes as Record<string, unknown>)[key];
  }
  return undefined;
};

const deviceToTypePayload = (device: Device): Record<string, unknown> => {
  const record = device as unknown as Record<string, unknown>;
  return {
    ...record,
    deviceId: record.id,
    deviceType: getDeviceObjectField(record, 'deviceType'),
    deviceKind: getDeviceObjectField(record, 'deviceKind'),
    family: getDeviceObjectField(record, 'family'),
    platform: getDeviceObjectField(record, 'platform'),
    role: record.role ?? getDeviceObjectField(record, 'role'),
    vendor: record.vendor ?? getDeviceObjectField(record, 'vendor'),
    model: record.model ?? getDeviceObjectField(record, 'model'),
    osName: record.osName ?? getDeviceObjectField(record, 'osName'),
    osVersion: record.osVersion ?? getDeviceObjectField(record, 'osVersion'),
    version: record.version ?? getDeviceObjectField(record, 'version'),
    hostname: record.hostname ?? getDeviceObjectField(record, 'hostname'),
    displayName: record.displayName ?? getDeviceObjectField(record, 'displayName'),
    mgmtAddress: record.mgmtAddress ?? getDeviceObjectField(record, 'mgmtAddress')
  };
};

const looksLikeDeviceName = (value: string, payload: Record<string, unknown>): boolean => {
  const normalized = value.toLowerCase();
  const identityValues = [payload.hostname, payload.displayName, payload.deviceId, payload.id]
    .map((item) => toCleanString(item)?.toLowerCase())
    .filter(Boolean);
  if (identityValues.includes(normalized)) {
    return true;
  }
  return /^[a-z0-9]+[-_](?:sw|switch|ap|fw|pan|core|dist|acc|edge|isp|lab)[-_a-z0-9]*$/.test(normalized);
};

const sanitizeDeviceModel = (value: unknown, payload: Record<string, unknown>): string | undefined => {
  const model = toCleanString(value);
  if (!model || looksLikeDeviceName(model, payload)) {
    return undefined;
  }
  return model;
};

const resolveDeviceDetailText = (payload: Record<string, unknown>): string | undefined => {
  return sanitizeDeviceModel(payload.model, payload);
};

const resolveSiteDeviceTypeMetadata = (device: Device): Omit<SiteDeviceTypeSummary, 'count'> => {
  const payload = deviceToTypePayload(device);
  const vendorLabel = normalizeDeviceVendorLabel(toCleanString(payload.vendor));
  const detail = resolveDeviceDetailText(payload);
  const { family, iconKind } = inferDeviceFamily(payload);
  const explicitLabel = toCleanString(payload.deviceType) ?? toCleanString(payload.deviceKind) ?? toCleanString(payload.family);
  const normalizedExplicit = explicitLabel?.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const label =
    explicitLabel && normalizedExplicit && normalizedExplicit !== family.toLowerCase() && normalizedExplicit !== 'device'
      ? toTitleCase(explicitLabel)
      : vendorLabel
      ? `${vendorLabel} ${family}`
      : family;
  return {
    key: label.toLowerCase(),
    label,
    iconKind,
    detail
  };
};

const summarizeSiteDeviceTypes = (devices: Device[]): SiteDeviceTypeSummary[] => {
  const buckets = new Map<string, SiteDeviceTypeSummary>();
  devices.forEach((device) => {
    const { key, label, iconKind, detail } = resolveSiteDeviceTypeMetadata(device);
    const current = buckets.get(key) ?? { key, label, count: 0, iconKind, detail: undefined };
    current.count += 1;
    if (!current.detail && detail) {
      current.detail = detail;
    }
    buckets.set(key, current);
  });
  return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

const summarizeSiteVendors = (devices: Device[]): SiteVendorSummary[] => {
  const buckets = new Map<string, SiteVendorSummary>();
  devices.forEach((device) => {
    const payload = deviceToTypePayload(device);
    const label = normalizeDeviceVendorLabel(toCleanString(payload.vendor));
    if (!label) {
      return;
    }
    const key = label.toLowerCase();
    const current = buckets.get(key) ?? { key, label, count: 0 };
    current.count += 1;
    buckets.set(key, current);
  });
  return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

const renderSiteDeviceTypeIcon = (iconKind: SiteDeviceTypeIconKind, size = 20) => {
  switch (iconKind) {
    case 'endpoint':
      return <UserIcon size={size} aria-hidden="true" />;
    case 'firewall':
      return <ShieldIcon size={size} aria-hidden="true" />;
    case 'load-balancer':
      return <ForkIcon size={size} aria-hidden="true" />;
    case 'router':
      return <NetworkIcon size={size} aria-hidden="true" />;
    case 'switch':
      return <LayersIcon size={size} aria-hidden="true" />;
    case 'wireless-ap':
      return <RadarIcon size={size} aria-hidden="true" />;
    default:
      return <RadarIcon size={size} aria-hidden="true" />;
  }
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DETAIL_RELATION_PAGE_SIZE = 100;
const CONNECTIVITY_SEARCH_DEBOUNCE_MS = 300;
const CONNECTIVITY_CATALOG_PAGE_SIZE = 200;

async function collectCursorPages<T>(
  loadPage: (after?: string) => Promise<{ items?: T[]; meta?: { next?: string | null } }>
): Promise<T[]> {
  const items: T[] = [];
  const seenCursors = new Set<string>();
  let after: string | undefined;

  for (;;) {
    const page = await loadPage(after);
    items.push(...(page.items ?? []));
    const next = typeof page.meta?.next === 'string' && page.meta.next.length > 0 ? page.meta.next : undefined;
    if (!next || seenCursors.has(next)) {
      return items;
    }
    seenCursors.add(next);
    after = next;
  }
}

const SITE_COCKPIT_REFRESH_OPTIONS_SECONDS = [0, 5, 10, 30, 60] as const;
const ConnectivitySitesPage = () => {
  const orgId = useActiveOrgId();
  const config = useConfig();
  const { apiBaseUrl } = config;
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchWithAuthRetry } = useAuthFetch();
  const sitesApi = useSitesApi();
  const clustersApi = useConnectorClustersApi();
  const connectorsApi = useConnectorsApi();
  const visibilityApi = useVisibilityApi();
  const devicesApi = useDevicesApi();
  const cmdbReconciliationApi = useCMDBReconciliationApi();
  const discoveryApi = useDiscoveryApi();
  const dashboardsApi = useDashboardsApi();
  const inventoryApi = useInventoryApi();
  const integrationsApi = useIntegrationsApi();
  const topologyApi = useTopologyApi();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ConnectivityTab>('sites');
  const activeTabRef = useRef(activeTab);

  const [siteCockpitRangePreset, setSiteCockpitRangePreset] = useState<TimeRangePreset>('1h');
  const [siteCockpitRefreshSeconds, setSiteCockpitRefreshSeconds] = useState<number>(30);

  const refreshGrafanaEmbedSession = useCallback(() => {
    if (!orgId) {
      return Promise.resolve();
    }
    return dashboardsApi.orgsOrgIdGrafanaEmbedSessionPost({ orgId }, { credentials: 'include' }).then(() => undefined);
  }, [dashboardsApi, orgId]);

  const [sitesState, setSitesState] = useState<ListState<SiteView>>(defaultListState);
  const [clustersState, setClustersState] = useState<ListState<ClusterView>>(defaultListState);
  const [connectorsState, setConnectorsState] = useState<ListState<ConnectorView>>(defaultListState);
  const [cloudAccessState, setCloudAccessState] = useState<ListState<CloudAccessConnection>>(defaultListState);
  const [cloudAccessActionBusyKeys, setCloudAccessActionBusyKeys] = useState<Set<string>>(new Set());
  const [cloudAccessActionError, setCloudAccessActionError] = useState<string | null>(null);
  const [unavailableCloudProviders, setUnavailableCloudProviders] = useState<Set<string>>(new Set());
  const cloudAccessRequestIDRef = useRef(0);
  const cloudAccessActionsRef = useRef(new Map<string, AbortController>());

  useEffect(() => {
    setCloudAccessActionBusyKeys(new Set());
    setCloudAccessActionError(null);
    return () => {
      cloudAccessActionsRef.current.forEach((controller) => controller.abort());
      cloudAccessActionsRef.current.clear();
    };
  }, [orgId, activeTab]);

  const connectorsCatalogRef = useRef<ConnectorView[]>([]);
  const connectorCatalogPromiseRef = useRef<Promise<ConnectorView[]> | null>(null);
  const connectorCatalogOrgIdRef = useRef<string | null>(null);
  const sitesPageRequestIDRef = useRef(0);
  const clustersPageRequestIDRef = useRef(0);
  const connectorsPageRequestIDRef = useRef(0);
  const [sitesPageState, setSitesPageState] = useState<ListState<SiteView>>(defaultListState);
  const [clustersPageState, setClustersPageState] = useState<ListState<ClusterView>>(defaultListState);
  const [connectorsPageState, setConnectorsPageState] = useState<ListState<ConnectorView>>(defaultListState);
  const [siteFilters, setSiteFilters] = useState<{ q: string }>({ q: '' });
  const [inventoryPolicy, setInventoryPolicy] = useState<{ mode: string; connectionId: string }>({ mode: 'inherit', connectionId: '' });
  const [inventoryImportBusy, setInventoryImportBusy] = useState(false);
  const [netBoxConnections, setNetBoxConnections] = useState<IntegrationConnection[]>([]);
  const [netBoxConnectionsLoading, setNetBoxConnectionsLoading] = useState(false);
	const [netBoxSites, setNetBoxSites] = useState<Array<{ id: string; name: string }>>([]);
  const [netBoxSitesLoading, setNetBoxSitesLoading] = useState(false);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [inventorySourceCatalog, setInventorySourceCatalog] = useState<InventorySourceCatalogItem[]>([]);
  const [importSourceMode, setImportSourceMode] = useState('');
  const [importConnectionID, setImportConnectionID] = useState('');
  const [importNetBoxSites, setImportNetBoxSites] = useState<Array<{ id: string; name: string }>>([]);
  const [importSelectedNetBoxSiteIDs, setImportSelectedNetBoxSiteIDs] = useState<string[]>([]);
  const [importSitesLoading, setImportSitesLoading] = useState(false);
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const [bulkImportRows, setBulkImportRows] = useState<BulkNetBoxImportRow[]>([]);
  const [csvImportDraft, setCSVImportDraft] = useState<CSVImportDraft | null>(null);
  const [csvImportBusy, setCSVImportBusy] = useState(false);
  const [csvSchemas, setCSVSchemas] = useState<CSVImportSchema[]>([]);
  const [csvSchemasLoading, setCSVSchemasLoading] = useState(false);
  const [csvSchemaKey, setCSVSchemaKey] = useState('');
  const activeImportSource = inventorySourceCatalog.find((source) => source.key === importSourceMode);
  const activeImportWorkflow = activeImportSource?.workflow;
  const selectedCSVSchema = csvSchemas.find((schema) => schema.key === csvSchemaKey);
  const [clusterFilters, setClusterFilters] = useState<{ q: string }>({ q: '' });
  const [connectorFilters, setConnectorFilters] = useState<{ q: string }>({ q: '' });
  const [siteQuery, setSiteQuery] = useState('');
  const [clusterQuery, setClusterQuery] = useState('');
  const [connectorQuery, setConnectorQuery] = useState('');
  const clusterNames = useMemo(() => {
    const lookup: Record<string, string> = {};
    clustersState.items.forEach((cluster) => {
      if (cluster.id) {
        lookup[cluster.id] = cluster.name ?? cluster.id;
      }
    });
    return lookup;
  }, [clustersState.items]);

  const {
    state: sitePageState,
    setPage: setSitePage,
    setPageSize: setSitePageSize,
    reset: resetSitePagination,
    registerPageMeta: registerSitePageMeta,
    deriveMeta: deriveSitePaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });
  const {
    state: clusterPageState,
    setPage: setClusterPage,
    setPageSize: setClusterPageSize,
    reset: resetClusterPagination,
    registerPageMeta: registerClusterPageMeta,
    deriveMeta: deriveClusterPaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });
  const {
    state: connectorPageState,
    setPage: setConnectorPage,
    setPageSize: setConnectorPageSize,
    reset: resetConnectorPagination,
    registerPageMeta: registerConnectorPageMeta,
    deriveMeta: deriveConnectorPaginationMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const updateSiteFilters = useCallback(
    (updater: (prev: { q: string }) => { q: string }) => {
      setSiteFilters((prev) => updater(prev));
    },
    []
  );
  const updateClusterFilters = useCallback(
    (updater: (prev: { q: string }) => { q: string }) => {
      setClusterFilters((prev) => updater(prev));
    },
    []
  );
  const updateConnectorFilters = useCallback(
    (updater: (prev: { q: string }) => { q: string }) => {
      setConnectorFilters((prev) => updater(prev));
    },
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = normalizeConnectivityQuery(siteFilters.q);
      if (shouldResetConnectivityPagination(siteQuery, siteFilters.q)) {
        setSiteQuery(next);
        resetSitePagination();
      }
    }, CONNECTIVITY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [resetSitePagination, siteFilters.q, siteQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = normalizeConnectivityQuery(clusterFilters.q);
      if (shouldResetConnectivityPagination(clusterQuery, clusterFilters.q)) {
        setClusterQuery(next);
        resetClusterPagination();
      }
    }, CONNECTIVITY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [clusterFilters.q, clusterQuery, resetClusterPagination]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = normalizeConnectivityQuery(connectorFilters.q);
      if (shouldResetConnectivityPagination(connectorQuery, connectorFilters.q)) {
        setConnectorQuery(next);
        resetConnectorPagination();
      }
    }, CONNECTIVITY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [connectorFilters.q, connectorQuery, resetConnectorPagination]);

  const [siteDrawer, setSiteDrawer] = useState<{ siteId: string; open: boolean } | null>(null);
  const [inventoryImportRuns, setInventoryImportRuns] = useState<Array<{ id: string; connectionId?: string; netboxSiteId?: string; status: string; error?: string; createdAt?: string; startedAt?: string; finishedAt?: string; summary?: Record<string, number>; logs?: string[] }>>([]);
  const [inventoryImportRunsRefresh, setInventoryImportRunsRefresh] = useState(0);
  const [clusterDrawer, setClusterDrawer] = useState<{ clusterId: string; open: boolean } | null>(null);
  const [connectorDrawer, setConnectorDrawer] = useState<{ connectorId: string; open: boolean; clusterId?: string; allowedClusterIds?: string[] } | null>(null);
  const [awsTelemetryDrawer, setAwsTelemetryDrawer] = useState<{ clusterId: string; clusterName?: string; initialRegion?: string } | null>(null);

  const [siteDetail, setSiteDetail] = useState<SiteDetailState>(() => createInitialSiteDetailState());
  const [siteCMDBState, setSiteCMDBState] = useState<SiteCMDBState>(() => createInitialSiteCMDBState());
  const [selectedSiteCMDBConnectionId, setSelectedSiteCMDBConnectionId] = useState('');

  const [clusterDetail, setClusterDetail] = useState<ClusterDetailState>(() => createInitialClusterDetailState());

  const [connectorDetail, setConnectorDetail] = useState<ConnectorDetailState>(() => createInitialConnectorDetailState());

  const [manualNodePositions, setManualNodePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [edgeDetail, setEdgeDetail] = useState<EdgeDetail | null>(null);
  const [edgeDetailPosition, setEdgeDetailPosition] = useState<{ x: number; y: number } | null>(null);
  const [deviceDetail, setDeviceDetail] = useState<DeviceTooltipData | null>(null);
  const [deviceDetailPosition, setDeviceDetailPosition] = useState<{ x: number; y: number } | null>(null);
  const [topologyDevicesById, setTopologyDevicesById] = useState<Record<string, Device>>({});
  const [topologyFullscreenOpen, setTopologyFullscreenOpen] = useState(false);
  const [layoutResetCounter, setLayoutResetCounter] = useState(0);
  const topologyDiagramRef = useRef<HTMLDivElement | null>(null);
  const topologyWindowDiagramRef = useRef<HTMLDivElement | null>(null);

  const [siteDiscoveryJobsVersion, setSiteDiscoveryJobsVersion] = useState(0);
  const [siteDiscoveryRunsVersion, setSiteDiscoveryRunsVersion] = useState(0);
  const [jobDrawerOpen, setJobDrawerOpen] = useState(false);
  const [jobDrawerMode, setJobDrawerMode] = useState<'create' | 'edit'>('create');
  const [jobDrawerJobId, setJobDrawerJobId] = useState<string | null>(null);
  const [jobActionBusyId, setJobActionBusyId] = useState<string | null>(null);
  const [jobActionError, setJobActionError] = useState<string | null>(null);
  const [pendingDeleteJobs, setPendingDeleteJobs] = useState<DiscoveryJob[] | null>(null);
  const [locallyDeletedJobIds, setLocallyDeletedJobIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [pendingResourceDelete, setPendingResourceDelete] = useState<
    | { kind: 'sites'; rows: SiteView[] }
    | { kind: 'clusters'; rows: ClusterView[] }
    | { kind: 'connectors'; rows: ConnectorView[] }
    | null
  >(null);
  const [resourceDeleteBusy, setResourceDeleteBusy] = useState(false);
  const [runActionBusyId, setRunActionBusyId] = useState<string | null>(null);
  const [runActionError, setRunActionError] = useState<string | null>(null);
  const [runDrawerOpen, setRunDrawerOpen] = useState(false);
  const [runDetailOpen, setRunDetailOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [pendingRun, setPendingRun] = useState<DiscoveryRun | null>(null);
  const [siteJobsPage, setSiteJobsPage] = useState(1);
  const [siteJobsPageSize, setSiteJobsPageSize] = useState(5);
  const [siteRunsPage, setSiteRunsPage] = useState(1);
  const [siteRunsPageSize, setSiteRunsPageSize] = useState(5);
  const discoveryPanelRefreshTimerRef = useRef<number | null>(null);
  const discoveryPanelRefreshPendingRef = useRef<{ runs: boolean; jobs: boolean }>({ runs: false, jobs: false });
  const discoveryPanelLastRefreshAtRef = useRef(0);
  const siteDataRefreshTimerRef = useRef<number | null>(null);
  const siteDataLastRefreshAtRef = useRef(0);
  const selectedRunLiveStateRef = useRef<{ id: string; live: boolean } | null>(null);
  const previousDiscoverySiteIdRef = useRef<string | null>(null);

  const siteDetailRef = useRef(siteDetail);
  const clusterDetailRef = useRef(clusterDetail);
  const connectorDetailRef = useRef(connectorDetail);
  const siteDetailFormRef = useRef<HTMLFormElement | null>(null);
  const clusterDetailFormRef = useRef<HTMLFormElement | null>(null);
  const connectorDetailFormRef = useRef<HTMLFormElement | null>(null);

  const connectorLoadWidget = useMemo(() => {
    const connectorId = connectorDetail.record?.id;
    if (!orgId || !connectorId) return null;
    const now = new Date();
    return {
      id: `connector-load-${connectorId}`,
      title: 'Command-Agent Collector Load',
      iframeUrl: buildGrafanaSoloUrl({
        dashUid: 'connector-runtime',
        panelId: 16,
        title: 'Command-Agent Collector Load',
        varBindings: { org_id: orgId, connector_id: connectorId },
        vars: ['org_id', 'connector_id'],
        from: new Date(now.getTime() - 15 * 60 * 1000),
        to: now,
        mockMode: config.mockRoutes.includes('dashboards')
      })
    };
  }, [config.mockRoutes, connectorDetail.record?.id, orgId]);

  const clusterConnectorLoadWidgets = useMemo(() => {
    if (!orgId) return [];
    return clusterDetail.connectors
      .filter((connector): connector is ConnectorView & { id: string } => Boolean(connector.id))
      .map((connector) => {
        const now = new Date();
        return {
          id: `cluster-connector-load-${connector.id}`,
          title: connector.name ?? connector.id,
          iframeUrl: buildGrafanaSoloUrl({
            dashUid: 'connector-runtime',
            panelId: 16,
            title: 'Command-Agent Collector Load',
            varBindings: { org_id: orgId, connector_id: connector.id },
            vars: ['org_id', 'connector_id'],
            from: new Date(now.getTime() - 15 * 60 * 1000),
            to: now,
            mockMode: config.mockRoutes.includes('dashboards')
          })
        };
      });
  }, [clusterDetail.connectors, config.mockRoutes, orgId]);

  useEffect(() => {
    siteDetailRef.current = siteDetail;
  }, [siteDetail]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    clusterDetailRef.current = clusterDetail;
  }, [clusterDetail]);

  useEffect(() => {
    connectorDetailRef.current = connectorDetail;
  }, [connectorDetail]);

  useEffect(() => {
    connectorsCatalogRef.current = connectorsState.items;
  }, [connectorsState.items]);

  const siteClusterCacheRef = useRef<Map<string, string[]>>(new Map());

  const [siteCreateSaving, setSiteCreateSaving] = useState(false);
  const [clusterCreateSaving, setClusterCreateSaving] = useState(false);
  const [connectorCreateSaving, setConnectorCreateSaving] = useState(false);
  const [siteCreateError, setSiteCreateError] = useState<string | null>(null);
  const [clusterCreateError, setClusterCreateError] = useState<string | null>(null);
  const [connectorCreateError, setConnectorCreateError] = useState<string | null>(null);
  const [siteClusterAttachId, setSiteClusterAttachId] = useState<string>('');
  const [clusterSiteAttachId, setClusterSiteAttachId] = useState<string>('');
  const createSiteInitialValues = useMemo<SiteFormValues>(
    () => ({
      name: '',
      siteType: SiteSiteTypeEnum.OnPrem,
      awsRegion: '',
      awsAccountId: '',
      azureSubscriptionId: '',
      azureLocation: '',
      notes: '',
      address: { line1: '', line2: '', city: '', state: '', postalCode: '', country: '' },
      labels: []
    }),
    []
  );

  const scheduleDiscoveryPanelRefresh = useCallback((resource?: string | null) => {
    const normalized = (resource ?? '').trim().toLowerCase();
    if (normalized !== 'discovery_runs' && normalized !== 'discovery_jobs') {
      return;
    }

    if (normalized === 'discovery_runs') {
      discoveryPanelRefreshPendingRef.current.runs = true;
    } else {
      discoveryPanelRefreshPendingRef.current.jobs = true;
    }

    const flushPending = () => {
      discoveryPanelRefreshTimerRef.current = null;
      const pending = discoveryPanelRefreshPendingRef.current;
      discoveryPanelRefreshPendingRef.current = { runs: false, jobs: false };
      if (pending.runs) {
        setSiteDiscoveryRunsVersion((prev) => prev + 1);
      }
      if (pending.jobs) {
        setSiteDiscoveryJobsVersion((prev) => prev + 1);
      }
      discoveryPanelLastRefreshAtRef.current = Date.now();
    };

    if (discoveryPanelRefreshTimerRef.current) {
      return;
    }

    const elapsed = Date.now() - discoveryPanelLastRefreshAtRef.current;
    const waitMs =
      elapsed >= DISCOVERY_CHANGE_MIN_REFRESH_GAP_MS
        ? DISCOVERY_CHANGE_BATCH_MS
        : Math.max(DISCOVERY_CHANGE_BATCH_MS, DISCOVERY_CHANGE_MIN_REFRESH_GAP_MS - elapsed);

    discoveryPanelRefreshTimerRef.current = window.setTimeout(flushPending, waitMs);
  }, []);

  useEffect(
    () => () => {
      if (discoveryPanelRefreshTimerRef.current) {
        window.clearTimeout(discoveryPanelRefreshTimerRef.current);
        discoveryPanelRefreshTimerRef.current = null;
      }
      if (siteDataRefreshTimerRef.current) {
        window.clearTimeout(siteDataRefreshTimerRef.current);
        siteDataRefreshTimerRef.current = null;
      }
    },
    []
  );

  const openSiteCreate = () => {
    setSiteCreateError(null);
    setSiteCreateSaving(false);
    setSiteDrawer({ siteId: '', open: true });
  };

  const openClusterCreate = () => {
    setClusterCreateError(null);
    setClusterCreateSaving(false);
    setClusterDrawer({ clusterId: '', open: true });
  };

  const openConnectorCreate = (options?: { clusterId?: string; allowedClusterIds?: string[] }) => {
    setConnectorCreateError(null);
    setConnectorCreateSaving(false);
    setConnectorDrawer({ connectorId: '', open: true, ...(options ?? {}) });
  };

  const resolveCursorParams = useCallback((cursor: CursorPaginationState['cursor']) => {
    if (cursor.direction === 'after') {
      return { after: cursor.value, before: undefined };
    }
    if (cursor.direction === 'before') {
      return { after: undefined, before: cursor.value };
    }
    return { after: undefined, before: undefined };
  }, []);

  const resolveListCache = useCallback((target: ConnectivityTab): RequestCache => {
    return activeTabRef.current === target ? 'no-store' : 'no-cache';
  }, []);

  const loadConnectorCatalog = useCallback(
    async (options?: { cache?: RequestCache; force?: boolean }) => {
      if (!orgId) {
        return [] as ConnectorView[];
      }
      if (!options?.force && connectorCatalogOrgIdRef.current === orgId && connectorsCatalogRef.current.length > 0) {
        return connectorsCatalogRef.current;
      }
      if (connectorCatalogOrgIdRef.current === orgId && connectorCatalogPromiseRef.current) {
        return connectorCatalogPromiseRef.current;
      }
      connectorCatalogOrgIdRef.current = orgId;
      const promise = collectCursorPages<Connector>((after) =>
        connectorsApi.orgsOrgIdConnectorsGet(
          { orgId, limit: CONNECTIVITY_CATALOG_PAGE_SIZE, after },
          options?.cache ? { cache: options.cache } : undefined
        )
      ).then((items) => items.map((connector) => ({ ...connector } as ConnectorView)));
      connectorCatalogPromiseRef.current = promise;
      try {
        const items = await promise;
        if (connectorCatalogOrgIdRef.current === orgId) {
          connectorsCatalogRef.current = items;
        }
        return items;
      } finally {
        if (connectorCatalogPromiseRef.current === promise) {
          connectorCatalogPromiseRef.current = null;
        }
      }
    },
    [connectorsApi, orgId]
  );

  const refreshSitesCatalog = useCallback(async (options?: { cache?: RequestCache }) => {
    if (!orgId) {
      setSitesState(defaultListState);
      return;
    }
    setSitesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const catalog = await collectCursorPages<Site>((after) =>
        sitesApi.orgsOrgIdSitesGet(
          { orgId, limit: CONNECTIVITY_CATALOG_PAGE_SIZE, after },
          options?.cache ? { cache: options.cache } : undefined
        )
      );
      const items = catalog.map((site) => {
        const asView = { ...site } as SiteView;
        return { ...asView, health: deriveSiteHealth(asView) };
      });
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setSitesState({ items, loading: false, error: null });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load sites';
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setSitesState({ items: [], loading: false, error: message });
      }
    }
  }, [orgId, sitesApi]);

  const refreshCloudAccessCatalog = useCallback(
    async (options?: { cache?: RequestCache }) => {
      const requestID = ++cloudAccessRequestIDRef.current;
      if (!orgId) {
        setCloudAccessState(defaultListState);
        setUnavailableCloudProviders(new Set());
        return;
      }
      setCloudAccessState((prev) => ({ ...prev, loading: true, error: null }));
      const { items, failures } = await loadCloudAccessCatalog<CloudAccessConnection>((provider) =>
        collectCursorPages<CloudAccessConnection>((after) =>
          sitesApi.listProviderCloudAccessConnections(
            { orgId, provider, limit: CONNECTIVITY_CATALOG_PAGE_SIZE, after },
            options?.cache ? { cache: options.cache } : undefined
          )
        )
      );
      const messages = await Promise.all(failures.map(async ({ provider, error }) =>
        `${cloudProviderLabel(provider)}: ${await resolveErrorMessage(error, 'Failed to load cloud access')}`
      ));
      if (requestID === cloudAccessRequestIDRef.current && isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setUnavailableCloudProviders(new Set(failures.map(({ provider }) => provider)));
        setCloudAccessState({ items, loading: false, error: messages.join(' · ') || null });
      }
    },
    [orgId, sitesApi]
  );

  const handleCloudAccessAction = useCallback(
    async (row: CloudAccessView, checkOnly = false) => {
      const accountId = row.accountId.trim();
      const provider = row.provider.trim().toLowerCase();
      const actionKey = cloudAccessRowKey(row);
      if (!orgId || cloudAccessActionsRef.current.has(actionKey)) return;
      if (!accountId) {
        setCloudAccessActionError(
          provider === 'aws'
            ? 'AWS account ID is missing for this cloud access row.'
            : `${cloudProviderLabel(provider)} account identifier is missing for this cloud access row.`
        );
        return;
      }
      const controller = new AbortController();
      cloudAccessActionsRef.current.set(actionKey, controller);
      const { signal } = controller;
      let popup: Window | null = null;
      let navigated = false;
      setCloudAccessActionBusyKeys((keys) => new Set(keys).add(actionKey));
      setCloudAccessActionError(null);
      try {
        let connectionId = row.id;
        if (!checkOnly) {
          popup = openPendingTab(provider === 'azure' ? 'azure' : 'aws');
          if (!popup) throw new Error('Popup was blocked. Allow popups for this site and try again.');
          const payload = provider === 'azure'
            ? await sitesApi.createProviderCloudAccessLaunch({ orgId, provider: 'azure', cloudAccessLaunchRequest: { accountId } }, { signal })
            : await sitesApi.createCloudAccessQuickCreate({ orgId, cloudAccessQuickCreateRequest: { awsAccountId: accountId } }, { signal });
          signal.throwIfAborted();
          const launchUrl = 'launchUrl' in payload ? payload.launchUrl : payload.quickCreateUrl;
          if (!launchUrl) {
            throw new Error(
              provider === 'aws'
                ? 'Backend response missing AWS CloudFormation quick-create URL.'
                : `Backend response missing ${cloudProviderLabel(provider)} onboarding URL.`
            );
          }
          if (!navigatePendingTab(popup, launchUrl)) throw new Error('Popup was blocked. Allow popups for this site and try again.');
          navigated = true;
          connectionId = payload.connection.id;
          await refreshCloudAccessCatalog({ cache: 'no-store' });
          signal.throwIfAborted();
        }
        if (provider === 'azure' && checkOnly) {
          if (!connectionId) throw new Error('Backend response missing Azure connection ID. Refresh Cloud Access and try again.');
          const connectionToVerify = connectionId;
          await pollCloudAccessVerification(
            (requestSignal) => sitesApi.verifyProviderCloudAccessConnection({ orgId, provider: 'azure', connectionId: connectionToVerify }, { signal: requestSignal }),
            {
              signal,
              onAzureAccessPending: async () => {
                await refreshCloudAccessCatalog({ cache: 'no-store' });
                signal.throwIfAborted();
              }
            }
          );
          signal.throwIfAborted();
          await refreshCloudAccessCatalog({ cache: 'no-store' });
        }
      } catch (error) {
        if (!navigated) popup?.close();
        if (!signal.aborted) {
          const message = await resolveErrorMessage(
            error,
            provider === 'aws'
              ? 'Failed to open AWS CloudFormation quick-create flow.'
              : `Failed to complete ${cloudProviderLabel(provider)} onboarding.`
          );
          if (!signal.aborted) setCloudAccessActionError(message);
        }
      } finally {
        if (cloudAccessActionsRef.current.get(actionKey) === controller) {
          cloudAccessActionsRef.current.delete(actionKey);
          setCloudAccessActionBusyKeys((keys) => {
            const next = new Set(keys);
            next.delete(actionKey);
            return next;
          });
        }
      }
    },
    [orgId, refreshCloudAccessCatalog, sitesApi]
  );


  const refreshSitesPage = useCallback(
    async (options?: { cache?: RequestCache }) => {
      // Invalidate any in-flight response even when the active org disappears.
      const requestID = ++sitesPageRequestIDRef.current;
      if (!orgId) {
        setSitesPageState(defaultListState);
        registerSitePageMeta(null);
        return;
      }
      setSitesPageState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const cursorParams = resolveCursorParams(sitePageState.cursor);
        const response = await sitesApi.orgsOrgIdSitesGetRaw(
          {
            orgId,
            limit: sitePageState.pageSize,
            after: cursorParams.after,
            before: cursorParams.before,
            q: siteQuery || undefined
          },
          options?.cache ? { cache: options.cache } : undefined
        );
        const payload = await response.value();
        if (isLatestConnectivityRequest(requestID, sitesPageRequestIDRef.current)) {
          registerSitePageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
        }
        const items = (payload.items ?? []).map((site: Site) => {
          const asView = { ...site } as SiteView;
          return { ...asView, health: deriveSiteHealth(asView) };
        });
        // Show the Sites response immediately. Device and connector summaries
        // are supplemental and can take much longer than the table query.
        if (isLatestConnectivityRequest(requestID, sitesPageRequestIDRef.current)) {
          setSitesPageState({ items, loading: false, error: null });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load sites';
        if (isLatestConnectivityRequest(requestID, sitesPageRequestIDRef.current)) {
          setSitesPageState({ items: [], loading: false, error: message });
        }
      }
    },
    [
      orgId,
      registerSitePageMeta,
      resolveCursorParams,
      sitesApi,
      sitePageState.cursor,
      sitePageState.pageSize,
      siteQuery
    ]
  );

  const refreshClustersCatalog = useCallback(async (options?: { cache?: RequestCache }) => {
    if (!orgId) {
      setClustersState(defaultListState);
      return;
    }
    setClustersState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const catalog = await collectCursorPages<ConnectorCluster>((after) =>
        clustersApi.orgsOrgIdConnectorClustersGet(
          { orgId, limit: CONNECTIVITY_CATALOG_PAGE_SIZE, after },
          options?.cache ? { cache: options.cache } : undefined
        )
      );
      const items = catalog.map((cluster) => ({ ...cluster } as ClusterView));
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setClustersState({ items, loading: false, error: null });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load connector clusters';
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setClustersState({ items: [], loading: false, error: message });
      }
    }
  }, [orgId, clustersApi]);

  const refreshClustersPage = useCallback(
    async (options?: { cache?: RequestCache }) => {
      const requestID = ++clustersPageRequestIDRef.current;
      if (!orgId) {
        setClustersPageState(defaultListState);
        registerClusterPageMeta(null);
        return;
      }
      setClustersPageState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const cursorParams = resolveCursorParams(clusterPageState.cursor);
        const response = await clustersApi.orgsOrgIdConnectorClustersGetRaw(
          {
            orgId,
            limit: clusterPageState.pageSize,
            after: cursorParams.after,
            before: cursorParams.before,
            q: clusterQuery || undefined
          },
          options?.cache ? { cache: options.cache } : undefined
        );
        const payload = await response.value();
        if (!isLatestConnectivityRequest(requestID, clustersPageRequestIDRef.current)) {
          return;
        }
        registerClusterPageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
        const items = (payload.items ?? []).map((cluster) => ({ ...cluster } as ClusterView));
        setClustersPageState({ items, loading: false, error: null });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load connector clusters';
        if (isLatestConnectivityRequest(requestID, clustersPageRequestIDRef.current)) {
          setClustersPageState({ items: [], loading: false, error: message });
        }
      }
    },
    [
      clusterPageState.cursor,
      clusterPageState.pageSize,
      clusterQuery,
      clustersApi,
      orgId,
      registerClusterPageMeta,
      resolveCursorParams
    ]
  );

  const refreshConnectorsCatalog = useCallback(async (options?: { cache?: RequestCache }) => {
    if (!orgId) {
      setConnectorsState(defaultListState);
      return;
    }
    setConnectorsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const items = await loadConnectorCatalog({ cache: options?.cache, force: true });
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setConnectorsState({ items, loading: false, error: null });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load connectors';
      if (isActiveConnectivityOrg(orgId, connectorCatalogOrgIdRef.current)) {
        setConnectorsState({ items: [], loading: false, error: message });
      }
    }
  }, [loadConnectorCatalog, orgId]);

  const refreshConnectorsPage = useCallback(
    async (options?: { cache?: RequestCache }) => {
      const requestID = ++connectorsPageRequestIDRef.current;
      if (!orgId) {
        setConnectorsPageState(defaultListState);
        registerConnectorPageMeta(null);
        return;
      }
      setConnectorsPageState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const cursorParams = resolveCursorParams(connectorPageState.cursor);
        const response = await connectorsApi.orgsOrgIdConnectorsGetRaw(
          {
            orgId,
            limit: connectorPageState.pageSize,
            after: cursorParams.after,
            before: cursorParams.before,
            q: connectorQuery || undefined
          },
          options?.cache ? { cache: options.cache } : undefined
        );
        const payload = await response.value();
        if (!isLatestConnectivityRequest(requestID, connectorsPageRequestIDRef.current)) {
          return;
        }
        registerConnectorPageMeta(payload.meta ?? null, response.raw.headers.get('Link'));
        const items = (payload.items ?? []).map((connector) => ({ ...connector } as ConnectorView));
        if (isLatestConnectivityRequest(requestID, connectorsPageRequestIDRef.current)) {
          setConnectorsPageState({ items, loading: false, error: null });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load connectors';
        if (isLatestConnectivityRequest(requestID, connectorsPageRequestIDRef.current)) {
          setConnectorsPageState({ items: [], loading: false, error: message });
        }
      }
    },
    [
      connectorPageState.cursor,
      connectorPageState.pageSize,
      connectorQuery,
      connectorsApi,
      orgId,
      registerConnectorPageMeta,
      resolveCursorParams
    ]
  );

  const loadSiteCMDBContext = useCallback(
    async (siteId: string, options?: { refreshReport?: boolean; connectionId?: string }) => {
      if (!orgId || !siteId) {
        setSiteCMDBState(createInitialSiteCMDBState());
        setSelectedSiteCMDBConnectionId('');
        return;
      }

      setSiteCMDBState((prev) => ({ ...prev, loadingConnections: true, loadingReport: true, error: null }));
      try {
        const connectionsPayload = await devicesApi.listInventoryCMDBConnections({ orgId }, { cache: 'no-store' });
        const connections = connectionsPayload.items ?? [];
        const requestedConnectionId = options?.connectionId ?? '';
        const activeConnection =
          connections.find((item) => item.id === requestedConnectionId) ??
          connections.find((item) => Boolean(item.id)) ??
          null;

        if (!activeConnection?.id) {
          setSiteCMDBState({
            connections,
            loadingConnections: false,
            report: null,
            loadingReport: false,
            error: null
          });
          setSelectedSiteCMDBConnectionId('');
          return;
        }
        setSelectedSiteCMDBConnectionId(activeConnection.id);

        const report = await (options?.refreshReport
          ? cmdbReconciliationApi.runInventoryCMDBDiscrepancyReport({ orgId, connectionId: activeConnection.id }, { cache: 'no-store' })
          : cmdbReconciliationApi.getInventoryCMDBStoredDiscrepancyReport({ orgId, connectionId: activeConnection.id }, { cache: 'no-store' })
        ).catch((error) => {
          if (!options?.refreshReport && error instanceof ResponseError && error.response.status === 404) {
            return null;
          }
          throw error;
        });

        setSiteCMDBState({
          connections,
          loadingConnections: false,
          report,
          loadingReport: false,
          error: null
        });
      } catch (error) {
        setSiteCMDBState((prev) => ({
          ...prev,
          loadingConnections: false,
          loadingReport: false,
          error: error instanceof Error ? error.message : 'Failed to load CMDB site context.'
        }));
      }
    },
    [cmdbReconciliationApi, devicesApi, orgId]
  );

  useEffect(() => {
    if (!siteDetail.record?.id || siteDetail.activeTab !== 'inventory') {
      return;
    }
    void loadSiteCMDBContext(siteDetail.record.id);
  }, [loadSiteCMDBContext, siteDetail.activeTab, siteDetail.record?.id]);

  const refreshSitesAll = useCallback(
    async (options?: { cache?: RequestCache }) => {
      await Promise.all([refreshSitesCatalog(options), refreshSitesPage(options)]);
    },
    [refreshSitesCatalog, refreshSitesPage]
  );

  const refreshClustersAll = useCallback(
    async (options?: { cache?: RequestCache }) => {
      await Promise.all([refreshClustersCatalog(options), refreshClustersPage(options)]);
    },
    [refreshClustersCatalog, refreshClustersPage]
  );

  const refreshConnectorsAll = useCallback(
    async (options?: { cache?: RequestCache }) => {
      await Promise.all([refreshConnectorsCatalog(options), refreshConnectorsPage(options)]);
    },
    [refreshConnectorsCatalog, refreshConnectorsPage]
  );

  const refreshConnectivityLists = useCallback(
    (options?: { skipActive?: boolean }) => {
      const skipActive = options?.skipActive ?? false;
      const tasks: Array<Promise<unknown>> = [];
      if (!skipActive || activeTabRef.current !== 'sites') {
        tasks.push(refreshSitesCatalog({ cache: resolveListCache('sites') }));
      }
      if (!skipActive || activeTabRef.current !== 'cloud-access') {
        tasks.push(refreshCloudAccessCatalog({ cache: resolveListCache('cloud-access') }));
      }
      if (!skipActive || activeTabRef.current !== 'clusters') {
        tasks.push(refreshClustersCatalog({ cache: resolveListCache('clusters') }));
      }
      if (!skipActive || activeTabRef.current !== 'connectors') {
        tasks.push(refreshConnectorsCatalog({ cache: resolveListCache('connectors') }));
      }
      return Promise.all(tasks);
    },
    [refreshCloudAccessCatalog, refreshClustersCatalog, refreshConnectorsCatalog, refreshSitesCatalog, resolveListCache]
  );

  useEffect(() => {
    sitesPageRequestIDRef.current += 1;
    cloudAccessRequestIDRef.current += 1;
    setUnavailableCloudProviders(new Set());
    clustersPageRequestIDRef.current += 1;
    connectorsPageRequestIDRef.current += 1;
    connectorCatalogOrgIdRef.current = orgId ?? null;
    connectorCatalogPromiseRef.current = null;
    connectorsCatalogRef.current = [];
    siteClusterCacheRef.current.clear();
    setSitesState(defaultListState);
    setClustersState(defaultListState);
    setConnectorsState(defaultListState);
    setCloudAccessState(defaultListState);
    setSitesPageState(defaultListState);
    setClustersPageState(defaultListState);
    setConnectorsPageState(defaultListState);
    setSiteDrawer(null);
    setClusterDrawer(null);
    setConnectorDrawer(null);
    setAwsTelemetryDrawer(null);
    setSiteDetail(createInitialSiteDetailState());
    setSiteCMDBState(createInitialSiteCMDBState());
    setSelectedSiteCMDBConnectionId('');
    setClusterDetail(createInitialClusterDetailState());
    setConnectorDetail(createInitialConnectorDetailState());
    resetSitePagination();
    resetClusterPagination();
    resetConnectorPagination();
    setLocallyDeletedJobIds(new Set());
  }, [orgId, resetClusterPagination, resetConnectorPagination, resetSitePagination]);

  useEffect(() => {
    if (!orgId || activeTab !== 'cloud-access') {
      return;
    }
    void Promise.all([refreshSitesCatalog({ cache: 'no-store' }), refreshCloudAccessCatalog({ cache: 'no-store' })]);
  }, [activeTab, orgId, refreshCloudAccessCatalog, refreshSitesCatalog]);

  useEffect(() => {
    const needsClusters = (siteDrawer?.siteId && siteDetail.activeTab === 'clusters') || Boolean(connectorDrawer?.open);
    if (!orgId || !needsClusters || clustersState.loading || clustersState.items.length > 0) {
      return;
    }
    void refreshClustersCatalog({ cache: 'no-cache' });
  }, [clustersState.items.length, clustersState.loading, connectorDrawer?.open, orgId, refreshClustersCatalog, siteDetail.activeTab, siteDrawer?.siteId]);

  useEffect(() => {
    if (!orgId || !clusterDrawer?.clusterId || clusterDetail.activeTab !== 'sites' || sitesState.loading || sitesState.items.length > 0) {
      return;
    }
    void refreshSitesCatalog({ cache: 'no-cache' });
  }, [clusterDetail.activeTab, clusterDrawer?.clusterId, orgId, refreshSitesCatalog, sitesState.items.length, sitesState.loading]);

  useEffect(() => {
    if (!orgId || activeTab !== 'sites') {
      return;
    }
    void refreshSitesPage({ cache: 'no-store' });
  }, [activeTab, orgId, refreshSitesPage, sitePageState.cursor, sitePageState.page, sitePageState.pageSize]);

  useEffect(() => {
    if (!orgId || activeTab !== 'clusters') {
      return;
    }
    void refreshClustersPage({ cache: 'no-store' });
  }, [activeTab, clusterPageState.cursor, clusterPageState.page, clusterPageState.pageSize, orgId, refreshClustersPage]);

  useEffect(() => {
    if (!orgId || activeTab !== 'connectors') {
      return;
    }
    void refreshConnectorsPage({ cache: 'no-store' });
  }, [activeTab, connectorPageState.cursor, connectorPageState.page, connectorPageState.pageSize, orgId, refreshConnectorsPage]);

  const loadSiteDeviceInventory = useCallback(
    async (siteId: string) => {
      if (!orgId) {
        return;
      }
      try {
        const byId: Record<string, Device> = {};
        let after: string | undefined;
        const seenCursors = new Set<string>();
        for (let page = 0; page < 8; page += 1) {
          const response = await devicesApi.orgsOrgIdDevicesGet(
            {
              orgId,
              siteId,
              limit: 200,
              after
            },
            { cache: 'no-store' }
          );
          (response.items ?? []).forEach((device) => {
            if (device.id) {
              byId[device.id] = device;
            }
          });
          const nextCursor = typeof response.meta?.next === 'string' && response.meta.next.length > 0 ? response.meta.next : undefined;
          if (!nextCursor || seenCursors.has(nextCursor)) {
            break;
          }
          seenCursors.add(nextCursor);
          after = nextCursor;
        }
        setTopologyDevicesById(byId);
        const devices = Object.values(byId);
        const deviceTypeCounts = summarizeSiteDeviceTypes(devices);
        const vendorCounts = summarizeSiteVendors(devices);
        setSiteDetail((prev) => {
          if (prev.record?.id !== siteId) {
            return prev;
          }
          return {
            ...prev,
            record: {
              ...prev.record,
              deviceCounts: {
                total: devices.length,
                active: prev.record.deviceCounts?.active ?? 0,
                offline: prev.record.deviceCounts?.offline ?? 0
              },
              deviceTypeCounts,
              vendorCounts
            }
          };
        });
      } catch (deviceError) {
        console.warn('[connectivity] failed to load site devices for topology classification', deviceError);
        setTopologyDevicesById({});
      }
    },
    [devicesApi, orgId]
  );

  const loadSiteTopology = useCallback(
    async (siteId: string, options?: { expandInterfaces?: boolean }) => {
      if (!orgId) {
        return;
      }
      const expandInterfaces = Boolean(options?.expandInterfaces);
      setSiteDetail((prev) => ({
        ...prev,
        topologyLoading: true,
        topologyExpandInterfaces: expandInterfaces
      }));
      try {
        const baseTopology = await visibilityApi.orgsOrgIdSitesSiteIdTopologyGet({
          orgId,
          siteId,
          expand: expandInterfaces ? 'interfaces' : undefined
        });
        let resolvedTopology = baseTopology;

        if (!expandInterfaces) {
          try {
            resolvedTopology = await visibilityApi.orgsOrgIdSitesSiteIdTopologyGet({
              orgId,
              siteId,
              expand: 'interfaces'
            });
          } catch (detailedError) {
            console.warn('[connectivity] failed to load detailed topology (expand=interfaces)', detailedError);
          }
        }

        setSiteDetail((prev) => ({
          ...prev,
          topology: resolvedTopology,
          topologyLoading: false
        }));
      } catch (topologyError) {
        console.warn('[connectivity] failed to load site topology', topologyError);
        setSiteDetail((prev) => ({
          ...prev,
          topology: null,
          topologyLoading: false
        }));
      }
    },
    [orgId, visibilityApi]
  );

  const loadSiteDetail = useCallback(
    async (siteId: string, options?: { activeTab?: SiteDetailTab }) => {
      if (!orgId) {
        return;
      }
      const currentState = siteDetailRef.current;
      const desiredTab: SiteDetailTab = options?.activeTab ?? (currentState.record?.id === siteId ? currentState.activeTab : 'details');
      const preservedExpand = currentState.record?.id === siteId ? currentState.topologyExpandInterfaces : false;
      setSiteDetail({
        ...createInitialSiteDetailState(),
        loading: true,
        activeTab: desiredTab,
        topologyExpandInterfaces: preservedExpand
      });
      setTopologyDevicesById({});
      setTopologyFullscreenOpen(false);
      try {
        const response = await sitesApi.orgsOrgIdSitesSiteIdGetRaw({ orgId, siteId });
        const payload = (await response.value()) as SiteView;
        const providerEdgePayload = payload as unknown as { providerEdgePorts?: unknown; provider_edge_ports?: unknown };
        (payload as any).providerEdgePorts = normalizeProviderEdgePorts(providerEdgePayload.providerEdgePorts ?? providerEdgePayload.provider_edge_ports);
        const etag = response.raw.headers.get('ETag') ?? null;
        const [clustersResponse, connectorsResponse] = await Promise.all([
          sitesApi.orgsOrgIdSitesSiteIdConnectorClustersGet({ orgId, siteId, limit: DETAIL_RELATION_PAGE_SIZE }),
          connectorsApi.orgsOrgIdConnectorsGet({ orgId, siteId, limit: DETAIL_RELATION_PAGE_SIZE })
        ]);
        const clusters = (clustersResponse.items ?? []).map((cluster: ConnectorCluster) => ({ ...cluster } as ClusterView));
        const connectors = (connectorsResponse.items ?? []).map((connector) => ({ ...connector } as ConnectorView));
        const knownSiteRecord =
          sitesPageState.items.find((site) => site.id === siteId) ??
          sitesState.items.find((site) => site.id === siteId) ??
          null;
        const mergedRecord = {
          ...(knownSiteRecord ?? {}),
          ...payload
        } as SiteView;
        const connectorCount = firstValidCount(mergedRecord.connectorCount);

        siteClusterCacheRef.current.set(
          siteId,
          clusters
            .map((cluster: ClusterView) => cluster.id)
            .filter((value: string | undefined): value is string => typeof value === 'string')
        );

        setSiteDetail({
          ...createInitialSiteDetailState(),
          loading: false,
          record: {
            ...mergedRecord,
            connectorCount: connectorCount ?? null,
            health: deriveSiteHealth({ ...mergedRecord, connectorCount: connectorCount ?? null })
          },
          etag,
          clusters,
          connectors,
          connectorsNext: connectorsResponse.meta?.next ?? null,
          topologyLoading: true,
          topologyExpandInterfaces: preservedExpand,
          activeTab: desiredTab
        });

        await Promise.all([loadSiteTopology(siteId, { expandInterfaces: preservedExpand }), loadSiteDeviceInventory(siteId)]);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load site detail';
        setSiteDetail({
          ...createInitialSiteDetailState(),
          loading: false,
          error: message,
          activeTab: desiredTab
        });
      }
    },
    [connectorsApi, orgId, sitesApi, loadSiteTopology, loadSiteDeviceInventory, sitesPageState.items, sitesState.items]
  );

  const scheduleOpenSiteDataRefresh = useCallback(
    (options?: { refreshLists?: boolean; refreshTopology?: boolean }) => {
      if (!orgId) {
        return;
      }

      const flushPending = () => {
        siteDataRefreshTimerRef.current = null;
        const currentSite = siteDetailRef.current;
        const siteId = currentSite.record?.id;
        if (!siteId) {
          return;
        }

        if (options?.refreshLists !== false) {
          void refreshSitesAll({ cache: resolveListCache('sites') });
        }

        const preservedExpand = currentSite.topologyExpandInterfaces;
        const refreshes: Array<Promise<void>> = [loadSiteDeviceInventory(siteId)];
        if (options?.refreshTopology !== false) {
          refreshes.push(loadSiteTopology(siteId, { expandInterfaces: preservedExpand }));
        }
        void Promise.all(refreshes);
        siteDataLastRefreshAtRef.current = Date.now();
      };

      if (siteDataRefreshTimerRef.current) {
        return;
      }

      const elapsed = Date.now() - siteDataLastRefreshAtRef.current;
      const waitMs =
        elapsed >= SITE_DATA_CHANGE_MIN_REFRESH_GAP_MS
          ? SITE_DATA_CHANGE_BATCH_MS
          : Math.max(SITE_DATA_CHANGE_BATCH_MS, SITE_DATA_CHANGE_MIN_REFRESH_GAP_MS - elapsed);

      siteDataRefreshTimerRef.current = window.setTimeout(flushPending, waitMs);
    },
    [loadSiteDeviceInventory, loadSiteTopology, orgId, refreshSitesAll, resolveListCache]
  );

  const setSiteDetailTab = useCallback((tab: SiteDetailTab) => {
    setSiteDetail((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const setClusterDetailTab = useCallback((tab: ClusterDetailTab) => {
    setClusterDetail((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const setConnectorDetailTab = useCallback((tab: ConnectorDetailTab) => {
    setConnectorDetail((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const loadClusterDetail = useCallback(
    async (clusterId: string) => {
      if (!orgId) {
        return;
      }
      const currentState = clusterDetailRef.current;
      const preservedTab: ClusterDetailTab = currentState.record?.id === clusterId ? currentState.activeTab : 'details';
      setClusterDetail({
        ...createInitialClusterDetailState(),
        loading: true,
        activeTab: preservedTab
      });
      try {
        const response = await clustersApi.orgsOrgIdConnectorClustersClusterIdGetRaw({ orgId, clusterId });
        const payload = await response.value();
        const etag = response.raw.headers.get('ETag') ?? null;
        const [connectorsResponse, sitesResponse] = await Promise.all([
          connectorsApi.orgsOrgIdConnectorsGet({ orgId, clusterId, limit: DETAIL_RELATION_PAGE_SIZE }),
          sitesApi.orgsOrgIdSitesGet({ orgId, connectorClusterId: clusterId, limit: DETAIL_RELATION_PAGE_SIZE })
        ]);
        const connectors = (connectorsResponse.items ?? []).map((connector) => ({ ...connector } as ConnectorView));
        const attachedSites = (sitesResponse.items ?? []).map((site: Site) => {
          const asView = { ...site } as SiteView;
          return { ...asView, health: deriveSiteHealth(asView) };
        });
        const knownCluster =
          clustersPageState.items.find((cluster) => cluster.id === clusterId) ??
          clustersState.items.find((cluster) => cluster.id === clusterId) ??
          null;

        setClusterDetail({
          ...createInitialClusterDetailState(),
          loading: false,
          record: { ...(knownCluster ?? {}), ...payload } as ClusterView,
          etag,
          sites: attachedSites,
          sitesNext: sitesResponse.meta?.next ?? null,
          connectors,
          connectorsNext: connectorsResponse.meta?.next ?? null,
          activeTab: preservedTab
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load cluster detail';
        setClusterDetail({
          ...createInitialClusterDetailState(),
          loading: false,
          error: message,
          activeTab: preservedTab
        });
      }
    },
    [clustersApi, clustersPageState.items, clustersState.items, connectorsApi, orgId, sitesApi]
  );

  const loadMoreSiteConnectors = useCallback(async () => {
    const current = siteDetailRef.current;
    const siteId = current.record?.id;
    const after = current.connectorsNext;
    if (!orgId || !siteId || !after || current.connectorsLoadingMore) {
      return;
    }

    setSiteDetail((prev) => ({ ...prev, connectorsLoadingMore: true, connectorsError: null }));
    try {
      const response = await connectorsApi.orgsOrgIdConnectorsGet({
        orgId,
        siteId,
        after,
        limit: DETAIL_RELATION_PAGE_SIZE
      });
      const connectors = (response.items ?? []).map((connector) => ({ ...connector } as ConnectorView));
      setSiteDetail((prev) =>
        prev.record?.id !== siteId
          ? prev
          : {
              ...prev,
              connectors: appendUniqueById(prev.connectors, connectors),
              connectorsNext: response.meta?.next ?? null,
              connectorsLoadingMore: false,
              connectorsError: null
            }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more connectors';
      setSiteDetail((prev) =>
        prev.record?.id !== siteId ? prev : { ...prev, connectorsLoadingMore: false, connectorsError: message }
      );
    }
  }, [connectorsApi, orgId]);

  const loadMoreClusterConnectors = useCallback(async () => {
    const current = clusterDetailRef.current;
    const clusterId = current.record?.id;
    const after = current.connectorsNext;
    if (!orgId || !clusterId || !after || current.connectorsLoadingMore) {
      return;
    }

    setClusterDetail((prev) => ({ ...prev, connectorsLoadingMore: true, connectorsError: null }));
    try {
      const response = await connectorsApi.orgsOrgIdConnectorsGet({
        orgId,
        clusterId,
        after,
        limit: DETAIL_RELATION_PAGE_SIZE
      });
      const connectors = (response.items ?? []).map((connector) => ({ ...connector } as ConnectorView));
      setClusterDetail((prev) =>
        prev.record?.id !== clusterId
          ? prev
          : {
              ...prev,
              connectors: appendUniqueById(prev.connectors, connectors),
              connectorsNext: response.meta?.next ?? null,
              connectorsLoadingMore: false,
              connectorsError: null
            }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more connectors';
      setClusterDetail((prev) =>
        prev.record?.id !== clusterId ? prev : { ...prev, connectorsLoadingMore: false, connectorsError: message }
      );
    }
  }, [connectorsApi, orgId]);

  const loadMoreClusterSites = useCallback(async () => {
    const current = clusterDetailRef.current;
    const clusterId = current.record?.id;
    const after = current.sitesNext;
    if (!orgId || !clusterId || !after || current.sitesLoadingMore) {
      return;
    }

    setClusterDetail((prev) => ({ ...prev, sitesLoadingMore: true, sitesError: null }));
    try {
      const response = await sitesApi.orgsOrgIdSitesGet({
        orgId,
        connectorClusterId: clusterId,
        after,
        limit: DETAIL_RELATION_PAGE_SIZE
      });
      const sites = (response.items ?? []).map((site: Site) => {
        const asView = { ...site } as SiteView;
        return { ...asView, health: deriveSiteHealth(asView) };
      });
      setClusterDetail((prev) =>
        prev.record?.id !== clusterId
          ? prev
          : {
              ...prev,
              sites: appendUniqueById(prev.sites, sites),
              sitesNext: response.meta?.next ?? null,
              sitesLoadingMore: false,
              sitesError: null
            }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more sites';
      setClusterDetail((prev) =>
        prev.record?.id !== clusterId ? prev : { ...prev, sitesLoadingMore: false, sitesError: message }
      );
    }
  }, [orgId, sitesApi]);

  const loadConnectorDetail = useCallback(
    async (connectorId: string) => {
      if (!orgId) {
        return;
      }
      const currentState = connectorDetailRef.current;
      const preservedTab: ConnectorDetailTab = currentState.record?.id === connectorId ? currentState.activeTab : 'details';
      setConnectorDetail({
        ...createInitialConnectorDetailState(),
        loading: true,
        activeTab: preservedTab
      });
      try {
        const response = await connectorsApi.orgsOrgIdConnectorsConnectorIdGetRaw({ orgId, connectorId });
        const payload = await response.value();
        const etag = response.raw.headers.get('ETag') ?? null;
        setConnectorDetail({
          ...createInitialConnectorDetailState(),
          loading: false,
          record: { ...payload } as ConnectorView,
          etag,
          activeTab: preservedTab
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load connector detail';
        setConnectorDetail({
          ...createInitialConnectorDetailState(),
          loading: false,
          error: message,
          activeTab: preservedTab
        });
      }
    },
    [orgId, connectorsApi]
  );

  const refreshFromChangefeed = useCallback((event?: { resource?: string | null }) => {
    if (!orgId) {
      return;
    }
    const resource = (event?.resource ?? '').toLowerCase();
    if (resource === 'discovery_runs' || resource === 'discovery_jobs') {
      scheduleDiscoveryPanelRefresh(resource);
      if (resource === 'discovery_runs') {
        scheduleOpenSiteDataRefresh();
      }
      return;
    }
    if (resource === 'sites') {
      void refreshSitesAll({ cache: resolveListCache('sites') });
    } else if (resource === 'connector_clusters') {
      void refreshClustersAll({ cache: resolveListCache('clusters') });
    } else if (resource === 'connectors') {
      void refreshConnectorsAll({ cache: resolveListCache('connectors') });
    } else if (resource === 'connector_cluster_sites') {
      void Promise.all([
        refreshSitesAll({ cache: resolveListCache('sites') }),
        refreshClustersAll({ cache: resolveListCache('clusters') })
      ]);
    } else if (resource === 'devices') {
      scheduleOpenSiteDataRefresh();
    } else {
      void refreshConnectivityLists();
    }

    if (!runDetailOpen && resource !== 'devices' && siteDetail.record?.id) {
      loadSiteDetail(siteDetail.record.id, { activeTab: siteDetail.activeTab });
    }
    if (clusterDetail.record?.id) {
      loadClusterDetail(clusterDetail.record.id);
    }
    if (connectorDetail.record?.id) {
      loadConnectorDetail(connectorDetail.record.id);
    }
  }, [
    clusterDetail.record?.id,
    connectorDetail.record?.id,
    loadClusterDetail,
    loadConnectorDetail,
    loadSiteDetail,
    orgId,
    refreshClustersAll,
    refreshConnectorsAll,
    refreshConnectivityLists,
    resolveListCache,
    runDetailOpen,
    scheduleOpenSiteDataRefresh,
    scheduleDiscoveryPanelRefresh,
    refreshSitesAll,
    siteDetail.activeTab,
    siteDetail.record?.id
  ]);

  useOrgChangefeedEffect(
    ['sites', 'connectors', 'connector_clusters', 'connector_cluster_sites', 'devices', 'discovery_jobs', 'discovery_runs'],
    refreshFromChangefeed,
    [refreshFromChangefeed]
  );

  const handleCreateSite = useCallback(
    async (values: SiteFormValues) => {
      if (!orgId) {
        return;
      }
      setSiteCreateSaving(true);
      setSiteCreateError(null);
      try {
        await sitesApi.orgsOrgIdSitesPost({ orgId, siteCreate: toSiteCreatePayload(values) });
        setSiteCreateSaving(false);
        setSiteDrawer(null);
        siteClusterCacheRef.current.clear();
        await refreshSitesAll({ cache: resolveListCache('sites') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create site';
        setSiteCreateError(message);
        setSiteCreateSaving(false);
      }
    },
    [orgId, sitesApi, refreshSitesAll, resolveListCache]
  );

  const handleCreateCluster = useCallback(
    async (values: ClusterFormValues) => {
      if (!orgId) {
        return;
      }
      setClusterCreateSaving(true);
      setClusterCreateError(null);
      try {
        const payload = toClusterCreatePayload(values);
        await clustersApi.orgsOrgIdConnectorClustersPost({ orgId, connectorClusterCreate: payload });
        setClusterCreateSaving(false);
        setClusterDrawer(null);
        await refreshClustersAll({ cache: resolveListCache('clusters') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create cluster';
        setClusterCreateError(message);
        setClusterCreateSaving(false);
      }
    },
    [orgId, clustersApi, refreshClustersAll, resolveListCache]
  );

  const handleCreateConnector = useCallback(
    async (values: ConnectorFormValues) => {
      if (!orgId) {
        return;
      }
      if (!values.clusterId) {
        setConnectorCreateError('Select a cluster for the new connector.');
        return;
      }
      setConnectorCreateSaving(true);
      setConnectorCreateError(null);
      try {
        const payload = toConnectorCreatePayload(values);
        await connectorsApi.orgsOrgIdConnectorsPost({ orgId, connectorCreate: payload });
        setConnectorCreateSaving(false);
        setConnectorDrawer(null);
        await refreshConnectorsAll({ cache: resolveListCache('connectors') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create connector';
        setConnectorCreateError(message);
        setConnectorCreateSaving(false);
      }
    },
    [connectorsApi, orgId, refreshConnectorsAll, resolveListCache]
  );

  const handleUpdateSite = useCallback(
    async (values: SiteFormValues) => {
      if (!orgId || !siteDetail.record?.id || !siteDetail.etag) {
        return;
      }
      setSiteDetail((prev) => ({ ...prev, saving: true, error: null }));
      try {
        await sitesApi.orgsOrgIdSitesSiteIdPatch({
          orgId,
          siteId: siteDetail.record.id,
          siteUpdate: toSiteUpdatePayload(values),
          ifMatch: siteDetail.etag
        });
        await loadSiteDetail(siteDetail.record.id);
        await refreshSitesAll({ cache: resolveListCache('sites') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update site';
        setSiteDetail((prev) => ({ ...prev, saving: false, error: message }));
        return;
      }
      setSiteDetail((prev) => ({ ...prev, saving: false }));
    },
    [loadSiteDetail, orgId, refreshSitesAll, resolveListCache, siteDetail.etag, siteDetail.record?.id, sitesApi]
  );

  const searchPingMeshEndpoints = useCallback(
    async (request: TopologyPathEndpointSearchRequest, signal: AbortSignal): Promise<TopologyPathEndpointSearchResponse> => {
      if (!orgId) {
        throw new Error('No active organization is selected.');
      }
      return topologyApi.searchTopologyPathEndpoints(
        { orgId, topologyPathEndpointSearchRequest: request },
        { signal }
      );
    },
    [orgId, topologyApi]
  );

  const handleUpdateSitePingEndpoint = useCallback(
    async (rawEndpoint: string) => {
      if (!orgId || !siteDetail.record?.id || !siteDetail.etag) {
        return;
      }
      const endpoint = rawEndpoint.trim();
      // The generated TypeScript model represents nullable fields as optional
      // strings. The API contract intentionally accepts null here to clear the
      // saved Site attribute.
      const siteUpdate = (endpoint ? { pingEndpointIp: endpoint } : { pingEndpointIp: null }) as unknown as SiteUpdate;

      setSiteDetail((prev) => ({ ...prev, saving: true, error: null }));
      try {
        await sitesApi.orgsOrgIdSitesSiteIdPatch({
          orgId,
          siteId: siteDetail.record.id,
          siteUpdate,
          ifMatch: siteDetail.etag
        });
        await loadSiteDetail(siteDetail.record.id, { activeTab: 'ping-mesh' });
        await refreshSitesAll({ cache: resolveListCache('sites') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update Ping Mesh endpoint';
        setSiteDetail((prev) => ({ ...prev, saving: false, error: message }));
        return;
      }
      setSiteDetail((prev) => ({ ...prev, saving: false }));
    },
    [loadSiteDetail, orgId, refreshSitesAll, resolveListCache, siteDetail.etag, siteDetail.record?.id, sitesApi]
  );

  const handleUpdateCluster = useCallback(
    async (values: ClusterFormValues) => {
      if (!orgId || !clusterDetail.record?.id || !clusterDetail.etag) {
        return;
      }
      setClusterDetail((prev) => ({ ...prev, saving: true, error: null }));
      try {
        const payload = toClusterUpdatePayload(values);
        await clustersApi.orgsOrgIdConnectorClustersClusterIdPatch({
          orgId,
          clusterId: clusterDetail.record.id,
          connectorClusterUpdate: payload,
          ifMatch: clusterDetail.etag
        });
        await loadClusterDetail(clusterDetail.record.id);
        await refreshClustersAll({ cache: resolveListCache('clusters') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update cluster';
        setClusterDetail((prev) => ({ ...prev, saving: false, error: message }));
        return;
      }
      setClusterDetail((prev) => ({ ...prev, saving: false }));
    },
    [clusterDetail.etag, clusterDetail.record?.id, clustersApi, loadClusterDetail, orgId, refreshClustersAll, resolveListCache]
  );

  const handleUpdateConnector = useCallback(
    async (values: ConnectorFormValues) => {
      if (!orgId || !connectorDetail.record?.id || !connectorDetail.etag) {
        return;
      }
      setConnectorDetail((prev) => ({ ...prev, saving: true, error: null }));
      try {
        const payload = toConnectorUpdatePayload(values);
        await connectorsApi.orgsOrgIdConnectorsConnectorIdPatch({
          orgId,
          connectorId: connectorDetail.record.id,
          connectorUpdate: payload,
          ifMatch: connectorDetail.etag
        });
        await loadConnectorDetail(connectorDetail.record.id);
        await refreshConnectorsAll({ cache: resolveListCache('connectors') });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update connector';
        setConnectorDetail((prev) => ({ ...prev, saving: false, error: message }));
        return;
      }
      setConnectorDetail((prev) => ({ ...prev, saving: false }));
    },
    [connectorDetail.etag, connectorDetail.record?.id, connectorsApi, loadConnectorDetail, orgId, refreshConnectorsAll, resolveListCache]
  );

  const downloadConnectorUserdata = useCallback(
    async (connectorId: string, accept: 'text/cloud-config' | 'application/json' | 'application/base64+json') => {
      if (!orgId) {
        return;
      }
      setConnectorDetail((prev) => ({ ...prev, userdataStatus: 'loading', userdataError: null }));
      try {
        const response = await connectorsApi.orgsOrgIdConnectorsConnectorIdUserdataGetRaw(
          { orgId, connectorId },
          async ({ init }) => {
            const headers = await cloneHeadersWithAuth(init.headers ?? undefined);
            headers['Accept'] = accept;
            return { headers };
          }
        );
        if (accept === 'text/cloud-config') {
          const text = await response.raw.text();
          const blob = new Blob([text], { type: 'text/plain' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${connectorId}-userdata.yaml`;
          link.click();
          URL.revokeObjectURL(link.href);
        } else {
          const payload = await response.value();
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${connectorId}-userdata.json`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
        setConnectorDetail((prev) => ({ ...prev, userdataStatus: 'success', userdataError: null }));
      } catch (error) {
        let message = 'Failed to download bootstrap bundle';
        if (error instanceof ResponseError) {
          try {
            const payload = await error.response.clone().json();
            message =
              (payload as { title?: string; detail?: string; message?: string }).title ??
              (payload as { title?: string; detail?: string; message?: string }).detail ??
              (payload as { title?: string; detail?: string; message?: string }).message ??
              message;
          } catch {
            message = `Request failed with status ${error.response.status}`;
          }
        } else if (error instanceof Error) {
          message = error.message;
        }
        setConnectorDetail((prev) => ({ ...prev, userdataStatus: 'error', userdataError: message }));
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert(message);
        }
      }
    },
    [orgId, connectorsApi]
  );

  const resetConnectorEnrollment = useCallback(
    async (connectorId: string) => {
      if (!orgId) {
        return;
      }
      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        const confirmed = window.confirm(
          'Reset enrollment? This will prevent the current VM certificate from authenticating on future VPN handshakes. To immediately cut off an already-connected VM, you must also disconnect it.'
        );
        if (!confirmed) {
          return;
        }
      }
      setConnectorDetail((prev) => ({ ...prev, resetStatus: 'loading', resetError: null }));
      try {
        const url = buildOrgUrl(basePath, orgId, `connectors/${connectorId}/enrollment:reset`);
        const headers = await cloneHeadersWithAuth(undefined);
        const response = await fetchWithAuthRetry(url, { method: 'POST', headers });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        setConnectorDetail((prev) => ({ ...prev, resetStatus: 'success', resetError: null }));
        await Promise.all([refreshConnectorsAll(), refreshClustersAll(), refreshSitesAll()]);
        await loadConnectorDetail(connectorId);
      } catch (error) {
        let message = 'Failed to reset enrollment';
        if (error instanceof ApiResponseError) {
          try {
            const payload = await error.response.clone().json();
            message =
              (payload as { title?: string; detail?: string; message?: string }).title ??
              (payload as { title?: string; detail?: string; message?: string }).detail ??
              (payload as { title?: string; detail?: string; message?: string }).message ??
              message;
          } catch {
            message = `Request failed with status ${error.response.status}`;
          }
        } else if (error instanceof Error) {
          message = error.message;
        }
        setConnectorDetail((prev) => ({ ...prev, resetStatus: 'error', resetError: message }));
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert(message);
        }
      }
    },
    [basePath, loadConnectorDetail, orgId, refreshClustersAll, refreshConnectorsAll, refreshSitesAll]
  );

  const attachClusterToSite = useCallback(
    async (siteId: string, clusterId: string) => {
      if (!orgId) {
        return;
      }
      try {
        const clusterResponse = await clustersApi.orgsOrgIdConnectorClustersClusterIdGetRaw({ orgId, clusterId });
        const etag = clusterResponse.raw.headers.get('ETag') ?? '';
        const request: ClusterSitesAttachRequest = { siteIds: [siteId] };
        siteClusterCacheRef.current.delete(siteId);
        await clustersApi.orgsOrgIdConnectorClustersClusterIdSitesattachPost({
          orgId,
          clusterId,
          ifMatch: etag,
          clusterSitesAttachRequest: request
        });
        await Promise.all([
          loadSiteDetail(siteId),
          loadClusterDetail(clusterId),
          refreshSitesAll({ cache: resolveListCache('sites') }),
          refreshClustersAll({ cache: resolveListCache('clusters') })
        ]);
      } catch (error) {
        console.warn('Failed to attach cluster to site', error);
      }
    },
    [orgId, clustersApi, loadSiteDetail, loadClusterDetail, refreshSitesAll, refreshClustersAll, resolveListCache]
  );

  const detachClusterFromSite = useCallback(
    async (siteId: string, clusterId: string) => {
      if (!orgId) {
        return;
      }
      try {
        const clusterResponse = await clustersApi.orgsOrgIdConnectorClustersClusterIdGetRaw({ orgId, clusterId });
        const etag = clusterResponse.raw.headers.get('ETag') ?? '';
        const request: ClusterSitesDetachRequest = { siteIds: [siteId] };
        siteClusterCacheRef.current.delete(siteId);
        await clustersApi.orgsOrgIdConnectorClustersClusterIdSitesdetachPost({
          orgId,
          clusterId,
          ifMatch: etag,
          clusterSitesDetachRequest: request
        });
        await Promise.all([
          loadSiteDetail(siteId),
          loadClusterDetail(clusterId),
          refreshSitesAll({ cache: resolveListCache('sites') }),
          refreshClustersAll({ cache: resolveListCache('clusters') })
        ]);
      } catch (error) {
        console.warn('Failed to detach cluster from site', error);
      }
    },
    [orgId, clustersApi, loadSiteDetail, loadClusterDetail, refreshSitesAll, refreshClustersAll, resolveListCache]
  );

  const deleteSites = useCallback(
    async (rows: SiteView[]) => {
      if (!orgId) {
        return;
      }
      const ids = rows.filter(hasStableId).map((row) => row.id);
      if (ids.length === 0) {
        return;
      }
      const results = await Promise.allSettled(
        ids.map(async (siteId) => {
          const response = await sitesApi.orgsOrgIdSitesSiteIdGetRaw({ orgId, siteId });
          const etag = response.raw.headers.get('ETag') ?? undefined;
          await sitesApi.orgsOrgIdSitesSiteIdDelete({ orgId, siteId, ifMatch: etag });
          siteClusterCacheRef.current.delete(siteId);
          return siteId;
        })
      );
      const deletedIds = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      setSitesPageState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
      if (siteDrawer?.siteId && deletedIds.includes(siteDrawer.siteId)) {
        setSiteDrawer(null);
        setSiteDetail(createInitialSiteDetailState());
      }
      void refreshSitesAll({ cache: resolveListCache('sites') });
      return { deletedIds, failedCount: results.length - deletedIds.length };
    },
    [orgId, sitesApi, siteDrawer?.siteId, refreshSitesAll, resolveListCache]
  );

  const deleteClusters = useCallback(
    async (rows: ClusterView[]) => {
      if (!orgId) {
        return;
      }
      const ids = rows.filter(hasStableId).map((row) => row.id);
      if (ids.length === 0) {
        return;
      }
      const results = await Promise.allSettled(
        ids.map(async (clusterId) => {
          const response = await clustersApi.orgsOrgIdConnectorClustersClusterIdGetRaw({ orgId, clusterId });
          const etag = response.raw.headers.get('ETag') ?? undefined;
          await clustersApi.orgsOrgIdConnectorClustersClusterIdDelete({ orgId, clusterId, ifMatch: etag });
          siteClusterCacheRef.current.forEach((clusterIds, siteId) => {
            if (!clusterIds.includes(clusterId)) {
              return;
            }
            const filtered = clusterIds.filter((value) => value !== clusterId);
            siteClusterCacheRef.current.set(siteId, filtered);
          });
          return clusterId;
        })
      );
      const deletedIds = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      setClustersPageState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
      if (clusterDrawer?.clusterId && deletedIds.includes(clusterDrawer.clusterId)) {
        setClusterDrawer(null);
        setClusterDetail(createInitialClusterDetailState());
      }
      void refreshClustersAll({ cache: resolveListCache('clusters') });
      return { deletedIds, failedCount: results.length - deletedIds.length };
    },
    [clustersApi, clusterDrawer?.clusterId, orgId, refreshClustersAll, resolveListCache]
  );

  const deleteConnectors = useCallback(
    async (rows: ConnectorView[]) => {
      if (!orgId) {
        return;
      }
      const ids = rows.filter(hasStableId).map((row) => row.id);
      if (ids.length === 0) {
        return;
      }
      const results = await Promise.allSettled(
        ids.map(async (connectorId) => {
          const response = await connectorsApi.orgsOrgIdConnectorsConnectorIdGetRaw({ orgId, connectorId });
          const etag = response.raw.headers.get('ETag') ?? undefined;
          await connectorsApi.orgsOrgIdConnectorsConnectorIdDelete({ orgId, connectorId, ifMatch: etag });
          return connectorId;
        })
      );
      const deletedIds = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      setConnectorsPageState((current) => ({ ...current, items: current.items.filter((item) => !item.id || !deletedIds.includes(item.id)) }));
      if (connectorDrawer?.connectorId && deletedIds.includes(connectorDrawer.connectorId)) {
        setConnectorDrawer(null);
        setConnectorDetail(createInitialConnectorDetailState());
      }
      void refreshConnectorsAll({ cache: resolveListCache('connectors') });
      return { deletedIds, failedCount: results.length - deletedIds.length };
    },
    [connectorDrawer?.connectorId, connectorsApi, orgId, refreshConnectorsAll, resolveListCache]
  );

  const confirmResourceDelete = useCallback(async () => {
    if (!pendingResourceDelete || resourceDeleteBusy) {
      return;
    }
    setResourceDeleteBusy(true);
    const request = pendingResourceDelete;
    const label = request.kind === 'sites' ? 'site' : request.kind === 'clusters' ? 'cluster' : 'connector';
    try {
      const result = request.kind === 'sites'
        ? await deleteSites(request.rows)
        : request.kind === 'clusters'
          ? await deleteClusters(request.rows)
          : await deleteConnectors(request.rows);
      const deletedCount = result?.deletedIds.length ?? 0;
      const failedCount = result?.failedCount ?? request.rows.length;
      if (deletedCount > 0) {
        setPendingResourceDelete(null);
        setDeleteToast({
          tone: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `${deletedCount} of ${request.rows.length} ${label}s deleted. ${failedCount} could not be deleted.`
              : `${deletedCount} ${label}${deletedCount === 1 ? '' : 's'} deleted successfully.`
        });
      } else {
        setDeleteToast({ tone: 'error', message: `Failed to delete the selected ${label}s.` });
      }
    } catch (error) {
      console.warn(`[connectivity] failed to refresh ${label}s after deletion`, error);
      setDeleteToast({ tone: 'error', message: `Failed to complete ${label} deletion.` });
    } finally {
      setResourceDeleteBusy(false);
    }
  }, [deleteClusters, deleteConnectors, deleteSites, pendingResourceDelete, resourceDeleteBusy]);

  const topError =
    sitesPageState.error ??
    clustersPageState.error ??
    connectorsPageState.error ??
    sitesState.error ??
    clustersState.error ??
    connectorsState.error ??
    null;
  const topSlot = topError ? <OrgModulePlaceholder intent={`Failed to load some connectivity data: ${topError}`} /> : undefined;

  const handleSiteRowClick = useCallback(
    (site: SiteView) => {
      if (!site.id) {
        return;
      }
      setSiteDetailTab('details');
      setSiteDrawer({ siteId: site.id, open: true });
      loadSiteDetail(site.id);
    },
    [loadSiteDetail, setSiteDetailTab]
  );

  const handleSiteTopologyShortcut = useCallback(
    (siteId: string) => {
      setSiteDetailTab('topology');
      setSiteDrawer({ siteId, open: true });
      loadSiteDetail(siteId, { activeTab: 'topology' });
    },
    [loadSiteDetail, setSiteDetailTab]
  );

  const handleSiteCockpitShortcut = useCallback(
    (siteId: string) => {
      setSiteDetailTab('cockpit');
      setSiteDrawer({ siteId, open: true });
      loadSiteDetail(siteId, { activeTab: 'cockpit' });
    },
    [loadSiteDetail, setSiteDetailTab]
  );

  const handleSiteDiscoverShortcut = useCallback(
    (siteId: string) => {
      setSiteDetailTab('discover');
      setSiteDrawer({ siteId, open: true });
      loadSiteDetail(siteId, { activeTab: 'discover' });
    },
    [loadSiteDetail, setSiteDetailTab]
  );

  const activeSiteId = siteDetail.record?.id ?? null;

  const siteCockpitTimeRange = useMemo(() => computeTimeRange(siteCockpitRangePreset), [siteCockpitRangePreset]);
  const siteCockpitFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    if (siteDetail.activeTab !== 'cockpit') {
      return null;
    }
    if (!activeSiteId) {
      return null;
    }
    return (): Promise<DashboardsRecommendedResponse> =>
      dashboardsApi.orgsOrgIdDashboardsRecommendedGet({
        orgId,
        pageKey: 'site.cockpit',
        scopeType: 'site',
        scopeId: activeSiteId,
        from: siteCockpitTimeRange.from,
        to: siteCockpitTimeRange.to
      });
  }, [activeSiteId, dashboardsApi, orgId, siteCockpitTimeRange.from, siteCockpitTimeRange.to, siteDetail.activeTab]);

	  const siteCockpitQuery = useApiQuery<DashboardsRecommendedResponse>(siteCockpitFetcher, [siteCockpitFetcher]);

	  useGrafanaEmbedSessionKeepalive({
	    dashboardsApi,
	    orgId,
	    enabled:
	      Boolean(orgId) &&
	      (siteDetail.activeTab === 'cockpit' ||
	        Boolean(connectorDetail.record) ||
	        (clusterDetail.activeTab === 'details' && Boolean(clusterDetail.record))),
	    logTag: 'connectivity'
	  });

  const handleTopologyExpandToggle = useCallback(
    (checked: boolean) => {
      if (!activeSiteId) {
        return;
      }
      loadSiteTopology(activeSiteId, { expandInterfaces: checked });
      setEdgeDetail(null);
      setManualNodePositions({});
    },
    [activeSiteId, loadSiteTopology]
  );

  const handleLayoutReset = useCallback(() => {
    setManualNodePositions({});
    setEdgeDetail(null);
    setLayoutResetCounter((count) => count + 1);
  }, []);

  const handleNodePositionPersist = useCallback((nodeId: string, position: { x: number; y: number }) => {
    setManualNodePositions((prev) => ({
      ...prev,
      [nodeId]: position
    }));
  }, []);

  const handleTopologyFullscreenToggle = useCallback((open: boolean) => {
    setTopologyFullscreenOpen(open);
    setEdgeDetail(null);
    setEdgeDetailPosition(null);
    setDeviceDetail(null);
    setDeviceDetailPosition(null);
  }, []);

  useEffect(() => {
    if (!topologyFullscreenOpen) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleTopologyFullscreenToggle(false);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [topologyFullscreenOpen, handleTopologyFullscreenToggle]);

  const projectTooltipPosition = useCallback(
    (position?: { x: number; y: number } | null, variant: 'device' | 'edge' = 'device') => {
      const viewport = topologyFullscreenOpen ? topologyWindowDiagramRef.current : topologyDiagramRef.current;
      if (!position || !viewport) {
        return null;
      }
      const { width, height } = viewport.getBoundingClientRect();
      const padding = 14;
      const offset = 18;
      const estimatedWidth = variant === 'edge' ? 340 : 300;
      const estimatedHeight = variant === 'edge' ? 190 : 170;
      const anchorX = Math.min(Math.max(position.x, padding), Math.max(padding, width - padding));
      const anchorY = Math.min(Math.max(position.y, padding), Math.max(padding, height - padding));

      let preferredX = anchorX + offset;
      if (preferredX + estimatedWidth > width - padding) {
        preferredX = anchorX - estimatedWidth - offset;
      }
      if (preferredX < padding) {
        preferredX = Math.min(
          Math.max(anchorX - estimatedWidth / 2, padding),
          Math.max(padding, width - estimatedWidth - padding)
        );
      }

      let preferredY = anchorY - estimatedHeight - offset;
      if (preferredY < padding) {
        preferredY = anchorY + offset;
      }
      if (preferredY + estimatedHeight > height - padding) {
        preferredY = Math.max(padding, height - estimatedHeight - padding);
      }

      return {
        x: Math.min(Math.max(preferredX, padding), Math.max(padding, width - estimatedWidth - padding)),
        y: Math.min(Math.max(preferredY, padding), Math.max(padding, height - estimatedHeight - padding))
      };
    },
    [topologyFullscreenOpen]
  );

  const handleEdgeFocus = useCallback(
    (detail: EdgeDetail | null, position?: { x: number; y: number }) => {
      if (detail) {
        setDeviceDetail(null);
        setDeviceDetailPosition(null);
      }
      setEdgeDetail(detail);
      setEdgeDetailPosition(detail && position ? projectTooltipPosition(position, 'edge') : null);
    },
    [projectTooltipPosition]
  );

  const handleDeviceFocus = useCallback(
    (detail: DeviceTooltipData | null, position?: { x: number; y: number }) => {
      if (!detail) {
        setDeviceDetail(null);
        setDeviceDetailPosition(null);
        return;
      }
      setEdgeDetail(null);
      setEdgeDetailPosition(null);

      setDeviceDetail(detail);
      setDeviceDetailPosition(projectTooltipPosition(position, 'device') ?? null);
    },
    [projectTooltipPosition]
  );

  useEffect(() => {
    setEdgeDetail(null);
    setEdgeDetailPosition(null);
    setDeviceDetail(null);
    setDeviceDetailPosition(null);
  }, [siteDetail.topologyExpandInterfaces, layoutResetCounter, siteDetail.record?.id]);

  useEffect(() => {
    setManualNodePositions({});
    setEdgeDetail(null);
    setTopologyFullscreenOpen(false);
    setLayoutResetCounter((count) => count + 1);
  }, [siteDetail.record?.id]);

  const cytoscapeElements = useMemo(
    () => buildCytoscapeElements(siteDetail.topology, topologyDevicesById, siteDetail.topologyExpandInterfaces),
    [siteDetail.topology, topologyDevicesById, siteDetail.topologyExpandInterfaces, layoutResetCounter]
  );

  const topologyDiagramKey = `${siteDetail.topologyExpandInterfaces ? 'interfaces' : 'devices'}-${layoutResetCounter}`;

  const cloudAccessRows = useMemo<CloudAccessView[]>(
    () => buildCloudAccessRows(sitesState.items, cloudAccessState.items, unavailableCloudProviders),
    [cloudAccessState.items, sitesState.items, unavailableCloudProviders]
  );
  const awsOnlyCloudAccess = useMemo(
    () => cloudAccessRows.every((row) => row.provider.trim().toLowerCase() === 'aws'),
    [cloudAccessRows]
  );

  const tabItems = useMemo(
    () => [
      { id: 'sites', label: 'Sites' },
      { id: 'clusters', label: 'Clusters' },
      { id: 'connectors', label: 'Connectors' },
      { id: 'cloud-access', label: 'Cloud Access' }
    ],
    []
  );

  const cloudAccessColumns = useMemo<Array<ListWorkspaceColumn<CloudAccessView>>>(
    () => [
      ...(awsOnlyCloudAccess
        ? []
        : [{ key: 'provider', header: 'Provider', render: (row: CloudAccessView) => cloudProviderLabel(row.provider) }]),
      {
        key: 'accountId',
        header: awsOnlyCloudAccess ? 'AWS account ID' : 'Account / subscription',
        render: (row) => row.accountId || '—'
      },
      {
        key: 'type',
        header: 'Type',
        render: () => 'Cloud discovery'
      },
      {
        key: 'sites',
        header: 'Linked sites',
        render: (row) => row.linkedSiteCount
      },
      {
        key: 'regions',
        header: awsOnlyCloudAccess ? 'Regions' : 'Regions / locations',
        render: (row) => row.linkedLocations.join(', ') || '—'
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          if (row.provider === 'azure' && row.status === 'pending') {
            return 'Azure is still applying access';
          }
          return row.status === 'enabled' ? 'Enabled' : 'Action required';
        }
      },
    ],
    [awsOnlyCloudAccess]
  );

  const siteColumns = useMemo<Array<ListWorkspaceColumn<SiteView>>>(
    () => [
      { key: 'name', header: 'Site', render: (row) => row.name ?? row.id ?? '—' },
      {
        key: 'location',
        header: 'Location / scope',
        render: getSiteLocationOrScope
      },
      {
        key: 'health',
        header: 'Health',
        render: (row) => deriveSiteHealth(row)
      },
      {
        key: 'connectors',
        header: 'Connectors',
        render: (row) => deriveSiteConnectorCount(row) ?? '—'
      },
      {
        key: 'devices',
        header: 'Devices',
        align: 'right',
        render: (row) => formatNumber(row.deviceCounts?.total)
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      }
    ],
    []
  );

  const inferAwsTelemetryRegionForCluster = useCallback(
    (clusterId: string): string | undefined => {
      const regions = new Set<string>();
      sitesState.items.forEach((site) => {
        const siteId = site.id;
        if (!siteId) {
          return;
        }
        const attachedClusterIds = siteClusterCacheRef.current.get(siteId) ?? [];
        if (!attachedClusterIds.includes(clusterId)) {
          return;
        }
        const region = getSiteAwsRegion(site);
        if (region) {
          regions.add(region);
        }
      });
      return regions.size === 1 ? Array.from(regions)[0] : undefined;
    },
    [sitesState.items]
  );

  const clusterColumns = useMemo<Array<ListWorkspaceColumn<ClusterView>>>(
    () => [
      { key: 'name', header: 'Cluster', render: (row) => row.name ?? row.id ?? '—' },
      {
        key: 'ha',
        header: 'HA',
        render: (row) => row.haState ?? '—'
      },
      {
        key: 'connectors',
        header: 'Connectors',
        render: (row) => typeof row.connectorsOnline === 'number'
          ? `${row.connectorsOnline}/${row.connectorsTotal ?? 0}`
          : `—/${row.connectorsTotal ?? 0}`
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => row.status ?? '—'
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      },
      {
        key: 'deployAwsTelemetry',
        header: 'Deploy',
        align: 'right',
        render: (row) => {
          const clusterId = row.id;
          if (!clusterId) {
            return '—';
          }
          return (
            <button
              type="button"
              className="chip-button"
              onClick={(event) => {
                event.stopPropagation();
                setAwsTelemetryDrawer({
                  clusterId,
                  clusterName: row.name?.trim() || undefined,
                  initialRegion: inferAwsTelemetryRegionForCluster(clusterId)
                });
              }}
            >
              AWS Telemetry Stack
            </button>
          );
        }
      }
    ],
    [inferAwsTelemetryRegionForCluster]
  );

  const connectorColumns = useMemo<Array<ListWorkspaceColumn<ConnectorView>>>(
    () => [
      { key: 'name', header: 'Connector', render: (row) => row.name ?? row.id ?? '—' },
      {
        key: 'status',
        header: 'Status',
        render: (row) => row.status ?? '—'
      },
      {
        key: 'cluster',
        header: 'Cluster',
        render: (row) => (row.clusterId ? clusterNames[row.clusterId] ?? row.clusterId : '—')
      },
      {
        key: 'lastSeen',
        header: 'Last seen',
        render: (row) => formatDateTime(row.lastSeenAt ?? row.updatedAt ?? null)
      }
    ],
    [clusterNames]
  );

  const siteClusterColumns = useMemo<Array<ListWorkspaceColumn<ClusterView>>>(
    () => [
      { key: 'name', header: 'Cluster', render: (row) => row.name ?? row.id ?? '—' },
      { key: 'ha', header: 'HA', render: (row) => row.haState ?? '—' },
      { key: 'status', header: 'Status', render: (row) => row.status ?? '—' }
    ],
    []
  );

  const siteConnectorColumns = useMemo<Array<ListWorkspaceColumn<ConnectorView>>>(
    () => [
      { key: 'name', header: 'Connector', render: (row) => row.name ?? row.id ?? '—' },
      { key: 'status', header: 'Status', render: (row) => row.status ?? '—' },
      {
        key: 'cluster',
        header: 'Cluster',
        render: (row) => (row.clusterId ? clusterNames[row.clusterId] ?? row.clusterId : '—')
      },
      {
        key: 'lastSeen',
        header: 'Last seen',
        render: (row) => formatDateTime(row.lastSeenAt ?? row.updatedAt ?? null)
      }
    ],
    [clusterNames]
  );

  const clusterSiteColumns = useMemo<Array<ListWorkspaceColumn<SiteView>>>(
    () => [
      { key: 'name', header: 'Site', render: (row) => row.name ?? row.id ?? '—' },
      {
        key: 'location',
        header: 'Location / scope',
        render: getSiteLocationOrScope
      },
      {
        key: 'status',
        header: 'Health',
        render: (row) => deriveSiteHealth(row)
      }
    ],
    []
  );

  const clusterConnectorColumns = useMemo<Array<ListWorkspaceColumn<ConnectorView>>>(
    () => [
      { key: 'name', header: 'Connector', render: (row) => row.name ?? row.id ?? '—' },
      { key: 'status', header: 'Status', render: (row) => row.status ?? '—' },
      {
        key: 'lastSeen',
        header: 'Last seen',
        render: (row) => formatDateTime(row.lastSeenAt ?? row.updatedAt ?? null)
      }
    ],
    []
  );

  const availableClustersForSite = useMemo(() => {
    if (!siteDetail.record?.id) {
      return [];
    }
    return clustersState.items.filter(
      (cluster): cluster is ClusterView & { id: string } =>
        hasStableId(cluster) && !siteDetail.clusters.some((attached) => attached.id === cluster.id)
    );
  }, [clustersState.items, siteDetail.clusters, siteDetail.record?.id]);

  const availableSitesForCluster = useMemo(() => {
    if (!clusterDetail.record?.id) {
      return [];
    }
    return sitesState.items.filter(
      (site): site is SiteView & { id: string } => hasStableId(site) && !clusterDetail.sites.some((attached) => attached.id === site.id)
    );
  }, [clusterDetail.record?.id, clusterDetail.sites, sitesState.items]);

  useEffect(() => {
    if (availableClustersForSite.some((cluster) => cluster.id === siteClusterAttachId)) {
      return;
    }
    const next = availableClustersForSite[0]?.id ?? '';
    if (next !== siteClusterAttachId) {
      setSiteClusterAttachId(next);
    }
  }, [availableClustersForSite, siteClusterAttachId]);

  useEffect(() => {
    if (availableSitesForCluster.some((site) => site.id === clusterSiteAttachId)) {
      return;
    }
    const next = availableSitesForCluster[0]?.id ?? '';
    if (next !== clusterSiteAttachId) {
      setClusterSiteAttachId(next);
    }
  }, [availableSitesForCluster, clusterSiteAttachId]);

  const shouldLoadSiteDiscoveryData = Boolean(
    orgId &&
      siteDetail.record?.id &&
      (siteDetail.activeTab === 'discover' || runDetailOpen || runDrawerOpen || jobDrawerOpen)
  );

  const discoveryJobsFetcher = useMemo(
    () =>
      shouldLoadSiteDiscoveryData && orgId
        ? () => discoveryApi.orgsOrgIdDiscoveryJobsGet({ orgId, limit: siteJobsPageSize, after: `offset:${(siteJobsPage - 1) * siteJobsPageSize}`, siteId: siteDetail.record?.id }, { cache: 'no-store' })
        : null,
    [discoveryApi, orgId, shouldLoadSiteDiscoveryData, siteDiscoveryJobsVersion, siteJobsPage, siteJobsPageSize, siteDetail.record?.id]
  );
  const discoveryJobsQuery = useApiQuery<DiscoveryJobsListResponse>(discoveryJobsFetcher, [discoveryJobsFetcher]);
  const siteDiscoveryJobs = useMemo(() => {
    const siteId = siteDetail.record?.id ?? '';
    if (!siteId) {
      return [];
    }
    const filtered = (discoveryJobsQuery.data?.items ?? []).filter(
      (job) => job.siteId === siteId && (!job.id || !locallyDeletedJobIds.has(job.id))
    );
    return filtered;
  }, [discoveryJobsQuery.data?.items, locallyDeletedJobIds, siteDetail.record?.id]);
  const [selectedSiteJobId, setSelectedSiteJobId] = useState<string | null>(null);
  const selectedSiteJob = useMemo<DiscoveryJobschema | null>(
    () => siteDiscoveryJobs.find((job) => job.id === selectedSiteJobId) ?? null,
    [siteDiscoveryJobs, selectedSiteJobId]
  );

  const selectDiscoveryJob = useCallback(
    (jobId: string | null, options?: { resetRun?: boolean }) => {
      const next = selectDiscoveryJobState(
        {
          selectedSiteJobId,
          selectedRunId,
          pendingRun,
          runDetailOpen
        },
        jobId,
        options?.resetRun ?? true
      );
      setSelectedSiteJobId(next.selectedSiteJobId);
      setSelectedRunId(next.selectedRunId);
      setPendingRun(next.pendingRun);
      setRunDetailOpen(next.runDetailOpen);
    },
    [pendingRun, runDetailOpen, selectedRunId, selectedSiteJobId]
  );

  useEffect(() => {
    if (siteDiscoveryJobs.length === 0) {
      if (!discoveryJobsQuery.loading) {
        selectDiscoveryJob(null);
      }
      return;
    }
    if (!selectedSiteJobId) {
      selectDiscoveryJob(siteDiscoveryJobs[0]?.id ?? null, { resetRun: false });
      return;
    }
    if (!siteDiscoveryJobs.some((job) => job.id === selectedSiteJobId)) {
      selectDiscoveryJob(siteDiscoveryJobs[0]?.id ?? null);
    }
  }, [discoveryJobsQuery.loading, selectDiscoveryJob, selectedSiteJobId, siteDiscoveryJobs]);

  useEffect(() => {
    setSiteJobsPage(1);
  }, [siteDetail.record?.id]);

  useEffect(() => {
    const currentSiteId = siteDetail.record?.id ?? null;
    if (previousDiscoverySiteIdRef.current === currentSiteId) {
      return;
    }
    previousDiscoverySiteIdRef.current = currentSiteId;
    setSelectedRunId(null);
    setPendingRun(null);
    setRunDetailOpen(false);
  }, [siteDetail.record?.id]);

  const discoveryRunsFetcher = useMemo(
    () => {
      const selectedJobId = selectedSiteJob?.id ?? null;
      if (!shouldLoadSiteDiscoveryData || !orgId || !selectedJobId) {
        return null;
      }
      return () =>
        discoveryApi.orgsOrgIdDiscoveryJobsJobIdRunsGet(
          { orgId, jobId: selectedJobId, limit: siteRunsPageSize, after: `offset:${(siteRunsPage - 1) * siteRunsPageSize}` },
          { cache: 'no-store' }
        );
    },
    [discoveryApi, orgId, selectedSiteJob?.id, shouldLoadSiteDiscoveryData, siteDiscoveryRunsVersion, siteRunsPage, siteRunsPageSize]
  );
  const discoveryRunsQuery = useApiQuery<DiscoveryRunsListResponse>(discoveryRunsFetcher, [discoveryRunsFetcher]);
  const allSiteDiscoveryRuns = useMemo(() => {
    const runs = discoveryRunsQuery.data?.items ?? [];
    return selectDiscoveryRunsForJob(selectedSiteJob?.id, runs, pendingRun);
  }, [discoveryRunsQuery.data?.items, pendingRun, selectedSiteJob?.id]);
  const siteDiscoveryRuns = allSiteDiscoveryRuns;

  useEffect(() => {
    setSiteRunsPage(1);
  }, [selectedSiteJob?.id]);
  const selectedSiteRun = useMemo<DiscoveryRun | null>(() => {
    if (selectedRunId) {
      const match = allSiteDiscoveryRuns.find((run) => run.id === selectedRunId);
      if (match) {
        return match;
      }
    }
    return allSiteDiscoveryRuns[0] ?? null;
  }, [allSiteDiscoveryRuns, selectedRunId]);

  const isRunLive = useCallback((status?: string | null) => status === 'running' || status === 'queued', []);

  useEffect(() => {
    const runId = selectedSiteRun?.id ?? null;
    const runStatus = selectedSiteRun?.status ?? null;
    if (!runId) {
      selectedRunLiveStateRef.current = null;
      return;
    }

    const live = isRunLive(runStatus);
    const previous = selectedRunLiveStateRef.current;
    selectedRunLiveStateRef.current = { id: runId, live };

    if (previous?.id === runId && previous.live && !live) {
      scheduleOpenSiteDataRefresh();
    }
  }, [isRunLive, scheduleOpenSiteDataRefresh, selectedSiteRun?.id, selectedSiteRun?.status]);

  useEffect(() => {
    const runId = selectedSiteRun?.id ?? null;
    const runStatus = selectedSiteRun?.status ?? null;
    const siteId = siteDetail.record?.id ?? null;
    if (!runId || !isRunLive(runStatus) || !siteId) {
      return;
    }

    scheduleOpenSiteDataRefresh();
    const interval = window.setInterval(() => {
      setSiteDiscoveryRunsVersion((prev) => prev + 1);
      scheduleOpenSiteDataRefresh();
    }, DISCOVERY_ACTIVE_RUN_POLL_MS);

    return () => window.clearInterval(interval);
  }, [isRunLive, scheduleOpenSiteDataRefresh, selectedSiteRun?.id, selectedSiteRun?.status, siteDetail.record?.id]);

  const siteNames = useMemo(() => {
    const lookup: Record<string, string> = {};
    sitesState.items.forEach((site) => {
      if (site.id) {
        lookup[site.id] = site.name ?? site.id;
      }
    });
    return lookup;
  }, [sitesState.items]);

  const openCreateJobDrawer = useCallback(() => {
    setJobDrawerMode('create');
    setJobDrawerJobId(null);
    setJobActionError(null);
    setJobDrawerOpen(true);
  }, []);

  const openEditJobDrawer = useCallback((jobId: string) => {
    setJobDrawerMode('edit');
    setJobDrawerJobId(jobId);
    setJobActionError(null);
    setJobDrawerOpen(true);
  }, []);

  const closeJobDrawer = useCallback(() => {
    setJobDrawerOpen(false);
    setJobDrawerMode('create');
    setJobDrawerJobId(null);
  }, []);

  const handleJobCreated = useCallback(
    (job: DiscoveryJob) => {
      closeJobDrawer();
      setJobActionError(null);
      setSiteDiscoveryJobsVersion((prev) => prev + 1);
      selectDiscoveryJob(job.id ?? job.siteId ?? null);
    },
    [closeJobDrawer, selectDiscoveryJob]
  );

  const handleJobUpdated = useCallback(
    (job: DiscoveryJob) => {
      closeJobDrawer();
      setJobActionError(null);
      setSiteDiscoveryJobsVersion((prev) => prev + 1);
      selectDiscoveryJob(job.id ?? selectedSiteJobId);
    },
    [closeJobDrawer, selectDiscoveryJob, selectedSiteJobId]
  );

  const handleDeleteJobs = useCallback(
    (rows: DiscoveryJob[]) => {
      if (!orgId || jobActionBusyId) {
        return;
      }
      const jobs = rows.filter((row): row is DiscoveryJob & { id: string } => Boolean(row.id));
      if (jobs.length === 0) {
        return;
      }
      setPendingDeleteJobs(jobs);
    },
    [jobActionBusyId, orgId]
  );

  const confirmDeleteJobs = useCallback(
    async () => {
      const jobs = pendingDeleteJobs?.filter((row): row is DiscoveryJob & { id: string } => Boolean(row.id)) ?? [];
      if (!orgId || jobs.length === 0 || jobActionBusyId) {
        return;
      }

      setJobActionBusyId('bulk-delete');
      setJobActionError(null);
      const deletedIds = new Set<string>();
      let failureMessage: string | null = null;

      try {
        for (const job of jobs) {
          try {
            const response = await discoveryApi.orgsOrgIdDiscoveryJobsJobIdGetRaw({ orgId, jobId: job.id }, { cache: 'no-store' });
            const etag = response.raw.headers.get('ETag');
            if (!etag) {
              throw new Error('Failed to resolve job version. Refresh and try again.');
            }
            await discoveryApi.orgsOrgIdDiscoveryJobsJobIdDeleteRaw({ orgId, jobId: job.id, ifMatch: etag });
            deletedIds.add(job.id);
          } catch (caught) {
            failureMessage = caught instanceof Error ? caught.message : 'Failed to delete discovery job.';
          }
        }

        if (deletedIds.size > 0) {
          setLocallyDeletedJobIds((current) => new Set([...current, ...deletedIds]));
          setPendingDeleteJobs(null);
          setSiteDiscoveryJobsVersion((prev) => prev + 1);
          if (selectedSiteJobId && deletedIds.has(selectedSiteJobId)) {
            selectDiscoveryJob(null);
          }
        }

        if (failureMessage) {
          const message = deletedIds.size > 0
            ? `${deletedIds.size} of ${jobs.length} discovery jobs deleted. ${jobs.length - deletedIds.size} could not be deleted.`
            : failureMessage;
          setJobActionError(message);
          setDeleteToast({ tone: deletedIds.size > 0 ? 'warning' : 'error', message });
        } else {
          setDeleteToast({
            tone: 'success',
            message: `${deletedIds.size} discovery job${deletedIds.size === 1 ? '' : 's'} deleted successfully.`
          });
        }
      } finally {
        setJobActionBusyId(null);
      }
    },
    [discoveryApi, jobActionBusyId, orgId, pendingDeleteJobs, selectDiscoveryJob, selectedSiteJobId]
  );

  const handleRefreshJobs = useCallback(() => {
    setSiteDiscoveryJobsVersion((prev) => prev + 1);
    setSiteDiscoveryRunsVersion((prev) => prev + 1);
  }, []);

  const handleStartRun = useCallback(() => {
    setRunDrawerOpen(true);
  }, []);

  const handleRefreshRuns = useCallback(() => {
    setSiteDiscoveryRunsVersion((prev) => prev + 1);
  }, []);

  const handleStopRun = useCallback(
    async (run: DiscoveryRun | null) => {
      if (!orgId || !run?.id || !run.jobId || !isRunLive(run.status)) {
        return;
      }
      const confirmed = window.confirm('Stop this discovery run? You can start another run once the stop request completes.');
      if (!confirmed) {
        return;
      }

      setRunActionBusyId(run.id);
      setRunActionError(null);
      try {
        const resource = `discovery/jobs/${encodeURIComponent(run.jobId)}/runs/${encodeURIComponent(run.id)}:cancel`;
        const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, resource), {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          }
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }

        setPendingRun((prev) => {
          if (!prev || prev.id !== run.id) {
            return prev;
          }
          const next: DiscoveryRun = {
            ...prev,
            status: 'cancelled' as unknown as DiscoveryRun['status'],
            finishedAt: new Date()
          };
          return next;
        });
        setSiteDiscoveryRunsVersion((prev) => prev + 1);
        setSiteDiscoveryJobsVersion((prev) => prev + 1);
      } catch (error) {
        let message = 'Failed to stop discovery run.';
        if (error instanceof ApiResponseError) {
          try {
            const payload = await error.response.clone().json();
            message =
              (payload as { title?: string; detail?: string; message?: string }).title ??
              (payload as { title?: string; detail?: string; message?: string }).detail ??
              (payload as { title?: string; detail?: string; message?: string }).message ??
              message;
          } catch {
            message = `Request failed with status ${error.response.status}`;
          }
        } else if (error instanceof Error) {
          message = error.message;
        }
        setRunActionError(message);
      } finally {
        setRunActionBusyId(null);
      }
    },
    [basePath, fetchWithAuthRetry, isRunLive, orgId]
  );

  const handleOpenRunDetail = useCallback((run: DiscoveryRun | null) => {
    if (run?.id) {
      setSelectedRunId(run.id);
      setPendingRun(run);
    }
    setRunDetailOpen(true);
  }, []);

  const handleCloseRunDetail = useCallback(() => {
    setRunDetailOpen(false);
  }, []);

  const handleRunStarted = useCallback(
    (run: DiscoveryRun) => {
      setRunDrawerOpen(false);
      setSiteDiscoveryRunsVersion((prev) => prev + 1);
      selectDiscoveryJob(run.jobId ?? selectedSiteJobId, { resetRun: false });
      handleOpenRunDetail(run);
    },
    [handleOpenRunDetail, selectDiscoveryJob, selectedSiteJobId]
  );

  const siteJobColumns = useMemo<Array<ListWorkspaceColumn<DiscoveryJobschema>>>(
    () => [
      {
        key: 'schedule',
        header: 'Schedule',
        render: (job) => describeDiscoverySchedule(job.schedule)
      },
      {
        key: 'scope',
        header: 'Scope',
        render: (job) => formatDiscoveryTriggerScope(job.schedule?.triggerScope) || (job.seeds ?? []).join(', ') || 'Seedless'
      },
      {
        key: 'status',
        header: 'Status',
        render: (job) => <span className={`ui-badge ui-badge--${job.status === 'active' ? 'success' : 'ghost'}`}>{job.status}</span>
      },
      {
        key: 'updated',
        header: 'Updated',
        render: (job) => formatDateTime(job.updatedAt ?? job.createdAt)
      }
    ],
    []
  );

  const siteRunColumns = useMemo<Array<ListWorkspaceColumn<DiscoveryRun>>>(
    () => [
      {
        key: 'status',
        header: 'Status',
        render: (run) => (
          <div className="discovery-cell">
            <span
              className={`ui-badge ui-badge--${
                run.status === 'succeeded' ? 'success' : run.status === 'failed' ? 'danger' : isRunLive(run.status) ? 'warning' : 'ghost'
              }`}
            >
              {run.status}
            </span>
            {isRunLive(run.status) && <span className="discovery-live-pill">LIVE</span>}
          </div>
        )
      },
      {
        key: 'started',
        header: 'Started',
        render: (run) => formatDateTime(run.startedAt ?? run.queuedAt)
      },
      {
        key: 'duration',
        header: 'Duration',
        render: (run) => formatDuration(run.startedAt ?? run.queuedAt, run.finishedAt ?? null)
      },
      {
        key: 'devices',
        header: 'Devices',
        render: (run) => formatNumber(run.summary?.devicesDiscovered ?? 0)
      },
      {
        key: 'links',
        header: 'Links',
        render: (run) => formatNumber(run.summary?.linksDiscovered ?? 0)
      }
    ],
    [isRunLive]
  );

  const buildPaginationMeta = useCallback((count: number, page: number, pageSize: number) => {
    const safePageSize = Math.max(1, pageSize);
    const pageCount = Math.max(1, Math.ceil(count / safePageSize));
    return {
      totalCount: count,
      exactTotal: count,
      pageCount,
      hasNextPage: page < pageCount,
      hasPrevPage: page > 1
    };
  }, []);

  const pagedSiteDiscoveryJobs = siteDiscoveryJobs;

  const pagedSiteDiscoveryRuns = siteDiscoveryRuns;

  const siteInventoryRows = useMemo(() => Object.values(topologyDevicesById), [topologyDevicesById]);
  const siteSelectedCMDBConnection = useMemo(
    () => siteCMDBState.connections.find((item) => item.id === selectedSiteCMDBConnectionId) ?? siteCMDBState.connections.find((item) => Boolean(item.id)) ?? null,
    [selectedSiteCMDBConnectionId, siteCMDBState.connections]
  );
  const siteCMDBItems = useMemo(() => {
    const activeSiteId = siteDetail.record?.id;
    if (!activeSiteId || !siteCMDBState.report?.items) {
      return [];
    }
    return siteCMDBState.report.items.filter((item) => item.siteId === activeSiteId);
  }, [siteCMDBState.report?.items, siteDetail.record?.id]);
  const siteCMDBRelationItems = useMemo(
    () => siteCMDBItems.filter((item) => item.domain === 'relation' || item.domain === 'topology' || item.domain === 'service' || item.domain === 'endpoint'),
    [siteCMDBItems]
  );
  const siteCMDBCountsByStatus = useMemo(
    () =>
      siteCMDBItems.reduce<Record<string, number>>((acc, item) => {
        acc[item.status] = (acc[item.status] ?? 0) + 1;
        return acc;
      }, {}),
    [siteCMDBItems]
  );
  const siteCMDBCountsByDomain = useMemo(
    () =>
      siteCMDBItems.reduce<Record<string, number>>((acc, item) => {
        acc[item.domain] = (acc[item.domain] ?? 0) + 1;
        return acc;
      }, {}),
    [siteCMDBItems]
  );
  const siteDeviceTypeSummaries = useMemo(
    () => (siteInventoryRows.length > 0 ? summarizeSiteDeviceTypes(siteInventoryRows) : siteDetail.record?.deviceTypeCounts ?? []),
    [siteDetail.record?.deviceTypeCounts, siteInventoryRows]
  );
  const siteVendorSummaries = useMemo(
    () => (siteInventoryRows.length > 0 ? summarizeSiteVendors(siteInventoryRows) : siteDetail.record?.vendorCounts ?? []),
    [siteDetail.record?.vendorCounts, siteInventoryRows]
  );
  const renderVendorSummary = (summaries: SiteVendorSummary[]) => {
    if (summaries.length === 0) {
      return null;
    }
    return (
      <div className="device-vendor-summary" aria-label="Devices by vendor">
        <span className="device-vendor-summary__label">Vendors</span>
        <div className="device-vendor-summary__chips">
          {summaries.map((item) => (
            <span key={item.key} className="device-vendor-chip">
              {item.label} <strong>{formatNumber(item.count)}</strong>
            </span>
          ))}
        </div>
      </div>
    );
  };
  const renderConnectivitySummaryCards = (cards: ConnectivitySummaryCard[]) => {
    if (cards.length === 0) {
      return null;
    }
    return (
      <section className="connectivity-summary-cards" aria-label="Connectivity summary">
        {cards.map((card) => (
          <article
            key={card.key}
            className={['connectivity-summary-card', card.tone ? `connectivity-summary-card--${card.tone}` : '']
              .filter(Boolean)
              .join(' ')}
          >
            <div className="connectivity-summary-card__icon">{renderSiteDeviceTypeIcon(card.iconKind, 18)}</div>
            <div className="connectivity-summary-card__body">
              <span>{card.label}</span>
              <strong>{formatNumber(card.value)}</strong>
              <p title={card.detail}>{card.detail}</p>
            </div>
          </article>
        ))}
      </section>
    );
  };
  const renderSiteDeviceTypeCards = (
    summaries: SiteDeviceTypeSummary[],
    options?: { compact?: boolean; dense?: boolean; vendors?: SiteVendorSummary[] }
  ) => {
    if (summaries.length === 0) {
      return null;
    }
    const total = summaries.reduce((sum, item) => sum + item.count, 0);
    return (
      <section
        className={[
          'site-device-types',
          options?.compact ? 'site-device-types--compact' : '',
          options?.dense ? 'site-device-types--dense' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <header className="site-device-types__header">
          <h3>Devices by type</h3>
          <span>{formatNumber(total)} devices</span>
        </header>
        <div className="site-device-types__grid">
          {summaries.map((item) => (
            <div key={item.key} className="site-device-type-card">
              <div className="site-device-type-card__icon">
                {renderSiteDeviceTypeIcon(item.iconKind, options?.compact || options?.dense ? 18 : 22)}
              </div>
              <div className="site-device-type-card__body">
                <p title={item.label}>{item.label}</p>
                {item.detail && <span>{item.detail}</span>}
              </div>
              <strong>{formatNumber(item.count)}</strong>
            </div>
          ))}
        </div>
        {renderVendorSummary(options?.vendors ?? [])}
      </section>
    );
  };
  const siteInventoryColumns = useMemo<Array<ListWorkspaceColumn<Device>>>(
    () => [
      {
        key: 'name',
        header: 'Device',
        width: '11%',
        render: (row) => row.displayName ?? row.hostname ?? row.mgmtAddress ?? row.id ?? '—'
      },
      {
        key: 'mgmt',
        header: 'Mgmt address',
        width: '10%',
        render: (row) => row.mgmtAddress ?? '—'
      },
      {
        key: 'role',
        header: 'Role',
        width: '9%',
        render: (row) => formatDeviceRoleLabel(row.role) || '—'
      },
      {
        key: 'vendor',
        header: 'Vendor',
        width: '8%',
        render: (row) => row.vendor ?? '—'
      },
      {
        key: 'model',
        header: 'Model',
        width: '9%',
        render: (row) => sanitizeDeviceModel(row.model, deviceToTypePayload(row)) ?? '—'
      },
      {
        key: 'os',
        header: 'OS',
        width: '14%',
        render: (row) => formatDeviceOS(getDeviceOsName(row), row.osVersion) || '—'
      },
      {
        key: 'serial',
        header: 'Serial number',
        width: '20%',
        render: (row) => row.serial ?? '—'
      },
      {
        key: 'state',
        header: 'State',
        width: '7%',
        render: (row) => row.state ?? '—'
      },
      {
        key: 'lastSeen',
        header: 'Last seen',
        width: '12%',
        render: (row) => formatDateTime(row.lastSeenAt ?? null)
      }
    ],
    []
  );

  const siteDetailTabs = useMemo(
    () => [
      { id: 'details', label: 'Details' },
      { id: 'cockpit', label: 'Cockpit' },
      { id: 'topology', label: 'Topology' },
      { id: 'discover', label: 'Discover' },
      { id: 'inventory', label: 'Inventory' },
      {
        id: 'clusters',
        label: 'Clusters'
      },
      {
        id: 'connectors',
        label: 'Connectors'
      },
      {
        id: 'ping-mesh',
        label: 'Ping Mesh'
      }
    ],
    []
  );

  const renderTopologyTooltip = () => {
    if (edgeDetail && edgeDetailPosition) {
      const firstConnection = edgeDetail.connections?.[0];
      const fromHeading = firstConnection?.fromDeviceLabel ?? 'Device A';
      const toHeading = firstConnection?.toDeviceLabel ?? 'Device B';
      return (
        <div
          className="topology-tooltip topology-tooltip--floating"
          style={{ left: `${edgeDetailPosition.x}px`, top: `${edgeDetailPosition.y}px` }}
          onClick={(event) => event.stopPropagation()}
        >
          <table className="topology-tooltip__matrix">
            <thead>
              <tr>
                <th className="topology-tooltip__matrix-heading">{fromHeading}</th>
                <th className="topology-tooltip__matrix-heading">{toHeading}</th>
              </tr>
            </thead>
            <tbody>
              {(edgeDetail.connections ?? []).map((connection, index) => (
                <tr key={`${edgeDetail.id}-conn-${index}`}>
                  <td>{connection.fromLabel}</td>
                  <td>{connection.toLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (deviceDetail && deviceDetailPosition) {
      const osLabel = formatDeviceOS(deviceDetail.osName, deviceDetail.osVersion);
      return (
        <div
          className="topology-tooltip topology-tooltip--floating"
          style={{ left: `${deviceDetailPosition.x}px`, top: `${deviceDetailPosition.y}px` }}
          onClick={(event) => event.stopPropagation()}
        >
          <strong>{deviceDetail.label}</strong>
          <ul>
            {deviceDetail.vendor && <li>Vendor: {deviceDetail.vendor}</li>}
            {deviceDetail.model && <li>Model: {deviceDetail.model}</li>}
            {deviceDetail.serial && <li>Serial: {deviceDetail.serial}</li>}
            {osLabel && <li>OS: {osLabel}</li>}
            {deviceDetail.role && <li>Role: {deviceDetail.role}</li>}
            {deviceDetail.mgmtAddress && <li>Mgmt IP: {deviceDetail.mgmtAddress}</li>}
          </ul>
        </div>
      );
    }
    return null;
  };

  const renderTopologyCanvas = (mode: 'embedded' | 'windowed') => (
    <div
      className={['topology-diagram', mode === 'windowed' ? 'topology-diagram--windowed' : ''].filter(Boolean).join(' ')}
      ref={mode === 'windowed' ? topologyWindowDiagramRef : topologyDiagramRef}
    >
      <TopologyDiagram
        elements={cytoscapeElements}
        layoutKey={`${topologyDiagramKey}-${mode}`}
        mode={mode}
        manualPositions={manualNodePositions}
        onNodePositionChange={handleNodePositionPersist}
        onEdgeFocus={handleEdgeFocus}
        onDeviceFocus={handleDeviceFocus}
      />
      {((mode === 'windowed') === topologyFullscreenOpen) && renderTopologyTooltip()}
    </div>
  );

  useEffect(() => {
    if (!orgId || !siteDrawer?.siteId) return;
    void inventoryApi.getSiteInventorySourcePolicy({ orgId, siteId: siteDrawer.siteId }, { cache: 'no-store' })
      .then((policy) => setInventoryPolicy({ mode: policy.mode, connectionId: policy.connectionId ?? '' }))
      .catch(() => setInventoryPolicy({ mode: 'inherit', connectionId: '' }));
  }, [inventoryApi, orgId, siteDrawer?.siteId]);

  useEffect(() => {
    if (!orgId || !siteDrawer?.siteId) {
      setNetBoxConnections([]);
      return;
    }
    let cancelled = false;
    setNetBoxConnectionsLoading(true);
    void integrationsApi.listIntegrationConnections({ orgId, provider: 'netbox', limit: 200 }, { cache: 'no-store' })
      .then((response) => {
        if (!cancelled) setNetBoxConnections(response.items ?? []);
      })
      .catch((error) => {
        if (!cancelled) {
          setNetBoxConnections([]);
          setDeleteToast({ tone: 'error', message: `Could not load NetBox connections: ${String(error)}` });
        }
      })
      .finally(() => {
        if (!cancelled) setNetBoxConnectionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [integrationsApi, orgId, siteDrawer?.siteId]);

  useEffect(() => {
    if (inventoryPolicy.mode !== 'netbox_authoritative' || inventoryPolicy.connectionId || netBoxConnections.length !== 1) return;
    setInventoryPolicy((policy) => policy.mode === 'netbox_authoritative' && !policy.connectionId
      ? { ...policy, connectionId: netBoxConnections[0].id }
      : policy);
  }, [inventoryPolicy.connectionId, inventoryPolicy.mode, netBoxConnections]);

	useEffect(() => {
		if (!orgId || !inventoryPolicy.connectionId) { setNetBoxSites([]); return; }
		let cancelled = false;
		setNetBoxSitesLoading(true);
	void inventoryApi.listNetBoxInventorySourceSites({ orgId, connectionId: inventoryPolicy.connectionId }, { cache: 'no-store' })
			.then((response) => { if (!cancelled) setNetBoxSites(response.items ?? []); })
			.catch(() => { if (!cancelled) { setNetBoxSites([]); setDeleteToast({ tone: 'error', message: 'Could not load NetBox sites.' }); } })
			.finally(() => { if (!cancelled) setNetBoxSitesLoading(false); });
		return () => { cancelled = true; };
	}, [inventoryApi, inventoryPolicy.connectionId, orgId]);

  useEffect(() => {
    if (!orgId || !siteDrawer?.siteId || !siteDrawer.open) { setInventoryImportRuns([]); return; }
    let cancelled = false;
    const load = () => fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `sites/${encodeURIComponent(siteDrawer.siteId)}/inventory-imports`))
      .then(async response => { if (!response.ok) throw new Error('Import history unavailable'); return response.json() as Promise<{ items?: typeof inventoryImportRuns }>; })
      .then(data => { if (!cancelled) setInventoryImportRuns(data.items ?? []); })
      .catch(() => { if (!cancelled) setInventoryImportRuns([]); });
    void load();
    return () => { cancelled = true; };
  }, [basePath, fetchWithAuthRetry, inventoryImportRunsRefresh, orgId, siteDrawer?.open, siteDrawer?.siteId]);

  const queueSiteNetBoxImport = async () => {
    if (!orgId || !siteDrawer?.siteId) return;
    if (inventoryPolicy.mode !== 'netbox_authoritative' || !inventoryPolicy.connectionId) {
      setDeleteToast({ tone: 'error', message: 'Set this site to NetBox authoritative and save the inventory source before syncing.' });
      return;
    }
	const netBoxSiteID = inventoryImportRuns[0]?.netboxSiteId;
	if (!netBoxSiteID) { setDeleteToast({ tone: 'error', message: 'This site has no persisted NetBox site binding. Import it from the Sites page first.' }); return; }
    setInventoryImportBusy(true);
    try {
      const run = await inventoryApi.enqueueNetBoxInventoryImport({
        orgId,
        connectionId: inventoryPolicy.connectionId,
        inventoryImportRequest: { siteId: siteDrawer.siteId, netboxSiteId: netBoxSiteID }
      }, { cache: 'no-store' });
      setDeleteToast({ tone: 'success', message: `NetBox import queued (${run.status}).` });
      setInventoryImportRunsRefresh(value => value + 1);
      await loadSiteDeviceInventory(siteDrawer.siteId);
    } catch (error) {
      setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) });
    } finally {
      setInventoryImportBusy(false);
    }
  };

  useEffect(() => {
    if (!importDrawerOpen || !orgId) return;
    void inventoryApi.getInventorySourceCatalog({ orgId }, { cache: 'no-store' }).then((catalog) => {
      const items = catalog.items as InventorySourceCatalogItem[];
      setInventorySourceCatalog(items);
      setImportSourceMode((current) => items.some((source) => source.key === current) ? current : (items[0]?.key ?? ''));
    }).catch(async (error) => setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) }));
  }, [importDrawerOpen, inventoryApi, orgId]);

  useEffect(() => {
    if (!importDrawerOpen || !orgId || activeImportWorkflow !== 'provider_site_import' || !activeImportSource?.provider) return;
    let cancelled = false;
    void integrationsApi.listIntegrationConnections({ orgId, provider: activeImportSource.provider, limit: 200 }, { cache: 'no-store' })
      .then((response) => {
        if (cancelled) return;
        const connections = response.items ?? [];
        setNetBoxConnections(connections);
        setImportConnectionID((current) => current || (connections.length === 1 ? connections[0].id : ''));
      })
      .catch((error) => {
        if (!cancelled) setDeleteToast({ tone: 'error', message: `Could not load NetBox connections: ${String(error)}` });
      });
    return () => { cancelled = true; };
  }, [activeImportSource?.provider, activeImportWorkflow, importDrawerOpen, integrationsApi, orgId]);

  useEffect(() => {
    if (!importDrawerOpen || activeImportWorkflow !== 'provider_site_import' || !orgId || !importConnectionID) {
      setImportNetBoxSites([]);
      setImportSelectedNetBoxSiteIDs([]);
      return;
    }
    let cancelled = false;
    setImportSitesLoading(true);
    void inventoryApi.listNetBoxInventorySourceSites({ orgId, connectionId: importConnectionID }, { cache: 'no-store' })
      .then((response) => {
        if (!cancelled) {
          setImportNetBoxSites(response.items ?? []);
          setImportSelectedNetBoxSiteIDs([]);
        }
      })
      .catch(() => {
        if (!cancelled) setDeleteToast({ tone: 'error', message: 'Could not load NetBox sites.' });
      })
      .finally(() => { if (!cancelled) setImportSitesLoading(false); });
    return () => { cancelled = true; };
	}, [activeImportWorkflow, importConnectionID, importDrawerOpen, inventoryApi, orgId]);

  useEffect(() => {
    const pending = bulkImportRows.filter((row) => row.reapSiteId && row.run && (row.run.status === 'queued' || row.run.status === 'running'));
    if (!importDrawerOpen || !orgId || pending.length === 0) return;
    const refreshRuns = async () => {
      const updates = await Promise.all(pending.map(async (row) => {
        const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `sites/${encodeURIComponent(row.reapSiteId!)}/inventory-imports`));
        if (!response.ok) return row;
        const body = await response.json() as { items?: Array<{ id: string; status: string; logs?: string[] }> };
        const run = body.items?.find((item) => item.id === row.run?.id);
        return run ? { ...row, run, logs: run.logs } : row;
      }));
      setBulkImportRows((rows) => rows.map((row) => updates.find((update) => update.netBoxSite.id === row.netBoxSite.id) ?? row));
    };
    void refreshRuns();
    const interval = window.setInterval(() => { void refreshRuns(); }, 2500);
    return () => window.clearInterval(interval);
  }, [basePath, bulkImportRows, fetchWithAuthRetry, importDrawerOpen, orgId]);

  const queueBulkNetBoxImports = async () => {
    if (!orgId || !importConnectionID || importSelectedNetBoxSiteIDs.length === 0) return;
    const selected = importNetBoxSites.filter((site) => importSelectedNetBoxSiteIDs.includes(site.id));
    setBulkImportBusy(true);
    setBulkImportRows(selected.map((netBoxSite) => ({ netBoxSite })));
    try {
      await Promise.all(selected.map(async (netBoxSite) => {
        try {
          const reapSite = await sitesApi.orgsOrgIdSitesPost({
            orgId,
            siteCreate: { name: netBoxSite.name, siteType: SiteSiteTypeEnum.OnPrem }
          });
          if (!reapSite.id) throw new Error('The REAP site was created without an ID.');
          setBulkImportRows((rows) => rows.map((row) => row.netBoxSite.id === netBoxSite.id ? { ...row, reapSiteId: reapSite.id } : row));
          await inventoryApi.updateSiteInventorySourcePolicy({
            orgId,
            siteId: reapSite.id,
            inventorySourcePolicy: { mode: importSourceMode as never, connectionId: importConnectionID }
          });
          const run = await inventoryApi.enqueueNetBoxInventoryImport({
            orgId,
            connectionId: importConnectionID,
            inventoryImportRequest: { siteId: reapSite.id, netboxSiteId: netBoxSite.id }
          }, { cache: 'no-store' });
          setBulkImportRows((rows) => rows.map((row) => row.netBoxSite.id === netBoxSite.id ? { ...row, run } : row));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Import could not be queued.';
          setBulkImportRows((rows) => rows.map((row) => row.netBoxSite.id === netBoxSite.id ? { ...row, error: message } : row));
        }
      }));
      await refreshSitesAll({ cache: 'no-store' });
    } finally {
      setBulkImportBusy(false);
    }
  };

  const loadCSVImportSchemas = useCallback(async () => {
    if (!orgId) return;
    setCSVSchemasLoading(true);
    try {
      const response = await inventoryApi.listCSVImportSchemas({ orgId }, { cache: 'no-store' });
      const schemas = response.items ?? [];
      setCSVSchemas(schemas);
      setCSVSchemaKey((current) => current && schemas.some((schema) => schema.key === current) ? current : (schemas[0]?.key ?? ''));
    } catch (error) {
      setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) });
    } finally {
      setCSVSchemasLoading(false);
    }
  }, [inventoryApi, orgId]);

  useEffect(() => {
    if (importDrawerOpen && activeImportWorkflow === 'incremental_file_import') {
      void loadCSVImportSchemas();
    }
  }, [activeImportWorkflow, importDrawerOpen, loadCSVImportSchemas]);

  const uploadCSVImport = async (file: File | null) => {
    if (!orgId || !file || !csvSchemaKey) return;
    setCSVImportBusy(true);
    try {
      const uploaded = await inventoryApi.uploadCSVImport({ orgId, schemaKey: csvSchemaKey, file }, { cache: 'no-store' });
      const runId = uploaded.runId;
      if (!runId) throw new Error('The CSV upload did not return an import ID.');
      const mapping = uploaded.mapping ?? {};
      const preview = await inventoryApi.previewCSVImport({ orgId, runId, cSVInventoryMappingRequest: { mapping } }, { cache: 'no-store' });
      setCSVImportDraft({ runId, fileName: file.name, entityType: selectedCSVSchema?.entityType ?? 'sites', headers: preview.headers ?? uploaded.headers ?? [], mapping: preview.mapping ?? mapping, rows: preview.rows ?? [], errors: preview.errors ?? [] });
    } catch (error) {
      setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) });
    } finally {
      setCSVImportBusy(false);
    }
  };

  const previewCSVImport = async (mapping: Record<string, string>) => {
    if (!orgId || !csvImportDraft) return;
    setCSVImportBusy(true);
    try {
      const preview = await inventoryApi.previewCSVImport({ orgId, runId: csvImportDraft.runId, cSVInventoryMappingRequest: { mapping } }, { cache: 'no-store' });
      setCSVImportDraft((current) => current ? { ...current, headers: preview.headers ?? current.headers, mapping: preview.mapping ?? mapping, rows: preview.rows ?? [], errors: preview.errors ?? [] } : current);
    } catch (error) {
      setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) });
    } finally {
      setCSVImportBusy(false);
    }
  };

  const confirmCSVImport = async () => {
    if (!orgId || !csvImportDraft) return;
    setCSVImportBusy(true);
    try {
      const run = await inventoryApi.confirmCSVImport({ orgId, runId: csvImportDraft.runId, cSVInventoryMappingRequest: { mapping: csvImportDraft.mapping } }, { cache: 'no-store' });
      setCSVImportDraft((current) => current ? { ...current, run } : current);
      setDeleteToast({ tone: 'success', message: 'Import started.' });
    } catch (error) {
      setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) });
    } finally {
      setCSVImportBusy(false);
    }
  };

  const renderSiteDetailContent = () => {
    if (siteDetail.loading) {
      return <p>Loading site…</p>;
    }
    const siteError = siteDetail.error;
    if (siteError && siteDetail.activeTab !== 'details') {
      return <OrgModulePlaceholder intent={siteError} />;
    }
    if (!siteDetail.record) {
      return <OrgModulePlaceholder intent="Select a site to view detail." />;
    }

    switch (siteDetail.activeTab) {
      case 'cockpit': {
        const widgets = (siteCockpitQuery.data?.widgets ?? []).filter((w: DashboardWidgetSpec) => w.type === 'grafana_panel_iframe') as DashboardWidgetSpec[];
        return siteCockpitQuery.backendMissing ? (
          <OrgModulePlaceholder intent="Implement dashboards-service + api-server wiring to populate site cockpit recommendations." />
        ) : (
          <div className="org-cockpit__layout">
            <div className="org-cockpit__toolbar">
              <label>
                Time range
                <select value={siteCockpitRangePreset} onChange={(e) => setSiteCockpitRangePreset(e.target.value as TimeRangePreset)}>
                  <option value="15m">Last 15m</option>
                  <option value="1h">Last 1h</option>
                  <option value="6h">Last 6h</option>
                  <option value="24h">Last 24h</option>
                </select>
              </label>
              <label>
                Refresh
                <select
                  value={siteCockpitRefreshSeconds}
                  onChange={(e) => setSiteCockpitRefreshSeconds(parseInt(e.target.value, 10) || 0)}
                >
                  {SITE_COCKPIT_REFRESH_OPTIONS_SECONDS.map((seconds) => (
                    <option key={seconds} value={seconds}>
                      {seconds === 0 ? 'Off' : `${seconds}s`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {renderSiteDeviceTypeCards(siteDeviceTypeSummaries, { dense: true, vendors: siteVendorSummaries })}

            {siteCockpitQuery.error && <p className="org-shell__banner">{siteCockpitQuery.error.message}</p>}
            {siteCockpitQuery.loading && <p className="org-shell__banner">Loading cockpit…</p>}
            {!siteCockpitQuery.loading && widgets.length === 0 && (
              <OrgModulePlaceholder intent="No cockpit widgets are recommended for this site yet." />
            )}

            {siteDetail.record.id ? (
              <SignalsSummary scopeType="site" scopeId={siteDetail.record.id} refreshSeconds={siteCockpitRefreshSeconds} />
            ) : null}

            <div className="org-cockpit__grid">
	              {widgets.map((widget) => (
	                <GrafanaIframeCard
	                  key={widget.id}
	                  widget={widget}
	                  height={260}
	                  refreshSeconds={siteCockpitRefreshSeconds}
	                  timeRangePreset={siteCockpitRangePreset}
	                  onAuthRefresh={refreshGrafanaEmbedSession}
	                />
	              ))}
	            </div>
          </div>
        );
      }
      case 'details': {
        const siteType = getSiteTypeForForm(siteDetail.record);
        const siteFormValues = siteToFormValues(siteDetail.record);
        const location =
          siteType === SiteSiteTypeEnum.OnPrem
            ? [siteDetail.record.address?.line1, siteDetail.record.address?.city, siteDetail.record.address?.country]
                .filter(Boolean)
                .join(', ') || '—'
            : getSiteLocationOrScope(siteDetail.record);
        const connectorCount = siteDetail.record.connectorCount ?? '—';
        const health = deriveSiteHealth(siteDetail.record);
        const createdAt = formatDateTime(siteDetail.record.createdAt ?? null);
        const updatedAt = formatDateTime(siteDetail.record.updatedAt ?? siteDetail.record.createdAt ?? null);
        const inventorySourceLabel = inventoryPolicy.mode === 'netbox_authoritative'
          ? 'NetBox authoritative'
          : inventoryPolicy.mode === 'reap_discovery'
            ? 'REAP discovery'
            : 'Inherit organization policy';
        const inventoryConnectionLabel = inventoryPolicy.connectionId
          ? netBoxConnections.find((connection) => connection.id === inventoryPolicy.connectionId)?.name ?? inventoryPolicy.connectionId
          : '—';
        const persistedNetBoxSiteID = inventoryImportRuns[0]?.netboxSiteId;
        const inventoryNetBoxSiteLabel = persistedNetBoxSiteID
          ? netBoxSites.find((site) => site.id === persistedNetBoxSiteID)?.name
            ? `${netBoxSites.find((site) => site.id === persistedNetBoxSiteID)?.name} (#${persistedNetBoxSiteID})`
            : `#${persistedNetBoxSiteID}`
          : '—';
        return (
          <div className="detail-stack">
            {siteError && <OrgModulePlaceholder intent={siteError} />}
            <div className="record-summary">
              <div className="record-summary__grid">
                <div>
                  <span>Name</span>
                  <strong>{siteDetail.record.name ?? '—'}</strong>
                </div>
                <div>
                  <span>Site ID</span>
                  {siteDetail.record.id ? (
                    <CopyField value={siteDetail.record.id} label="Site ID" variant="chip" copiedLabel="Site ID copied" />
                  ) : (
                    <strong>—</strong>
                  )}
                </div>
                <div>
                  <span>{siteType === SiteSiteTypeEnum.AzureCloud ? 'Azure subscription ID' : 'Location'}</span>
                  <strong className="record-summary__multiline">{location}</strong>
                </div>
                <div>
                  <span>Connectors</span>
                  <strong>{connectorCount}</strong>
                </div>
                <div>
                  <span>Health</span>
                  <strong>{health}</strong>
                </div>
                <div>
                  <span>Created</span>
                  <strong>{createdAt}</strong>
                </div>
                <div>
                  <span>Updated</span>
                  <strong>{updatedAt}</strong>
                </div>
                <div>
                  <span>Inventory source</span>
                  <strong>{inventorySourceLabel}</strong>
                </div>
                {inventoryPolicy.mode === 'netbox_authoritative' ? <>
                  <div>
                    <span>NetBox connection</span>
                    <strong>{inventoryConnectionLabel}</strong>
                  </div>
                  <div>
                    <span>NetBox site</span>
                    <strong>{inventoryNetBoxSiteLabel}</strong>
                  </div>
                </> : null}
              </div>
            </div>
            {renderSiteDeviceTypeCards(siteDeviceTypeSummaries, { dense: true, vendors: siteVendorSummaries })}
            <section className="detail-section">
              <header>
                <h3>Provider Edge Links</h3>
              </header>
              {(siteDetail.record.providerEdgePorts?.filter(shouldDisplayProviderEdgeLink).length ?? 0) > 0 ? (
                <div className="inventory-device__provider-edge-box">
                  <div className="site-provider-edge-header">
                    <span>Device</span>
                    <span>Interface</span>
                    <span>Provider</span>
                    <span>Site Role</span>
                    <span>Uplink Role</span>
                    <span>Evidence</span>
                  </div>
                  {siteDetail.record.providerEdgePorts?.filter(shouldDisplayProviderEdgeLink).map((port, index) => {
                    const providerName =
                      port.providerIdentity?.providerName ||
                      port.providerIdentity?.providerNormalizedName ||
                      port.providerIdentity?.providerType ||
                      '';
                    const providerConfidence = port.providerIdentity?.providerConfidence || '';
                    const effectiveRole = providerEdgeEffectiveRole(port);
                    const uplinkRole = port.redundancy?.uplinkRole || '';
                    const evidenceText = describeProviderEdgeEvidence(port);
                    const siteRoleTone = effectiveRole.trim().toLowerCase() === 'provider_handoff' ? 'success' : providerEdgeClassificationTone(port.classification);
                    return (
                      <div className="site-provider-edge-row" key={`${port.deviceName ?? 'device'}-${port.interfaceName ?? 'iface'}-${index}`}>
                        <div className="site-provider-edge-device">
                          <strong>{port.deviceName || '—'}</strong>
                        </div>
                        <div className="site-provider-edge-interface">
                          <strong>{port.interfaceName || '—'}</strong>
                        </div>
                        <div className="site-provider-edge-provider">
                          <strong>{providerName || '—'}</strong>
                          {providerConfidence
                            ? <span>{formatProviderEdgeLabel(providerConfidence)}</span>
                            : <span className={getConfidenceBadgeClass(port.confidence)}>{formatProviderEdgeConfidence(port.confidence)}</span>}
                        </div>
                        <div className="site-provider-edge-site-role">
                          <span className={`ui-badge ui-badge--${siteRoleTone}`}>
                            {formatProviderEdgeLabel(effectiveRole)}
                          </span>
                        </div>
                        <div className="site-provider-edge-uplink-role">
                          <span>{uplinkRole ? formatProviderEdgeLabel(uplinkRole) : '—'}</span>
                        </div>
                        <div className="site-provider-edge-evidence">
                          <span title={evidenceText}>{evidenceText}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="helper-text">No provider edge links identified for this site.</p>
              )}
            </section>
            {siteFormValues ? (
              <SiteForm
                initialValues={siteFormValues}
                saving={siteDetail.saving}
                submitLabel="Save Site"
                onSubmit={handleUpdateSite}
                showActions={false}
                formRef={siteDetailFormRef}
              />
            ) : (
              <OrgModulePlaceholder
                intent={`Site type "${String(siteDetail.record.siteType)}" is not supported by this UI. This Site is read-only.`}
              />
            )}
          </div>
        );
      }
      case 'ping-mesh':
        return (
          <SitePingMeshEndpointForm
            siteId={siteDetail.record.id ?? ''}
            siteType={siteDetail.record.siteType}
            initialEndpoint={siteDetail.record.pingEndpointIp ?? ''}
            saving={siteDetail.saving}
            onSubmit={handleUpdateSitePingEndpoint}
            searchPathEndpoints={searchPingMeshEndpoints}
          />
        );
      case 'topology':
        if (siteDetail.record && siteTopologyV2ViewId(siteDetail.record)) {
          return (
            <div>
              <TopologyV2Page
                embedded
                hideControls
                initialSiteId={siteDetail.record.id ?? undefined}
                forceViewId={siteTopologyV2ViewId(siteDetail.record) ?? undefined}
              />
            </div>
          );
        }
        if (siteDetail.topologyLoading) {
          return <p>Loading topology…</p>;
        }
        return (
          <div>
            <div className="topology-diagram__toolbar">
              <span className="topology-diagram__toolbar-label">Toggle interface detail to inspect port-level connectivity.</span>
              <label className="topology-toggle">
                <span>Show interfaces</span>
                <span
                  className={['topology-toggle__switch', siteDetail.topologyExpandInterfaces ? 'is-active' : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <input
                    type="checkbox"
                    className="topology-toggle__checkbox"
                    checked={siteDetail.topologyExpandInterfaces}
                    onChange={(event) => handleTopologyExpandToggle(event.target.checked)}
                  />
                  <span className="topology-toggle__knob" aria-hidden="true" />
                </span>
              </label>
              <div className="topology-layout-controls">
                <button type="button" className="ui-button ui-button--ghost" onClick={handleLayoutReset}>
                  Reset layout
                </button>
                <button
                  type="button"
                  className="ui-button ui-button--ghost topology-layout-controls__icon-button"
                  onClick={() => handleTopologyFullscreenToggle(!topologyFullscreenOpen)}
                  aria-label={topologyFullscreenOpen ? 'Close topology window' : 'Open topology window'}
                  title={topologyFullscreenOpen ? 'Close windowed topology' : 'Open windowed topology'}
                >
                  {topologyFullscreenOpen ? <CompressIcon size={16} /> : <ExpandIcon size={16} />}
                </button>
              </div>
            </div>
            {siteDetail.topology && cytoscapeElements.length > 0 ? (
              <>
                {renderTopologyCanvas('embedded')}
                {topologyFullscreenOpen && (
                  typeof document !== 'undefined' &&
                  createPortal(
                    <div className="topology-window-backdrop" onMouseDown={() => handleTopologyFullscreenToggle(false)}>
                      <div className="topology-window-shell" onMouseDown={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          className="topology-window-close"
                          onClick={() => handleTopologyFullscreenToggle(false)}
                          aria-label="Close topology window"
                          title="Close"
                        >
                          <CloseIcon size={16} />
                        </button>
                        <div className="topology-diagram topology-diagram--windowed" ref={topologyWindowDiagramRef}>
                          <TopologyDiagram
                            elements={cytoscapeElements}
                            layoutKey={`${topologyDiagramKey}-windowed`}
                            mode="windowed"
                            manualPositions={manualNodePositions}
                            onNodePositionChange={handleNodePositionPersist}
                            onEdgeFocus={handleEdgeFocus}
                            onDeviceFocus={handleDeviceFocus}
                          />
                          {renderTopologyTooltip()}
                        </div>
                      </div>
                    </div>,
                    document.body
                  )
                )}
              </>
            ) : (
              <OrgModulePlaceholder intent="Topology unavailable." />
            )}
          </div>
        );
      case 'discover': {
        const jobsEmptyState = discoveryJobsQuery.loading ? <p>Loading discovery jobs…</p> : <p>No discovery jobs for this site.</p>;
        const runsEmptyState = !selectedSiteJob
          ? <p>Select a job to view its runs.</p>
          : discoveryRunsQuery.loading
          ? <p>Loading runs…</p>
          : <p>No runs recorded for this job.</p>;
        return (
          <div className="detail-stack">
            <ListWorkspace
              headerLabel="Discovery Jobs"
              rows={pagedSiteDiscoveryJobs}
              columns={siteJobColumns}
              rowId={(row, index) => row.id ?? index}
              page={siteJobsPage}
              pageSize={siteJobsPageSize}
              pageSizeOptions={[5, 10, 25]}
              paginationMeta={buildPaginationMeta(discoveryJobsQuery.data?.meta?.exactTotal ?? siteDiscoveryJobs.length, siteJobsPage, siteJobsPageSize)}
              onPageChange={setSiteJobsPage}
              onPageSizeChange={(size) => {
                setSiteJobsPageSize(size);
                setSiteJobsPage(1);
              }}
              onRowClick={(row) => {
                if (!row.id) {
                  return;
                }
                selectDiscoveryJob(row.id);
              }}
              highlightRowId={selectedSiteJobId}
              loading={discoveryJobsQuery.loading}
              emptyState={jobsEmptyState}
              onAdd={openCreateJobDrawer}
              onRefresh={handleRefreshJobs}
              onDeleteSelected={(rows) => {
                void handleDeleteJobs(rows as DiscoveryJob[]);
              }}
              headerStatus={
                jobActionError ? (
                  <p className="discovery-inline-error">{jobActionError}</p>
                ) : discoveryJobsQuery.error ? (
                  <p className="discovery-inline-error">{`Failed to load jobs: ${discoveryJobsQuery.error.message}`}</p>
                ) : undefined
              }
              rowActions={(row) => (
                <button
                  type="button"
                  aria-label="Edit job"
                  title="Edit job"
                  onClick={(event) => {
                    event.stopPropagation();
                    row.id && openEditJobDrawer(row.id);
                  }}
                  disabled={!row.id || jobActionBusyId === row.id}
                >
                  <PencilIcon size={16} />
                </button>
              )}
            />
            <ListWorkspace
              headerLabel="Discovery Runs"
              rows={pagedSiteDiscoveryRuns}
              columns={siteRunColumns}
              rowId={(row, index) => row.id ?? index}
              selectionDisabled
              page={siteRunsPage}
              pageSize={siteRunsPageSize}
              pageSizeOptions={[5, 10, 25]}
              paginationMeta={buildPaginationMeta(discoveryRunsQuery.data?.meta?.exactTotal ?? siteDiscoveryRuns.length, siteRunsPage, siteRunsPageSize)}
              onPageChange={setSiteRunsPage}
              onPageSizeChange={(size) => {
                setSiteRunsPageSize(size);
                setSiteRunsPage(1);
              }}
              onRowClick={(row) => {
                if (!row.id) {
                  return;
                }
                setSelectedRunId(row.id);
                setPendingRun(row);
                if (isRunLive(row.status)) {
                  handleOpenRunDetail(row);
                }
              }}
              highlightRowId={selectedRunId}
              loading={discoveryRunsQuery.loading}
              emptyState={runsEmptyState}
              onAdd={selectedSiteJob ? handleStartRun : undefined}
              onRefresh={handleRefreshRuns}
              headerStatus={
                runActionError ? (
                  <p className="discovery-inline-error">{runActionError}</p>
                ) : selectedSiteJob ? (
                  <span className="discovery-subtitle">
                    Runs for {siteDetail.record?.name ?? siteDetail.record?.id ?? 'site'}
                  </span>
                ) : undefined
              }
              rowActions={(row) => (
                <>
                  {isRunLive(row.status) && (
                    <button
                      type="button"
                      aria-label="Stop run"
                      title="Stop run"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleStopRun(row);
                      }}
                      disabled={!row.id || runActionBusyId === row.id}
                    >
                      <PauseIcon size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="View run"
                    title="View run"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenRunDetail(row);
                    }}
                    disabled={!row.id}
                  >
                    <EyeIcon size={16} />
                  </button>
                </>
              )}
            />
          </div>
        );
      }
      case 'inventory': {
        const inventoryEmptyState = siteInventoryRows.length === 0 ? <p>No inventory discovered for this site.</p> : undefined;
        return (
          <div className="site-inventory-workspace">
            <section className="site-cmdb-panel">
              <div className="site-cmdb-panel__header">
                <div>
                  <h3 style={{ margin: 0 }}>CMDB Relation View</h3>
                  <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
                    Site-scoped readout of REAP vs CMDB relation alignment for the selected CMDB connection.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {siteCMDBState.connections.length > 1 && (
                    <label style={{ display: 'grid', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Connection</span>
                      <select
                        className="select"
                        value={selectedSiteCMDBConnectionId}
                        onChange={(event) => {
                          setSelectedSiteCMDBConnectionId(event.target.value);
                          if (!siteDetail.record?.id) {
                            return;
                          }
                          void loadSiteCMDBContext(siteDetail.record.id, { connectionId: event.target.value });
                        }}
                      >
                        {siteCMDBState.connections
                          .filter((item) => Boolean(item.id))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.providerLabel ?? item.provider ?? 'CMDB'}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  <button
                    type="button"
                    className="shortcut-button"
                    disabled={!siteDetail.record?.id || siteCMDBState.loadingReport || !siteSelectedCMDBConnection?.id}
                    onClick={() => {
                      if (!siteDetail.record?.id || !siteSelectedCMDBConnection?.id) {
                        return;
                      }
                      void loadSiteCMDBContext(siteDetail.record.id, {
                        refreshReport: true,
                        connectionId: siteSelectedCMDBConnection.id
                      });
                    }}
                  >
                    {siteCMDBState.loadingReport ? 'Refreshing…' : 'Refresh CMDB report'}
                  </button>
                </div>
              </div>
              {siteCMDBState.error ? (
                <p className="org-shell__banner">{siteCMDBState.error}</p>
              ) : siteCMDBState.loadingConnections ? (
                <p className="helper-text">Loading CMDB context…</p>
              ) : !siteSelectedCMDBConnection?.id ? (
                <p className="helper-text">No CMDB-capable connection is configured for this org.</p>
              ) : (
                <>
                  <div className="site-cmdb-grid">
                    <div className="site-cmdb-card">
                      <span>Connection</span>
                      <strong>{siteSelectedCMDBConnection.providerLabel ?? siteSelectedCMDBConnection.provider ?? 'CMDB'}</strong>
                      <p>{siteCMDBState.report?.mode?.replaceAll('_', ' ') ?? 'report only'}</p>
                    </div>
                    <div className="site-cmdb-card">
                      <span>Maturity</span>
                      <strong>
                        {siteCMDBState.report?.maturity ? `${siteCMDBState.report.maturity.score}/100` : '—'}
                      </strong>
                      <p>{formatCMDBMaturityLabel(siteCMDBState.report?.maturity?.level)}</p>
                    </div>
                    <div className="site-cmdb-card">
                      <span>Site discrepancies</span>
                      <strong>{siteCMDBItems.length}</strong>
                      <p>{siteCMDBCountsByStatus.drift ?? 0} drift · {siteCMDBCountsByStatus.missing_in_cmdb ?? 0} missing</p>
                    </div>
                    <div className="site-cmdb-card">
                      <span>Interpretation gaps</span>
                      <strong>{(siteCMDBCountsByStatus.mapping_unverified ?? 0) + (siteCMDBCountsByStatus.not_comparable ?? 0)}</strong>
                      <p>{siteCMDBCountsByDomain.relation ?? 0} relations · {siteCMDBCountsByDomain.service ?? 0} services</p>
                    </div>
                  </div>
                  <div className="site-cmdb-inline-meta">
                    <span>Report: {formatDateTime(normalizeDateString(siteCMDBState.report?.generatedAt) ?? null)}</span>
                    <span>Last CMDB sync: {formatDateTime(normalizeDateString(siteCMDBState.report?.coverage?.lastSyncedAt) ?? null)}</span>
                    <span>
                      Endpoint service coverage: {siteCMDBState.report?.coverage?.linkedEndpointCount ?? 0}/{siteCMDBState.report?.coverage?.endpointCount ?? 0}
                    </span>
                  </div>
                  {siteCMDBRelationItems.length === 0 ? (
                    <p className="helper-text">No relation-specific discrepancies are currently recorded for this site.</p>
                  ) : (
                    <div className="site-cmdb-list">
                      {siteCMDBRelationItems.slice(0, 8).map((item, index) => (
                        <article key={`${item.id ?? item.externalRelationId ?? item.deviceId ?? 'cmdb'}-${index}`} className="site-cmdb-list__item">
                          <div className="site-cmdb-list__headline">
                            <strong>{item.deviceName ?? item.externalCiId ?? item.summary}</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span className={`ui-badge ${item.status === 'drift' || item.status === 'missing_in_cmdb' ? 'ui-badge--danger' : item.status === 'mapping_unverified' || item.status === 'not_comparable' ? 'ui-badge--warning' : ''}`.trim()}>
                                {item.status.replaceAll('_', ' ')}
                              </span>
                              <span className="ui-badge">{item.domain.replaceAll('_', ' ')}</span>
                            </div>
                          </div>
                          <p>{item.summary}</p>
                          <div className="site-cmdb-inline-meta">
                            {item.rawRelationType && <span>CMDB relation: {item.rawRelationType}</span>}
                            {item.canonicalRelation && <span>REAP relation: {item.canonicalRelation}</span>}
                            {typeof item.confidence === 'number' && <span className={getConfidenceBadgeClass(item.confidence)}>{item.confidence}% confidence</span>}
                          </div>
                          {item.evidence && item.evidence.length > 0 && <p className="helper-text">Evidence: {item.evidence.join(' · ')}</p>}
                        </article>
                      ))}
                    </div>
                  )}
                  {siteCMDBCountsByDomain.service && siteCMDBState.report?.observedServiceDetailsAvailable !== true && (
                    <p className="helper-text">
                      Service entries remain report-only here because REAP does not yet observe runtime services on endpoints for this site.
                    </p>
                  )}
                </>
              )}
            </section>
            <section className="site-cmdb-panel">
              <div className="site-cmdb-list__headline"><h3>NetBox import runs</h3><button type="button" className="shortcut-button" onClick={() => setInventoryImportRunsRefresh(value => value + 1)}>Refresh</button></div>
              {inventoryImportRuns.length === 0 ? <p className="helper-text">No imports have been requested for this site.</p> : (
                <div className="table-shell"><table><thead><tr><th>Status</th><th>Started</th><th>Finished</th><th>Result</th><th>Details</th></tr></thead><tbody>
                  {inventoryImportRuns.slice(0, 10).map(run => {
                    const result = run.summary && Object.keys(run.summary).length ? Object.entries(run.summary).map(([key, value]) => `${key}: ${value}`).join(' · ') : run.status === 'succeeded' ? 'Completed' : run.status === 'running' ? 'In progress' : 'No result recorded';
                    return <tr key={run.id}><td><span className={`ui-badge ${run.status === 'failed' ? 'ui-badge--danger' : run.status === 'running' ? 'ui-badge--warning' : ''}`.trim()}>{run.status}</span></td><td>{formatDateTime(normalizeDateString(run.startedAt ?? run.createdAt) ?? null)}</td><td>{formatDateTime(normalizeDateString(run.finishedAt) ?? null)}</td><td>{result}</td><td>{run.error && <div className="helper-text">Failure: {run.error}</div>}{run.logs?.length ? <details><summary>Logs</summary><ul className="helper-text">{run.logs.map((entry, index) => <li key={`${run.id}-${index}`}>{entry}</li>)}</ul></details> : '—'}</td></tr>;
                  })}
                </tbody></table></div>
              )}
            </section>
            <ListWorkspace
              headerLabel="Inventory"
              rows={siteInventoryRows}
              columns={siteInventoryColumns}
              rowId={(row, index) => row.id ?? row.mgmtAddress ?? index}
              selectionDisabled
              emptyState={inventoryEmptyState}
              loading={siteDetail.loading}
              toolbarActions={
                <button
                  type="button"
                  className="shortcut-button"
                  disabled={inventoryImportBusy || inventoryPolicy.mode !== 'netbox_authoritative' || !inventoryPolicy.connectionId}
                  onClick={() => void queueSiteNetBoxImport()}
                >
                  {inventoryImportBusy ? 'Queueing…' : 'Sync now'}
                </button>
              }
              onRowClick={(row) => {
                if (!row.id) {
                  return;
                }
                const params = new URLSearchParams();
                params.set('deviceId', row.id);
                if (row.siteId) {
                  params.set('siteId', row.siteId);
                } else if (siteDetail.record?.id) {
                  params.set('siteId', siteDetail.record.id);
                }
                navigate(`/app/inventory?${params.toString()}`);
              }}
            />
          </div>
        );
      }
      case 'clusters': {
        const activeSiteId = siteDetail.record?.id ?? null;
        const canAttachCluster = Boolean(activeSiteId && siteClusterAttachId);
        const headerStatus =
          availableClustersForSite.length === 0 ? <p className="list-workspace__status-note">All clusters are attached to this site.</p> : undefined;
        return (
          <ListWorkspace
            headerLabel="Clusters"
            rows={siteDetail.clusters}
            columns={siteClusterColumns}
            rowId={(row, index) => row.id ?? index}
            selectionDisabled
            onRowClick={(row) => {
              if (!row.id) {
                return;
              }
              setClusterDetailTab('details');
              setClusterDrawer({ clusterId: row.id, open: true });
              loadClusterDetail(row.id);
            }}
            rowActions={(row) => {
              const clusterId = row.id;
              if (!clusterId || !activeSiteId) {
                return null;
              }
              return (
                <button type="button" onClick={() => detachClusterFromSite(activeSiteId, clusterId)}>
                  Detach
                </button>
              );
            }}
            emptyState={<p>No clusters attached to this site.</p>}
            toolbarActions={
              availableClustersForSite.length > 0 ? (
                <div className="list-workspace__toolbar-form">
                  <label htmlFor="attach-site-cluster" className="sr-only">
                    Cluster
                  </label>
                  <select
                    id="attach-site-cluster"
                    value={siteClusterAttachId}
                    onChange={(event) => setSiteClusterAttachId(event.target.value)}
                  >
                    {availableClustersForSite.map((cluster) => (
                      <option key={cluster.id ?? cluster.name ?? 'cluster-option'} value={cluster.id}>
                        {cluster.name ?? cluster.id}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ui-button"
                    disabled={!canAttachCluster}
                    onClick={() => {
                      if (!activeSiteId || !siteClusterAttachId) {
                        return;
                      }
                      attachClusterToSite(activeSiteId, siteClusterAttachId);
                    }}
                  >
                    Attach cluster
                  </button>
                </div>
              ) : undefined
            }
            headerStatus={headerStatus}
          />
        );
      }
      case 'connectors': {
        const attachedClusterIds = siteDetail.clusters.map((cluster) => cluster.id).filter((value): value is string => Boolean(value));
        const defaultClusterId = attachedClusterIds.length === 1 ? attachedClusterIds[0] : undefined;
        const handleAddConnector =
          attachedClusterIds.length === 0
            ? undefined
            : () =>
                openConnectorCreate({
                  clusterId: defaultClusterId,
                  allowedClusterIds: attachedClusterIds
                });
        return (
          <ListWorkspace
            headerLabel="Connectors"
            rows={siteDetail.connectors}
            columns={siteConnectorColumns}
            rowId={(row, index) => row.id ?? index}
            selectionDisabled
            onRowClick={(row) => {
              const connectorId = row.id;
              if (!connectorId) {
                return;
              }
              setConnectorDetailTab('details');
              setConnectorDrawer({ connectorId, open: true });
              loadConnectorDetail(connectorId);
            }}
            emptyState={<p>No connectors associated with this site.</p>}
            onAdd={handleAddConnector}
            headerStatus={
              siteDetail.connectorsError || siteDetail.connectorsNext ? (
                <div className="list-workspace__status-note">
                  {siteDetail.connectorsError ? <span>{siteDetail.connectorsError}</span> : null}
                  {siteDetail.connectorsNext ? (
                    <button
                      type="button"
                      className="ui-button"
                      disabled={siteDetail.connectorsLoadingMore}
                      onClick={() => void loadMoreSiteConnectors()}
                    >
                      {siteDetail.connectorsLoadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        );
      }
      default:
        return null;
    }
  };

  const clusterDetailTabs = useMemo(
    () => [
      { id: 'details', label: 'Details' },
      { id: 'sites', label: 'Sites' },
      { id: 'connectors', label: 'Connectors' }
    ],
    []
  );

  const connectorDetailTabs = useMemo(
    () => [
      { id: 'details', label: 'Details' },
      { id: 'bootstrap', label: 'Bootstrap' }
    ],
    []
  );

  const renderConnectorDetailContent = () => {
    if (connectorDetail.loading) {
      return <p>Loading connector…</p>;
    }
    const connectorError = connectorDetail.error;
    if (connectorError && connectorDetail.activeTab !== 'details') {
      return <OrgModulePlaceholder intent={connectorError} />;
    }
    if (!connectorDetail.record) {
      return <OrgModulePlaceholder intent="Select a connector to view detail." />;
    }

    switch (connectorDetail.activeTab) {
      case 'details': {
        const lastSeen = formatDateTime(connectorDetail.record.lastSeenAt ?? connectorDetail.record.updatedAt ?? null);
        const sourceIpObservedAt = formatDateTime(connectorDetail.record.sourceIpObservedAt ?? null);
        const sourceIpHistory = (connectorDetail.record.sourceIpHistory ?? []).slice(0, 10);
        return (
          <div className="detail-stack">
            {connectorError && <OrgModulePlaceholder intent={connectorError} />}
            <div className="record-summary">
              <div className="record-summary__grid">
                <div>
                  <span>Name</span>
                  <strong>{connectorDetail.record.name ?? '—'}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{connectorDetail.record.status ?? '—'}</strong>
                </div>
                <div>
                  <span>Version</span>
                  <strong>{connectorDetail.record.version ?? '—'}</strong>
                </div>
                <div>
                  <span>Cluster</span>
                  <strong>
                    {connectorDetail.record.clusterId
                      ? clusterNames[connectorDetail.record.clusterId] ?? connectorDetail.record.clusterId
                      : '—'}
                  </strong>
                </div>
                <div>
                  <span>Last seen</span>
                  <strong>{lastSeen}</strong>
                </div>
                <div>
                  <span>VPN source IP</span>
                  <strong>{connectorDetail.record.sourceIp ?? '—'}</strong>
                </div>
                <div>
                  <span>Source IP last observed</span>
                  <strong>{sourceIpObservedAt}</strong>
                </div>
                <div>
                  <span>Description</span>
                  <strong className="record-summary__multiline">{connectorDetail.record.description ?? '—'}</strong>
                </div>
              </div>
            </div>
            {sourceIpHistory.length > 0 && (
              <div className="record-summary">
                <h4>Previous VPN source IPs</h4>
                <div className="record-summary__grid">
                  {sourceIpHistory.map((observation) => (
                    <div key={`${observation.sourceIp}-${String(observation.lastObservedAt)}`}>
                      <span>{formatDateTime(observation.lastObservedAt)}</span>
                      <strong>{observation.sourceIp}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {connectorLoadWidget && (
              <GrafanaIframeCard
                widget={connectorLoadWidget}
                height={220}
                refreshSeconds={30}
                timeRangePreset="15m"
                onAuthRefresh={refreshGrafanaEmbedSession}
              />
            )}
            <ConnectorForm
              initialValues={connectorToFormValues(connectorDetail.record)}
              clusterOptions={clustersState.items}
              saving={connectorDetail.saving}
              submitLabel="Save Connector"
              onSubmit={handleUpdateConnector}
              showActions={false}
              formRef={connectorDetailFormRef}
            />
          </div>
        );
      }
      case 'bootstrap':
        if (connectorDetail.userdataStatus === 'loading') {
          return <p>Generating bootstrap payload…</p>;
        }
        return (
          <div className="detail-section">
            <p>Download bootstrap bundle to provision this connector.</p>
            {connectorDetail.userdataStatus === 'error' && (
              <OrgModulePlaceholder intent={connectorDetail.userdataError ?? 'Failed to fetch user-data'} />
            )}
            {connectorDetail.resetStatus === 'error' && (
              <OrgModulePlaceholder intent={connectorDetail.resetError ?? 'Failed to reset enrollment'} />
            )}
            <div className="button-row">
              <ShortcutButton
                label="Cloud-init"
                onClick={() => connectorDetail.record?.id && downloadConnectorUserdata(connectorDetail.record.id, 'text/cloud-config')}
              />
              <ShortcutButton
                label="JSON"
                onClick={() => connectorDetail.record?.id && downloadConnectorUserdata(connectorDetail.record.id, 'application/json')}
              />
              <ShortcutButton
                label={connectorDetail.resetStatus === 'loading' ? 'Resetting…' : 'Reset enrollment'}
                disabled={connectorDetail.resetStatus === 'loading'}
                onClick={() => connectorDetail.record?.id && resetConnectorEnrollment(connectorDetail.record.id)}
              />
            </div>
            {connectorDetail.userdataStatus === 'success' && <p className="helper-text">Bootstrap bundle downloaded.</p>}
            {connectorDetail.resetStatus === 'success' && <p className="helper-text">Enrollment reset.</p>}
          </div>
        );
      default:
        return null;
    }
  };

  const renderClusterDetailContent = () => {
    if (clusterDetail.loading) {
      return <p>Loading cluster…</p>;
    }
    const clusterError = clusterDetail.error;
    if (clusterError && clusterDetail.activeTab !== 'details') {
      return <OrgModulePlaceholder intent={clusterError} />;
    }
    if (!clusterDetail.record) {
      return <OrgModulePlaceholder intent="Select a cluster to view detail." />;
    }

    switch (clusterDetail.activeTab) {
      case 'details': {
        const totalConnectors = clusterDetail.record.connectorsTotal;
        const onlineConnectors = clusterDetail.record.connectorsOnline;
        const updatedAt = formatDateTime(clusterDetail.record.updatedAt ?? clusterDetail.record.createdAt ?? null);
        return (
          <div className="detail-stack">
            {clusterError && <OrgModulePlaceholder intent={clusterError} />}
            <div className="record-summary">
              <div className="record-summary__grid">
                <div>
                  <span>Name</span>
                  <strong>{clusterDetail.record.name ?? '—'}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{clusterDetail.record.status ?? '—'}</strong>
                </div>
                <div>
                  <span>HA state</span>
                  <strong>{clusterDetail.record.haState ?? '—'}</strong>
                </div>
                <div>
                  <span>Connectors</span>
                  <strong>
                    {onlineConnectors ?? '—'}/{totalConnectors ?? '—'}
                  </strong>
                </div>
                <div>
                  <span>Internal inspection</span>
                  <strong>{clusterDetail.record.settings?.internalInspection?.enabled ? 'Enabled' : 'Disabled'}</strong>
                </div>
                <div>
                  <span>Updated</span>
                  <strong>{updatedAt}</strong>
                </div>
              </div>
            </div>
            {clusterConnectorLoadWidgets.length > 0 && (
              <section className="detail-section">
                <div className="detail-section__header">
                  <h4>Connector load</h4>
                  <p className="helper-text">Current collector utilization for each connector in this cluster.</p>
                </div>
                <div className="connector-load-grid">
                  {clusterConnectorLoadWidgets.map((widget) => (
                    <GrafanaIframeCard
                      key={widget.id}
                      widget={widget}
                      height={110}
                      refreshSeconds={30}
                      timeRangePreset="15m"
                      onAuthRefresh={refreshGrafanaEmbedSession}
                    />
                  ))}
                </div>
              </section>
            )}
            <ClusterForm
              initialValues={clusterToFormValues(clusterDetail.record)}
              saving={clusterDetail.saving}
              submitLabel="Save Cluster"
              onSubmit={handleUpdateCluster}
              showActions={false}
              formRef={clusterDetailFormRef}
            />
          </div>
        );
      }
      case 'sites':
        const activeClusterId = clusterDetail.record?.id ?? null;
        const canAttachSite = Boolean(activeClusterId && clusterSiteAttachId);
        return (
          <ListWorkspace
            headerLabel="Sites"
            rows={clusterDetail.sites}
            columns={clusterSiteColumns}
            rowId={(row, index) => row.id ?? index}
            selectionDisabled
            onRowClick={(row) => {
              if (!row.id) {
                return;
              }
              setSiteDrawer({ siteId: row.id, open: true });
              loadSiteDetail(row.id);
            }}
            rowActions={(row) => {
              const siteId = row.id;
              if (!siteId || !activeClusterId) {
                return null;
              }
              return (
                <button type="button" onClick={() => detachClusterFromSite(siteId, activeClusterId)}>
                  Detach
                </button>
              );
            }}
            emptyState={<p>No sites attached to this cluster.</p>}
            toolbarActions={
              availableSitesForCluster.length > 0 ? (
                <div className="list-workspace__toolbar-form">
                  <label htmlFor="attach-cluster-site" className="sr-only">
                    Site
                  </label>
                  <select
                    id="attach-cluster-site"
                    value={clusterSiteAttachId}
                    onChange={(event) => setClusterSiteAttachId(event.target.value)}
                  >
                    {availableSitesForCluster.map((site) => (
                      <option key={site.id ?? site.name ?? 'site-option'} value={site.id}>
                        {site.name ?? site.id}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ui-button"
                    disabled={!canAttachSite}
                    onClick={() => {
                      if (!activeClusterId || !clusterSiteAttachId) {
                        return;
                      }
                      attachClusterToSite(clusterSiteAttachId, activeClusterId);
                    }}
                  >
                    Attach site
                  </button>
                </div>
              ) : undefined
            }
            headerStatus={
              clusterDetail.sitesError || clusterDetail.sitesNext || availableSitesForCluster.length === 0 ? (
                <div className="list-workspace__status-note">
                  {clusterDetail.sitesError ? <span>{clusterDetail.sitesError}</span> : null}
                  {availableSitesForCluster.length === 0 ? <span>All sites are attached to this cluster.</span> : null}
                  {clusterDetail.sitesNext ? (
                    <button
                      type="button"
                      className="ui-button"
                      disabled={clusterDetail.sitesLoadingMore}
                      onClick={() => void loadMoreClusterSites()}
                    >
                      {clusterDetail.sitesLoadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        );
      case 'connectors': {
        const clusterId = clusterDetail.record?.id ?? null;
        return (
          <ListWorkspace
            headerLabel="Connectors"
            rows={clusterDetail.connectors}
            columns={clusterConnectorColumns}
            rowId={(row, index) => row.id ?? index}
            selectionDisabled
            onRowClick={(row) => {
              const connectorId = row.id;
              if (!connectorId) {
                return;
              }
              setConnectorDetailTab('details');
              setConnectorDrawer({ connectorId, open: true });
              loadConnectorDetail(connectorId);
            }}
            emptyState={<p>No connectors assigned to this cluster.</p>}
            onAdd={
              clusterId
                ? () =>
                    openConnectorCreate({
                      clusterId,
                      allowedClusterIds: [clusterId]
                    })
                : undefined
            }
            headerStatus={
              clusterDetail.connectorsError || clusterDetail.connectorsNext ? (
                <div className="list-workspace__status-note">
                  {clusterDetail.connectorsError ? <span>{clusterDetail.connectorsError}</span> : null}
                  {clusterDetail.connectorsNext ? (
                    <button
                      type="button"
                      className="ui-button"
                      disabled={clusterDetail.connectorsLoadingMore}
                      onClick={() => void loadMoreClusterConnectors()}
                    >
                      {clusterDetail.connectorsLoadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        );
      }
      default:
        return null;
    }
  };

  const sitePaginationInfo = useMemo(
    () => deriveSitePaginationMeta(sitesPageState.items.length),
    [deriveSitePaginationMeta, sitesPageState.items.length]
  );
  const allSitesDeviceTypeSummaries = useMemo(() => {
    const buckets = new Map<string, SiteDeviceTypeSummary>();
    sitesPageState.items.forEach((site) => {
      (site.deviceTypeCounts ?? []).forEach((item) => {
        const current = buckets.get(item.key) ?? { ...item, count: 0 };
        current.count += item.count;
        if (!current.detail && item.detail) {
          current.detail = item.detail;
        }
        buckets.set(item.key, current);
      });
    });
    return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [sitesPageState.items]);
  const allSitesVendorSummaries = useMemo(() => {
    const buckets = new Map<string, SiteVendorSummary>();
    sitesPageState.items.forEach((site) => {
      (site.vendorCounts ?? []).forEach((item) => {
        const current = buckets.get(item.key) ?? { ...item, count: 0 };
        current.count += item.count;
        buckets.set(item.key, current);
      });
    });
    return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [sitesPageState.items]);
  const allSitesTotalDevices = useMemo(
    () => allSitesDeviceTypeSummaries.reduce((sum, item) => sum + item.count, 0),
    [allSitesDeviceTypeSummaries]
  );
  const allSitesActiveDevices = useMemo(
    () => sitesPageState.items.reduce((sum, site) => sum + Number(site.deviceCounts?.active ?? 0), 0),
    [sitesPageState.items]
  );
  const allSitesUnreachableDevices = useMemo(
    () => sitesPageState.items.reduce((sum, site) => sum + Number(site.deviceCounts?.offline ?? 0), 0),
    [sitesPageState.items]
  );
  const connectivitySummaryCards = useMemo<ConnectivitySummaryCard[]>(() => {
    const siteCount = sitesState.items.length || sitesPageState.items.length;
    const clusterCount = clustersState.items.length;
    const cloudAccessCount = cloudAccessState.items.length;
    return [
      {
        key: 'total-discovered',
        label: 'Total discovered',
        value: allSitesTotalDevices,
        detail: `${formatNumber(siteCount)} sites · ${formatNumber(clusterCount)} connector clusters`,
        iconKind: 'switch',
        tone: 'success'
      },
      {
        key: 'active-devices',
        label: 'Active devices',
        value: allSitesActiveDevices,
        detail: 'Responding inventory devices',
        iconKind: 'router',
        tone: 'info'
      },
      {
        key: 'sites',
        label: 'Sites',
        value: siteCount,
        detail: 'Connectivity and topology scopes',
        iconKind: 'load-balancer',
        tone: 'info'
      },
      {
        key: 'cloud-access',
        label: 'Cloud access',
        value: cloudAccessCount,
        detail: cloudAccessState.error
          ? 'Some provider statuses are unavailable'
          : awsOnlyCloudAccess
            ? cloudAccessCount === 1
              ? '1 AWS discovery role'
              : `${formatNumber(cloudAccessCount)} AWS discovery roles`
            : cloudAccessCount === 1
              ? '1 cloud connection'
              : `${formatNumber(cloudAccessCount)} cloud connections`,
        iconKind: 'wireless-ap',
        tone: 'info'
      },
      {
        key: 'unreachable',
        label: 'Unreachable',
        value: allSitesUnreachableDevices,
        detail: 'Inventory devices marked unreachable',
        iconKind: 'firewall',
        tone: allSitesUnreachableDevices > 0 ? 'danger' : 'success'
      }
    ];
  }, [
    allSitesActiveDevices,
    allSitesTotalDevices,
    allSitesUnreachableDevices,
    awsOnlyCloudAccess,
    cloudAccessState.items.length,
    cloudAccessState.error,
    clustersState.items.length,
    sitesPageState.items.length,
    sitesState.items.length
  ]);
  const clusterPaginationInfo = useMemo(
    () => deriveClusterPaginationMeta(clustersPageState.items.length),
    [clustersPageState.items.length, deriveClusterPaginationMeta]
  );
  const connectorPaginationInfo = useMemo(
    () => deriveConnectorPaginationMeta(connectorsPageState.items.length),
    [connectorsPageState.items.length, deriveConnectorPaginationMeta]
  );

  const handleSitePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage === sitePageState.page || nextPage < 1 || nextPage > sitePaginationInfo.pageCount) {
        return;
      }
      setSitePage(nextPage);
    },
    [sitePageState.page, sitePaginationInfo.pageCount, setSitePage]
  );
  const handleClusterPageChange = useCallback(
    (nextPage: number) => {
      if (nextPage === clusterPageState.page || nextPage < 1 || nextPage > clusterPaginationInfo.pageCount) {
        return;
      }
      setClusterPage(nextPage);
    },
    [clusterPageState.page, clusterPaginationInfo.pageCount, setClusterPage]
  );
  const handleConnectorPageChange = useCallback(
    (nextPage: number) => {
      if (nextPage === connectorPageState.page || nextPage < 1 || nextPage > connectorPaginationInfo.pageCount) {
        return;
      }
      setConnectorPage(nextPage);
    },
    [connectorPageState.page, connectorPaginationInfo.pageCount, setConnectorPage]
  );

  const handleSitePageSizeChange = useCallback((size: number) => setSitePageSize(size), [setSitePageSize]);
  const handleClusterPageSizeChange = useCallback((size: number) => setClusterPageSize(size), [setClusterPageSize]);
  const handleConnectorPageSizeChange = useCallback((size: number) => setConnectorPageSize(size), [setConnectorPageSize]);

const siteFilterBar = (
  <div className="list-workspace__filter-grid">
    <label className="form-field">
      <span className="form-field__label">Search</span>
      <input
        type="text"
        className="input"
        value={siteFilters.q}
        onChange={(event) => updateSiteFilters(() => ({ q: event.target.value }))}
        placeholder="Search by name, label, or location"
      />
    </label>
  </div>
);

const clusterFilterBar = (
  <div className="list-workspace__filter-grid">
    <label className="form-field">
      <span className="form-field__label">Search</span>
      <input
        type="text"
        className="input"
        value={clusterFilters.q}
        onChange={(event) => updateClusterFilters(() => ({ q: event.target.value }))}
        placeholder="Search clusters"
      />
    </label>
  </div>
);

const connectorFilterBar = (
  <div className="list-workspace__filter-grid">
    <label className="form-field">
      <span className="form-field__label">Search</span>
      <input
        type="text"
        className="input"
        value={connectorFilters.q}
        onChange={(event) => updateConnectorFilters(() => ({ q: event.target.value }))}
        placeholder="Search connectors"
      />
    </label>
  </div>
);

const siteWorkspace: WorkspaceProps<SiteView> = {
  rows: sitesPageState.items,
  columns: siteColumns,
  rowId: (row, index) => row.id ?? index,
  page: sitePageState.page,
  pageSize: sitePageState.pageSize,
  paginationMeta: sitePaginationInfo,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  onPageChange: handleSitePageChange,
  onPageSizeChange: handleSitePageSizeChange,
  highlightRowId: siteDrawer?.siteId ?? null,
  selectionDisabled: false,
  onRowClick: handleSiteRowClick,
  onDeleteSelected: (rows) => setPendingResourceDelete({ kind: 'sites', rows }),
  headerStatus: connectivitySummaryCards.length > 0 || allSitesDeviceTypeSummaries.length > 0 ? (
    <div className="connectivity-sites-summary">
      {renderConnectivitySummaryCards(connectivitySummaryCards)}
      {renderSiteDeviceTypeCards(allSitesDeviceTypeSummaries, { compact: true, vendors: allSitesVendorSummaries })}
    </div>
  ) : undefined,
  loading: sitesPageState.loading,
  emptyState: sitesPageState.loading ? <p>Loading sites…</p> : <p>No sites yet.</p>,
  rowActions: (row) => {
    if (!row.id) {
      return null;
    }
    const siteId = row.id;
    return (
      <>
        <button
          type="button"
          aria-label="Open site cockpit"
          title="Open site cockpit"
          onClick={(event) => {
            event.stopPropagation();
            handleSiteCockpitShortcut(siteId);
          }}
        >
          <ChartIcon size={16} />
        </button>
        <button
          type="button"
          aria-label="Open Site Intelligence"
          title="Open Site Intelligence"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/app/sites/${encodeURIComponent(siteId)}/intelligence`);
          }}
        >
          <GraphIcon size={16} />
        </button>
        <button
          type="button"
          aria-label="View inventory"
          title="View inventory"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/app/inventory?siteId=${encodeURIComponent(siteId)}`);
          }}
        >
          <LayersIcon size={16} />
        </button>
        <button
          type="button"
          aria-label="Run discovery"
          title="Run discovery"
          onClick={(event) => {
            event.stopPropagation();
            handleSiteDiscoverShortcut(siteId);
          }}
        >
          <RadarIcon size={16} />
        </button>
        <button
          type="button"
          aria-label="Open topology"
          title="Open topology"
          onClick={(event) => {
            event.stopPropagation();
            handleSiteTopologyShortcut(siteId);
          }}
        >
          <NetworkIcon size={16} />
        </button>
      </>
    );
  },
  toolbarActions: (
    <button
      type="button"
      className="list-workspace__icon-button"
      aria-label="Import sites"
      title="Import sites"
      onClick={() => {
        setBulkImportRows([]);
        setImportDrawerOpen(true);
      }}
    >
      <DownloadIcon size={18} />
    </button>
  ),
  onAdd: openSiteCreate,
  onRefresh: () => {
    void refreshSitesPage({ cache: 'no-store' });
  },
  filterBar: siteFilterBar,
  filterActive: Boolean(siteFilters.q.trim()),
  onFilterReset: () => updateSiteFilters(() => ({ q: '' }))
};

const cloudAccessWorkspace: WorkspaceProps<CloudAccessView> = {
  rows: cloudAccessRows,
  columns: cloudAccessColumns,
  rowId: cloudAccessRowKey,
  page: 1,
  pageSize: cloudAccessRows.length || PAGE_SIZE_OPTIONS[0],
  paginationMeta: undefined,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  onPageChange: () => undefined,
  onPageSizeChange: () => undefined,
  highlightRowId: null,
  selectionDisabled: true,
  loading: sitesState.loading || cloudAccessState.loading,
  rowActions: (row) => {
    const setupAction = cloudAccessSetupAction(row.provider, row.status);
    if (!setupAction.enabled) {
      return (
        <button type="button" disabled title={setupAction.unavailableReason}>
          {setupAction.label}
        </button>
      );
    }
    const busy = cloudAccessActionBusyKeys.has(cloudAccessRowKey(row));
    return (
      <>
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            void handleCloudAccessAction(row);
          }}
        >
          {busy ? (row.provider === 'aws' ? 'Opening…' : 'Working…') : setupAction.label}
        </button>
        {row.provider === 'azure' && (row.status === 'pending' || row.status === 'enabled') && row.id ? (
          <button type="button" disabled={busy} onClick={(event) => {
            event.stopPropagation();
            void handleCloudAccessAction(row, true);
          }}>Check access</button>
        ) : null}
      </>
    );
  },
  emptyState:
    sitesState.loading || cloudAccessState.loading ? (
      <p>Loading cloud access…</p>
    ) : cloudAccessState.error ? (
      <p>{cloudAccessState.error}</p>
    ) : (
      <p>{awsOnlyCloudAccess ? 'No AWS cloud sites found.' : 'No cloud sites found.'}</p>
    ),
  onRefresh: () => {
    setCloudAccessActionError(null);
    void Promise.all([refreshSitesCatalog({ cache: 'no-store' }), refreshCloudAccessCatalog({ cache: 'no-store' })]);
  }
};

const clusterWorkspace: WorkspaceProps<ClusterView> = {
  rows: clustersPageState.items,
  columns: clusterColumns,
  rowId: (row, index) => row.id ?? index,
  page: clusterPageState.page,
  pageSize: clusterPageState.pageSize,
  paginationMeta: clusterPaginationInfo,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  onPageChange: handleClusterPageChange,
  onPageSizeChange: handleClusterPageSizeChange,
  highlightRowId: clusterDrawer?.clusterId ?? null,
  selectionDisabled: false,
  onRowClick: (row) => {
    const clusterId = row.id;
    if (!clusterId) {
      return;
    }
      setClusterDetailTab('details');
      setClusterDrawer({ clusterId, open: true });
      loadClusterDetail(clusterId);
  },
  onDeleteSelected: (rows) => setPendingResourceDelete({ kind: 'clusters', rows }),
  loading: clustersPageState.loading,
  emptyState: clustersPageState.loading ? <p>Loading clusters…</p> : <p>No clusters configured.</p>,
  onAdd: openClusterCreate,
  onRefresh: () => {
    void refreshClustersPage({ cache: 'no-store' });
  },
  filterBar: clusterFilterBar,
  filterActive: Boolean(clusterFilters.q.trim()),
  onFilterReset: () => updateClusterFilters(() => ({ q: '' }))
};

const connectorWorkspace: WorkspaceProps<ConnectorView> = {
  rows: connectorsPageState.items,
  columns: connectorColumns,
  rowId: (row, index) => row.id ?? index,
  page: connectorPageState.page,
  pageSize: connectorPageState.pageSize,
  paginationMeta: connectorPaginationInfo,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  onPageChange: handleConnectorPageChange,
  onPageSizeChange: handleConnectorPageSizeChange,
  highlightRowId: connectorDrawer?.connectorId ?? null,
  selectionDisabled: false,
  onRowClick: (row) => {
    const connectorId = row.id;
    if (!connectorId) {
      return;
    }
      setConnectorDetailTab('details');
      setConnectorDrawer({ connectorId, open: true });
      loadConnectorDetail(connectorId);
  },
  onDeleteSelected: (rows) => setPendingResourceDelete({ kind: 'connectors', rows }),
  loading: connectorsPageState.loading,
  emptyState: connectorsPageState.loading ? <p>Loading connectors…</p> : <p>No connectors registered.</p>,
  rowActions: (row) => {
    const connectorId = row.id;
    if (!connectorId) {
      return null;
    }
    return (
      <button
        type="button"
        aria-label="Download bootstrap"
        onClick={(event) => {
          event.stopPropagation();
          downloadConnectorUserdata(connectorId, 'text/cloud-config');
        }}
      >
        <DownloadIcon size={16} />
      </button>
    );
  },
  onAdd: () => openConnectorCreate(),
  onRefresh: () => {
    void refreshConnectorsPage({ cache: 'no-store' });
  },
  filterBar: connectorFilterBar,
  filterActive: Boolean(connectorFilters.q.trim()),
  onFilterReset: () => updateConnectorFilters(() => ({ q: '' }))
};

  const createConnectorClusterOptions = useMemo(() => {
    if (!connectorDrawer?.allowedClusterIds || connectorDrawer.allowedClusterIds.length === 0) {
      return clustersState.items;
    }
    const allowed = new Set(connectorDrawer.allowedClusterIds);
    return clustersState.items.filter((cluster) => cluster.id && allowed.has(cluster.id));
  }, [connectorDrawer?.allowedClusterIds, clustersState.items]);

  const connectorClusterLocked =
    Boolean(
      connectorDrawer?.clusterId &&
        connectorDrawer.allowedClusterIds &&
        connectorDrawer.allowedClusterIds.length === 1 &&
        connectorDrawer.allowedClusterIds[0] === connectorDrawer.clusterId
    );

  const activeWorkspace: WorkspaceProps<any> =
    activeTab === 'sites'
      ? siteWorkspace
      : activeTab === 'cloud-access'
        ? cloudAccessWorkspace
        : activeTab === 'clusters'
          ? clusterWorkspace
          : connectorWorkspace;

  const siteDrawerActions =
    siteDetail.activeTab === 'details' && siteDetail.record && getSiteTypeForForm(siteDetail.record)
      ? (
          <button
            type="button"
            className="drawer-icon-button"
            aria-label="Save site"
            title="Save site"
            disabled={siteDetail.saving}
            onClick={() => siteDetailFormRef.current?.requestSubmit()}
          >
            <SaveIcon size={18} />
          </button>
        )
      : null;

  const clusterDrawerActions =
    clusterDetail.activeTab === 'details' && clusterDetail.record
      ? (
          <button
            type="button"
            className="drawer-icon-button"
            aria-label="Save cluster"
            title="Save cluster"
            disabled={clusterDetail.saving}
            onClick={() => clusterDetailFormRef.current?.requestSubmit()}
          >
            <SaveIcon size={18} />
          </button>
        )
      : null;

  const connectorDrawerActions =
    connectorDetail.activeTab === 'details' && connectorDetail.record
      ? (
          <button
            type="button"
            className="drawer-icon-button"
            aria-label="Save connector"
            title="Save connector"
            disabled={connectorDetail.saving}
            onClick={() => connectorDetailFormRef.current?.requestSubmit()}
          >
            <SaveIcon size={18} />
          </button>
        )
      : null;

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Connectivity & Sites"
        description="Sites, connector clusters, attachments, and topology viewer by site."
        topSlot={<OrgModulePlaceholder intent="Select an organization to load sites." />}
      >
        <OrgModulePlaceholder intent="Activate an organization to manage connectivity." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="Connectivity & Sites"
      description="Sites, connector clusters, attachments, and topology viewer by site."
      topSlot={topSlot}
    >
      {activeTab === 'cloud-access' && cloudAccessState.error ? <p className="org-shell__banner">{cloudAccessState.error}</p> : null}
      {activeTab === 'cloud-access' && cloudAccessActionError ? <p className="org-shell__banner">{cloudAccessActionError}</p> : null}
      <ListWorkspace tabs={tabItems} activeTabId={activeTab} onTabChange={(id) => setActiveTab(id as ConnectivityTab)} {...activeWorkspace} />

      <Drawer
        open={importDrawerOpen}
        title="Import sites"
        subtitle="Create REAP sites and import inventory"
        onClose={() => {
          if (!bulkImportBusy) setImportDrawerOpen(false);
        }}
        width="wide"
      >
        <div className="detail-stack site-import-form">
          <label className="form-field">
            <span className="form-field__label">Import from</span>
            <select value={importSourceMode} disabled={bulkImportBusy || csvImportBusy || !inventorySourceCatalog.length} onChange={(event) => setImportSourceMode(event.target.value)}>
              <option value="">Select an inventory source</option>
              {inventorySourceCatalog.map((source) => <option key={source.key} value={source.key}>{source.label}</option>)}
            </select>
          </label>

          {activeImportWorkflow === 'informational' ? (
            <p className="helper-text">{activeImportSource?.description}</p>
          ) : activeImportWorkflow === 'incremental_file_import' ? (
            <section className="site-cmdb-panel detail-stack">
              <div>
                <h3>Upload a site CSV</h3>
                <p className="helper-text">Choose the format of the file you received. We will match its columns and show the sites before anything is imported.</p>
              </div>
              {!csvImportDraft ? <>
                <label className="form-field">
                  <span className="form-field__label">CSV format</span>
                  <select value={csvSchemaKey} disabled={csvImportBusy || csvSchemasLoading} onChange={(event) => setCSVSchemaKey(event.target.value)}>
                    <option value="">{csvSchemasLoading ? 'Loading formats…' : 'Select a CSV format'}</option>
                    {csvSchemas.map((schema) => <option key={schema.key} value={schema.key}>{schema.label}</option>)}
                  </select>
                  <span className="helper-text">{selectedCSVSchema?.description ?? 'Choose the format that matches your export.'}</span>
                  {selectedCSVSchema?.requiredHeaders.length ? <span className="helper-text">Required columns: {selectedCSVSchema.requiredHeaders.join(', ')}.</span> : null}
                </label>
                  <label className="form-field">
                    <span className="form-field__label">Import file</span>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      disabled={csvImportBusy || !csvSchemaKey || csvSchemasLoading}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0] ?? null;
                        // Browser file inputs do not fire change again for the
                        // same file unless their value is cleared. Keep the
                        // drawer open and make every failed upload retryable.
                        event.currentTarget.value = '';
                        void uploadCSVImport(file);
                      }}
                    />
                  </label>
                  {!csvSchemaKey ? <p className="helper-text">Select a CSV format before uploading.</p> : null}
              </> : (
                <>
                  <p className="helper-text"><strong>{csvImportDraft.fileName}</strong> is a {selectedCSVSchema?.label ?? 'CSV'} import. Review the detected mapping and preview before importing.</p>
                  {selectedCSVSchema?.mappingEditable ? <div className="form-grid">
                    {CSV_SITE_FIELDS.map((field) => (
                      <label className="form-field" key={field.key}>
                        <span className="form-field__label">{field.label}{field.required ? ' *' : ''}</span>
                        <select
                          value={csvImportDraft.mapping[field.key] ?? ''}
                          disabled={csvImportBusy || Boolean(csvImportDraft.run)}
                          onChange={(event) => setCSVImportDraft((current) => current ? { ...current, mapping: { ...current.mapping, [field.key]: event.target.value } } : current)}
                        >
                          <option value="">{field.required ? 'Select column' : 'Not imported'}</option>
                          {csvImportDraft.headers.map((header) => <option key={header} value={header}>{header}</option>)}
                        </select>
                      </label>
                    ))}
                  </div> : <p className="helper-text">Column mapping is fixed by the selected CSV format.</p>}
                  <div className="button-row">
                    <ShortcutButton label={csvImportBusy ? 'Validating…' : 'Refresh preview'} disabled={csvImportBusy || Boolean(csvImportDraft.run)} onClick={() => void previewCSVImport(csvImportDraft.mapping)} />
                    <ShortcutButton label={csvImportDraft.run ? `Import ${csvImportDraft.run.status ?? 'queued'}` : csvImportBusy ? 'Starting import…' : `Import ${csvImportDraft.rows.length} sites`} disabled={csvImportBusy || Boolean(csvImportDraft.run)} onClick={() => void confirmCSVImport()} />
                    {!csvImportDraft.run ? <ShortcutButton label="Choose another file" disabled={csvImportBusy} onClick={() => setCSVImportDraft(null)} /> : null}
                  </div>
                  {csvImportDraft.errors.length > 0 ? (
                    <div className="org-shell__banner" role="status">
                      <strong>{csvImportDraft.errors.length} validation issue{csvImportDraft.errors.length === 1 ? '' : 's'}</strong>
                      <ul>{csvImportDraft.errors.slice(0, 20).map((error, index) => <li key={`${error.row ?? 'header'}-${error.field ?? ''}-${index}`}>Row {error.row ?? 1}{error.field ? `, ${error.field}` : ''}: {error.reason ?? 'Invalid value'}</li>)}</ul>
                    </div>
                  ) : <p className="helper-text">{csvImportDraft.rows.length} sites ready to import. No validation errors found.</p>}
                  {csvImportDraft.rows.length > 0 ? (
                    <div className="table-shell"><table><thead><tr>{CSV_SITE_FIELDS.filter((field) => csvImportDraft.mapping[field.key]).map((field) => <th key={field.key}>{field.label}</th>)}</tr></thead><tbody>
                      {csvImportDraft.rows.slice(0, 20).map((row, index) => <tr key={index}>{CSV_SITE_FIELDS.filter((field) => csvImportDraft.mapping[field.key]).map((field) => <td key={field.key}>{row[field.key] ?? '—'}</td>)}</tr>)}
                    </tbody></table></div>
                  ) : null}
                </>
              )}
            </section>
          ) : (
            <>
              <label className="form-field">
                <span className="form-field__label">NetBox connection</span>
                <select value={importConnectionID} disabled={bulkImportBusy} onChange={(event) => setImportConnectionID(event.target.value)}>
                  <option value="">Select a NetBox connection</option>
                  {netBoxConnections.map((connection) => <option key={connection.id} value={connection.id}>{connection.name ?? connection.id}</option>)}
                </select>
              </label>
              <div className="form-field">
                <span className="form-field__label">NetBox sites</span>
                <div className="netbox-site-checklist" aria-label="NetBox sites">
                  {importNetBoxSites.map((site) => {
                    const selected = importSelectedNetBoxSiteIDs.includes(site.id);
                    return (
                      <label key={site.id} className="checkbox-row netbox-site-checklist__item">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selected}
                          disabled={bulkImportBusy || importSitesLoading || !importConnectionID}
                          onChange={(event) => setImportSelectedNetBoxSiteIDs((current) => event.target.checked ? [...current, site.id] : current.filter((id) => id !== site.id))}
                        />
                        <span>{site.name} (#{site.id})</span>
                      </label>
                    );
                  })}
                  {!importSitesLoading && importConnectionID && importNetBoxSites.length === 0 ? <span className="form-field__hint">No NetBox sites are available.</span> : null}
                </div>
                <small>{importSitesLoading ? 'Loading NetBox sites…' : 'Select one or more sites. Each selection creates a REAP site and queues its inventory import.'}</small>
              </div>
              <div className="button-row">
                <ShortcutButton
                  label={bulkImportBusy ? 'Queueing imports…' : `Import ${importSelectedNetBoxSiteIDs.length || ''} site${importSelectedNetBoxSiteIDs.length === 1 ? '' : 's'}`}
                  disabled={bulkImportBusy || !importConnectionID || importSelectedNetBoxSiteIDs.length === 0}
                  onClick={() => void queueBulkNetBoxImports()}
                />
              </div>
            </>
          )}

          {bulkImportRows.length > 0 ? (
            <section className="site-cmdb-panel">
              <div className="site-cmdb-list__headline"><h3>Import runs</h3></div>
              <div className="table-shell"><table><thead><tr><th>NetBox site</th><th>REAP site</th><th>Status</th><th>Logs</th></tr></thead><tbody>
                {bulkImportRows.map((row) => (
                  <tr key={row.netBoxSite.id}>
                    <td>{row.netBoxSite.name}</td>
                    <td>{row.reapSiteId ?? 'Creating…'}</td>
                    <td>{row.error ? <span className="ui-badge ui-badge--danger">failed</span> : row.run ? <span className={`ui-badge ${row.run.status === 'failed' ? 'ui-badge--danger' : row.run.status === 'running' ? 'ui-badge--warning' : ''}`.trim()}>{row.run.status}</span> : 'Queueing…'}</td>
                    <td>{row.error ? <span className="helper-text">{row.error}</span> : row.logs?.length ? <details><summary>Logs</summary><ul className="helper-text">{row.logs.map((log, index) => <li key={`${row.netBoxSite.id}-${index}`}>{log}</li>)}</ul></details> : '—'}</td>
                  </tr>
                ))}
              </tbody></table></div>
            </section>
          ) : null}
        </div>
      </Drawer>

      <Drawer
        open={Boolean(siteDrawer?.siteId)}
        title={siteDetail.record?.name ?? sitesState.items.find((site) => site.id === siteDrawer?.siteId)?.name ?? 'Site detail'}
        subtitle={siteDetail.record?.id ?? siteDrawer?.siteId ?? undefined}
        onClose={() => {
          setSiteDrawer(null);
          setSiteDetail(createInitialSiteDetailState());
        }}
        width="wide"
        actions={siteDrawerActions}
      >
        {siteDrawer?.siteId ? (
          <>
            <Tabs items={siteDetailTabs} activeId={siteDetail.activeTab} onChange={(id) => setSiteDetailTab(id as SiteDetailTab)} />
            <div className="detail-body">{renderSiteDetailContent()}</div>
          </>
        ) : (
          <OrgModulePlaceholder intent="Select a site to view detail." />
        )}
      </Drawer>

      <Drawer
        open={Boolean(siteDrawer && !siteDrawer.siteId)}
        title="Create Site"
        onClose={() => {
          setSiteDrawer(null);
          setSiteCreateError(null);
          setSiteCreateSaving(false);
        }}
      >
        {siteCreateError && <OrgModulePlaceholder intent={siteCreateError} />}
        <SiteForm
          initialValues={createSiteInitialValues}
          saving={siteCreateSaving}
          submitLabel="Create Site"
          onSubmit={handleCreateSite}
          onCancel={() => {
            setSiteDrawer(null);
            setSiteCreateError(null);
            setSiteCreateSaving(false);
          }}
        />
      </Drawer>

      <Drawer
        open={Boolean(clusterDrawer?.clusterId)}
        title={clusterDetail.record?.name ?? clustersState.items.find((cluster) => cluster.id === clusterDrawer?.clusterId)?.name ?? 'Cluster detail'}
        subtitle={clusterDetail.record?.id ?? clusterDrawer?.clusterId ?? undefined}
        onClose={() => {
          setClusterDrawer(null);
          setClusterDetail(createInitialClusterDetailState());
        }}
        width="wide"
        actions={clusterDrawerActions}
      >
        {clusterDrawer?.clusterId ? (
          <>
            <Tabs items={clusterDetailTabs} activeId={clusterDetail.activeTab} onChange={(id) => setClusterDetailTab(id as ClusterDetailTab)} />
            <div className="detail-body">{renderClusterDetailContent()}</div>
          </>
        ) : (
          <OrgModulePlaceholder intent="Select a connector cluster to view detail." />
        )}
      </Drawer>

      <Drawer
        open={Boolean(connectorDrawer?.connectorId)}
        title={connectorDetail.record?.name ?? connectorsState.items.find((connector) => connector.id === connectorDrawer?.connectorId)?.name ?? 'Connector detail'}
        subtitle={connectorDetail.record?.id ?? connectorDrawer?.connectorId ?? undefined}
        onClose={() => {
          setConnectorDrawer(null);
          setConnectorDetail(createInitialConnectorDetailState());
        }}
        actions={connectorDrawerActions}
      >
        {connectorDrawer?.connectorId ? (
          <>
            <Tabs items={connectorDetailTabs} activeId={connectorDetail.activeTab} onChange={(id) => setConnectorDetailTab(id as ConnectorDetailTab)} />
            <div className="detail-body">{renderConnectorDetailContent()}</div>
          </>
        ) : (
          <OrgModulePlaceholder intent="Select a connector to view detail." />
        )}
      </Drawer>

      <Drawer
        open={Boolean(clusterDrawer && !clusterDrawer.clusterId)}
        title="Create Cluster"
        onClose={() => {
          setClusterDrawer(null);
          setClusterCreateError(null);
          setClusterCreateSaving(false);
        }}
      >
        {clusterCreateError && <OrgModulePlaceholder intent={clusterCreateError} />}
        <ClusterForm
          initialValues={clusterToFormValues(null)}
          saving={clusterCreateSaving}
          submitLabel="Create Cluster"
          onSubmit={handleCreateCluster}
          onCancel={() => {
            setClusterDrawer(null);
            setClusterCreateError(null);
            setClusterCreateSaving(false);
          }}
        />
      </Drawer>

      <Drawer
        open={Boolean(connectorDrawer && !connectorDrawer.connectorId)}
        title="Create Connector"
        onClose={() => {
          setConnectorDrawer(null);
          setConnectorCreateError(null);
          setConnectorCreateSaving(false);
        }}
      >
        {connectorCreateError && <OrgModulePlaceholder intent={connectorCreateError} />}
        <ConnectorForm
          initialValues={connectorToFormValues(
            connectorDrawer?.clusterId ? { clusterId: connectorDrawer.clusterId } : null
          )}
          clusterOptions={createConnectorClusterOptions}
          clusterSelectionLocked={connectorClusterLocked}
          saving={connectorCreateSaving}
          submitLabel="Create Connector"
          onSubmit={handleCreateConnector}
          onCancel={() => {
            setConnectorDrawer(null);
            setConnectorCreateError(null);
            setConnectorCreateSaving(false);
          }}
        />
      </Drawer>

      <Drawer
        open={Boolean(awsTelemetryDrawer?.clusterId)}
        title="Deploy AWS telemetry stack"
        subtitle={awsTelemetryDrawer?.clusterName ?? awsTelemetryDrawer?.clusterId ?? undefined}
        onClose={() => {
          setAwsTelemetryDrawer(null);
        }}
        width="wide"
      >
        {awsTelemetryDrawer?.clusterId ? (
          <div className="aws-telemetry-embed" style={{ minHeight: '70vh' }}>
            <AwsTelemetryLauncher
              initialClusterId={awsTelemetryDrawer.clusterId}
              initialClusterName={awsTelemetryDrawer.clusterName}
              initialRegion={awsTelemetryDrawer.initialRegion}
              onCancel={() => {
                setAwsTelemetryDrawer(null);
              }}
            />
          </div>
        ) : (
          <OrgModulePlaceholder intent="Select a cluster to launch AWS telemetry." />
        )}
      </Drawer>

      <DiscoveryJobDrawer
        open={jobDrawerOpen}
        mode={jobDrawerMode}
        jobId={jobDrawerJobId}
        orgId={orgId}
        initialSiteId={siteDetail.record?.id ?? null}
        lockSite={jobDrawerMode === 'create' && Boolean(siteDetail.record?.id)}
        discoveryApi={discoveryApi}
        sites={sitesState.items.map(toSdkSite)}
        clusters={clustersState.items.map(toSdkCluster)}
        onClose={closeJobDrawer}
        onCreated={handleJobCreated}
        onUpdated={handleJobUpdated}
      />
      <DiscoveryRunDrawer
        open={runDrawerOpen}
        orgId={orgId}
        discoveryApi={discoveryApi}
        sitesApi={sitesApi}
        jobs={siteDiscoveryJobs}
        jobId={selectedSiteJob?.id ?? null}
        siteNames={siteNames}
        sites={sitesState.items.map(toSdkSite)}
        onClose={() => setRunDrawerOpen(false)}
        onStarted={handleRunStarted}
      />
      <DiscoveryRunDetailDrawer
        open={runDetailOpen}
        orgId={orgId}
        job={selectedSiteJob}
        run={selectedSiteRun}
        siteNames={siteNames}
        onClose={handleCloseRunDetail}
        onStopRun={() => void handleStopRun(selectedSiteRun)}
        stopInProgress={Boolean(selectedSiteRun?.id) && runActionBusyId === selectedSiteRun?.id}
        onRefreshRuns={handleRefreshRuns}
        onRefreshJobs={handleRefreshJobs}
      />
      <DeleteConfirmationModal
        open={pendingDeleteJobs != null}
        title={pendingDeleteJobs?.length === 1 ? 'Delete discovery job?' : `Delete ${pendingDeleteJobs?.length ?? 0} discovery jobs?`}
        description="This permanently removes the selected discovery job configuration."
        deleting={jobActionBusyId === 'bulk-delete'}
        onCancel={() => setPendingDeleteJobs(null)}
        onConfirm={() => void confirmDeleteJobs()}
      />
      <DeleteConfirmationModal
        open={pendingResourceDelete != null}
        title={
          pendingResourceDelete
            ? `Delete ${pendingResourceDelete.rows.length} ${pendingResourceDelete.kind === 'sites' ? 'site' : pendingResourceDelete.kind === 'clusters' ? 'cluster' : 'connector'}${pendingResourceDelete.rows.length === 1 ? '' : 's'}?`
            : 'Delete selected resources?'
        }
        description="This permanently removes the selected connectivity configuration."
        deleting={resourceDeleteBusy}
        onCancel={() => setPendingResourceDelete(null)}
        onConfirm={() => void confirmResourceDelete()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

export default ConnectivitySitesPage;




const TOPOLOGY_ICON_BY_CLASS: Record<V1TopologyDeviceClass, string> = {
  switch: '/assets/topology-svg/switch.svg',
  router: '/assets/topology-svg/router.svg',
  firewall: '/assets/topology-svg/firewall.svg',
  gateway: '/assets/topology-svg/gateway.svg',
  wan_edge: '/assets/topology-svg/wan-edge.svg',
  wireless: '/assets/topology-svg/access-point.svg',
  'wireless-controller': '/assets/topology-svg/access-point.svg',
  load_balancer: '/assets/topology-svg/load-balancer.svg',
  vpn: '/assets/topology-svg/vpn.svg',
  server: '/assets/topology-svg/server.svg',
  storage: '/assets/topology-svg/storage.svg',
  cloud: '/assets/topology-svg/cloud.svg',
  endpoint: '/assets/topology-svg/server.svg',
  generic: '/assets/topology-svg/generic-device.svg'
};

const V1_TOPOLOGY_DEVICE_CLASSES = new Set<V1TopologyDeviceClass>([
  'switch', 'router', 'firewall', 'gateway', 'wan_edge', 'wireless', 'wireless-controller',
  'load_balancer', 'vpn', 'server', 'storage', 'cloud', 'endpoint', 'generic'
]);

const DEFAULT_V1_TOPOLOGY_PRESENTATION: V1TopologyClassification = {
  tier: 'unknown',
  tierRank: 7,
  deviceClass: 'generic'
};

const resolveCssVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value && value.trim().length > 0 ? value.trim() : fallback;
};

const createCytoscapeStyle = (): NonNullable<CytoscapeOptions['style']> => {
  const deviceText = resolveCssVar('--text-primary', '#0f172a');
  const textHalo = resolveCssVar('--surface-panel', '#ffffff');
  const edgeColor = resolveCssVar('--border-strong', '#64748b');
  const edgeConnected = resolveCssVar('--surface-accent', '#6366f1');
  const edgeHighlight = resolveCssVar('--surface-accent-strong', '#4f46e5');
  const tooltipBg = resolveCssVar('--surface-panel', '#ffffff');

  return [
    {
      selector: 'node[type="device"]',
      style: {
        shape: 'rectangle',
        'background-color': 'transparent',
        'background-opacity': 0,
        'background-image': 'data(icon)',
        'background-fit': 'contain',
        'background-repeat': 'no-repeat',
        'background-width': '96%',
        'background-height': '92%',
        'background-position-y': '50%',
        'border-width': 0,
        width: 'data(width)',
        height: 'data(height)',
        label: 'data(label)',
        color: deviceText,
        'font-size': '18px',
        'font-weight': 700,
        'font-family': '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        'text-wrap': 'wrap',
        'text-max-width': 'data(labelWidth)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 28,
        'text-outline-width': 3,
        'text-outline-color': textHalo,
        padding: '0px'
      }
    },
    {
      selector: 'edge',
      style: {
        width: '3px',
        'line-color': edgeColor,
        'target-arrow-shape': 'none',
        'source-endpoint': 'outside-to-node',
        'target-endpoint': 'outside-to-node',
        'curve-style': 'straight',
        color: resolveCssVar('--text-primary', '#0f172a'),
        'font-size': '11px',
        'text-background-color': tooltipBg,
        'text-background-opacity': 0.86,
        'text-background-padding': '3px',
        'text-wrap': 'wrap',
        opacity: 0.8
      }
    },
    {
      selector: 'edge.topology-edge--parallel-interface',
      style: {
        'curve-style': 'unbundled-bezier',
        'control-point-distances': 'data(routeOffset)',
        'control-point-weight': 0.55,
        opacity: 0.78
      }
    },
    {
      selector: 'edge.topology-edge--same-row-peer',
      style: {
        'curve-style': 'unbundled-bezier',
        'control-point-distances': 'data(routeOffset)',
        'control-point-weight': 0.5
      }
    },
    {
      selector: 'edge.is-connected',
      style: {
        'line-color': edgeConnected,
        width: '4px',
        opacity: 0.98
      }
    },
    {
      selector: 'edge.is-highlight',
      style: {
        'line-color': edgeHighlight,
        width: '4px',
        opacity: 1
      }
    }
  ];
};

interface DeviceMetadata {
  id: string;
  canonicalId?: string;
  label: string;
  vendor?: string;
  model?: string;
  role?: string;
  serial?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  mgmtAddress?: string;
  presentation: V1TopologyClassification;
}

interface DeviceTooltipData {
  id: string;
  canonicalId?: string;
  label: string;
  vendor?: string;
  model?: string;
  role?: string;
  serial?: string;
  osName?: string;
  osVersion?: string;
  mgmtAddress?: string;
}

interface InterfaceLinkMetadata {
  id: string;
  fromDeviceId: string;
  toDeviceId: string;
  fromLabel: string;
  toLabel: string;
  fromDeviceLabel: string;
  toDeviceLabel: string;
}

interface DeviceEdgeMetadata {
  id: string;
  fromDeviceId: string;
  toDeviceId: string;
  fromDeviceLabel: string;
  toDeviceLabel: string;
  properties?: Record<string, unknown> | null;
}

interface DeviceLinkSummary {
  id: string;
  deviceAId: string;
  deviceBId: string;
  deviceALabel: string;
  deviceBLabel: string;
  connections: Array<{
    fromLabel: string;
    toLabel: string;
    fromDeviceLabel: string;
    toDeviceLabel: string;
  }>;
}

interface EdgeDetail {
  id: string;
  detail?: string;
  connections?: Array<{ fromLabel: string; toLabel: string; fromDeviceLabel: string; toDeviceLabel: string }>;
}

interface TopologyDiagramProps {
  elements: ElementDefinition[];
  layoutKey: string;
  mode?: 'embedded' | 'windowed';
  manualPositions: Record<string, { x: number; y: number }>;
  onNodePositionChange: (nodeId: string, position: { x: number; y: number }) => void;
  onEdgeFocus: (detail: EdgeDetail | null, renderedPosition?: { x: number; y: number }) => void;
  onDeviceFocus: (detail: DeviceTooltipData | null, renderedPosition?: { x: number; y: number }) => void;
}

const TopologyDiagram = ({
  elements,
  layoutKey,
  mode = 'embedded',
  manualPositions,
  onNodePositionChange,
  onEdgeFocus,
  onDeviceFocus
}: TopologyDiagramProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const edgePinnedRef = useRef(false);
  const devicePinnedRef = useRef(false);
  const hoveredDeviceIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      layout: { name: 'preset' },
      style: createCytoscapeStyle(),
      wheelSensitivity: 0.2,
      boxSelectionEnabled: false,
      userZoomingEnabled: true,
      userPanningEnabled: true
    });
    const fitPadding = mode === 'windowed' ? 120 : 60;
    cy.fit(undefined, fitPadding);
    const containerRect = containerRef.current.getBoundingClientRect();
    const bbox = cy.elements().boundingBox();
    const currentZoom = cy.zoom();
    const targetFill = mode === 'windowed' ? 0.6 : 0.72;
    const desiredWidth = containerRect.width * targetFill;
    const desiredHeight = containerRect.height * targetFill;
    const widthRatio = bbox.w * currentZoom > 0 ? desiredWidth / (bbox.w * currentZoom) : 1;
    const heightRatio = bbox.h * currentZoom > 0 ? desiredHeight / (bbox.h * currentZoom) : 1;
    const ratio = Math.min(widthRatio, heightRatio);
    if (ratio > 1) {
      cy.zoom({
        level: currentZoom * Math.min(ratio, 1.45),
        renderedPosition: { x: containerRect.width / 2, y: containerRect.height / 2 }
      });
    }

    const setPanningCursor = (active: boolean) => {
      if (!containerRef.current) {
        return;
      }
      containerRef.current.classList.toggle('is-panning', active);
    };

    cy.on('mousedown', (event) => {
      if (event.target === cy) {
        setPanningCursor(true);
      }
    });
    cy.on('mouseup', () => setPanningCursor(false));
    cy.on('tapend', () => setPanningCursor(false));

    cy.on('dragfree', 'node', (event) => {
      const node = event.target;
      onNodePositionChange(node.id(), node.position());
      setPanningCursor(false);
    });

    const projectPosition = (renderedPosition?: { x: number; y: number }) => {
      if (!renderedPosition) {
        return undefined;
      }
      return {
        x: renderedPosition.x,
        y: renderedPosition.y
      };
    };

    const emitEdgeDetail = (target: cytoscape.EdgeSingular, renderedPosition?: { x: number; y: number }) => {
      const data = target.data() as EdgeDetail;
      onDeviceFocus(null);
      onEdgeFocus(
        {
          id: data.id,
          detail: data.detail,
          connections: data.connections
        },
        renderedPosition
      );
    };

    const emitDeviceDetail = (target: cytoscape.NodeSingular, renderedPosition?: { x: number; y: number }) => {
      const data = target.data() as { id: string; label?: string; metadata?: DeviceTooltipData };
      const base: DeviceTooltipData = {
        id: data.id,
        canonicalId: data.metadata?.canonicalId ?? stripGraphNamespace(data.id),
        label: typeof data.label === 'string' ? data.label : data.metadata?.label ?? data.id,
        vendor: data.metadata?.vendor,
        model: data.metadata?.model,
        role: data.metadata?.role,
        serial: data.metadata?.serial,
        osName: data.metadata?.osName,
        osVersion: data.metadata?.osVersion,
        mgmtAddress: data.metadata?.mgmtAddress
      };
      onEdgeFocus(null);
      onDeviceFocus(base, renderedPosition);
    };

    const clearConnectedHighlights = () => {
      cy.edges('.is-connected').removeClass('is-connected');
      hoveredDeviceIdRef.current = null;
    };

    const setConnectedHighlights = (node: cytoscape.NodeSingular) => {
      const nodeId = node.id();
      if (hoveredDeviceIdRef.current === nodeId) {
        return;
      }
      clearConnectedHighlights();
      node.connectedEdges().addClass('is-connected');
      hoveredDeviceIdRef.current = nodeId;
    };

    cy.on('tap', (event) => {
      if (event.target === cy) {
        edgePinnedRef.current = false;
        devicePinnedRef.current = false;
        clearConnectedHighlights();
        onEdgeFocus(null);
        onDeviceFocus(null);
      }
    });

    cy.on('tap', 'edge', (event) => {
      event.stopPropagation();
      edgePinnedRef.current = true;
      devicePinnedRef.current = false;
      const renderedPosition = projectPosition(event.renderedPosition);
      emitEdgeDetail(event.target, renderedPosition);
    });

    cy.on('tap', 'node[type="device"]', (event) => {
      event.stopPropagation();
      devicePinnedRef.current = true;
      edgePinnedRef.current = false;
      setConnectedHighlights(event.target);
      const renderedPosition = projectPosition(event.renderedPosition);
      emitDeviceDetail(event.target, renderedPosition);
    });

    cy.on('mouseover', 'edge', (event) => {
      event.target.addClass('is-highlight');
      if (edgePinnedRef.current || devicePinnedRef.current) {
        return;
      }
      const renderedPosition = projectPosition(event.renderedPosition);
      emitEdgeDetail(event.target, renderedPosition);
    });
    cy.on('mouseout', 'edge', (event) => {
      event.target.removeClass('is-highlight');
      if (edgePinnedRef.current) {
        return;
      }
      onEdgeFocus(null);
    });

    cy.on('mouseover', 'node[type="device"]', (event) => {
      setConnectedHighlights(event.target);
      if (devicePinnedRef.current || edgePinnedRef.current) {
        return;
      }
      const renderedPosition = projectPosition(event.renderedPosition);
      emitDeviceDetail(event.target, renderedPosition);
    });
    cy.on('mouseout', 'node[type="device"]', () => {
      clearConnectedHighlights();
      if (devicePinnedRef.current) {
        return;
      }
      onDeviceFocus(null);
    });

    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, layoutKey, mode, onDeviceFocus, onEdgeFocus, onNodePositionChange]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }
    cy.batch(() => {
      Object.entries(manualPositions).forEach(([id, position]) => {
        const node = cy.getElementById(id);
        if (!node.empty()) {
          node.position(position);
        }
      });
    });
  }, [manualPositions]);

  return <div className="topology-diagram__canvas topology-canvas" ref={containerRef} />;
};

export function buildCytoscapeElements(
  topology: TopologyGraph | null,
  topologyDevicesById: Record<string, Device>,
  showInterfaces: boolean
): ElementDefinition[] {
  if (!topology) {
    return [];
  }
  const nodes = topology.nodes ?? [];
  const edges = topology.edges ?? [];
  const nodeIndex = new Map<string, (typeof nodes)[number]>();
  nodes.forEach((node, idx) => {
    const id = typeof node?.id === 'string' ? node.id : `node-${idx}`;
    nodeIndex.set(id, { ...node, id });
  });

  const devices: V1TopologyDevice[] = [];
  const deviceMap = new Map<string, DeviceMetadata>();
  nodes.forEach((node, idx) => {
    if ((node.kind ?? '').toLowerCase() !== 'device') {
      return;
    }
    const id = typeof node.id === 'string' ? node.id : `device-${idx}`;
    const properties = (node.properties ?? {}) as Record<string, unknown>;
    const presentationPayload = (properties.topologyPresentation ?? {}) as Record<string, unknown>;
    const rawDeviceClass = toCleanString(presentationPayload.deviceClass);
    const rawRank = presentationPayload.tierRank;
    const rank = typeof rawRank === 'number' ? rawRank : Number.NaN;
    const presentation: V1TopologyClassification = {
      tier: toCleanString(presentationPayload.tier) ?? DEFAULT_V1_TOPOLOGY_PRESENTATION.tier,
      tierRank: Number.isFinite(rank) && rank >= 0 ? rank : DEFAULT_V1_TOPOLOGY_PRESENTATION.tierRank,
      deviceClass: rawDeviceClass && V1_TOPOLOGY_DEVICE_CLASSES.has(rawDeviceClass as V1TopologyDeviceClass)
        ? (rawDeviceClass as V1TopologyDeviceClass)
        : DEFAULT_V1_TOPOLOGY_PRESENTATION.deviceClass
    };
    const canonicalId =
      typeof (node.ref as { id?: string } | undefined)?.id === 'string'
        ? ((node.ref as { id?: string }).id as string)
        : stripGraphNamespace(id);
    const inventoryDevice = canonicalId ? topologyDevicesById[canonicalId] : undefined;
    const roleFromLabels = resolveRoleFromLabels(inventoryDevice?.labels);
    const label =
      inventoryDevice?.displayName ??
      inventoryDevice?.hostname ??
      node.label ??
      inventoryDevice?.mgmtAddress ??
      id;
    const metadataPayload: Record<string, unknown> = {
      id,
      deviceId: canonicalId,
      hostname: inventoryDevice?.hostname,
      displayName: inventoryDevice?.displayName ?? label,
      model: inventoryDevice?.model
    };
    const metadata: DeviceMetadata = {
      id,
      canonicalId,
      label,
      vendor: inventoryDevice?.vendor !== undefined ? String(inventoryDevice.vendor) : undefined,
      model: sanitizeDeviceModel(metadataPayload.model, metadataPayload),
      role:
        inventoryDevice?.role ??
        roleFromLabels ??
        toCleanString(properties['device.role']) ??
        toCleanString(properties.role) ??
        toCleanString(properties.layer),
      serial: inventoryDevice?.serial,
      hostname: inventoryDevice?.hostname,
      osName: getDeviceOsName(inventoryDevice),
      osVersion: inventoryDevice?.osVersion,
      mgmtAddress: inventoryDevice?.mgmtAddress,
      presentation
    };
    deviceMap.set(id, metadata);
    devices.push({ id: metadata.id, label: metadata.label, presentation });
  });
  if (devices.length === 0) {
    return [];
  }

  const interfaceParents = new Map<string, { deviceId: string; label: string }>();
  const interfaceNodes = nodes.filter((node) => (node.kind ?? '').toLowerCase() === 'interface');
  interfaceNodes.forEach((node, idx) => {
    const id = typeof node.id === 'string' ? node.id : `interface-${idx}`;
    const parentDeviceId = typeof node.parentId === 'string' ? node.parentId : '';
    if (!parentDeviceId || !deviceMap.has(parentDeviceId)) {
      return;
    }
    interfaceParents.set(id, {
      deviceId: parentDeviceId,
      label: node.label ?? id
    });
  });

  const adjacency = new Map<string, Set<string>>();
  const ensureLink = (from: string, to: string) => {
    if (!from || !to || from === to) {
      return;
    }
    const sourceNeighbors = adjacency.get(from) ?? new Set<string>();
    sourceNeighbors.add(to);
    adjacency.set(from, sourceNeighbors);
    const targetNeighbors = adjacency.get(to) ?? new Set<string>();
    targetNeighbors.add(from);
    adjacency.set(to, targetNeighbors);
  };

  const interfaceLinks: InterfaceLinkMetadata[] = [];
  const directDeviceEdges: DeviceEdgeMetadata[] = [];
  edges.forEach((edge, index) => {
    const fromId = edge.fromId ?? '';
    const toId = edge.toId ?? '';
    const fromPort = fromId ? interfaceParents.get(fromId) : undefined;
    const toPort = toId ? interfaceParents.get(toId) : undefined;
    if (fromPort && toPort && fromId && toId) {
      const edgeProps = ((edge as unknown) as { properties?: Record<string, unknown> }).properties ?? {};
      const rawEdgeId =
        typeof (edge as unknown as { id?: string }).id === 'string'
          ? (edge as unknown as { id?: string }).id
          : typeof edgeProps.mockId === 'string'
            ? (edgeProps.mockId as string)
            : undefined;
      const fromLabel = typeof edgeProps.fromLabel === 'string' ? (edgeProps.fromLabel as string) : fromPort.label;
      const toLabel = typeof edgeProps.toLabel === 'string' ? (edgeProps.toLabel as string) : toPort.label;
      interfaceLinks.push({
        id: rawEdgeId ?? `interface-link-${index}`,
        fromDeviceId: fromPort.deviceId,
        toDeviceId: toPort.deviceId,
        fromLabel,
        toLabel,
        fromDeviceLabel: deviceMap.get(fromPort.deviceId)?.label ?? fromPort.deviceId,
        toDeviceLabel: deviceMap.get(toPort.deviceId)?.label ?? toPort.deviceId
      });
      ensureLink(fromPort.deviceId, toPort.deviceId);
      return;
    }
    const fromNode = edge.fromId ? nodeIndex.get(edge.fromId) : undefined;
    const toNode = edge.toId ? nodeIndex.get(edge.toId) : undefined;
    if ((fromNode?.kind ?? '').toLowerCase() === 'device' && (toNode?.kind ?? '').toLowerCase() === 'device' && edge.fromId && edge.toId) {
      const edgeProps = ((edge as unknown) as { properties?: Record<string, unknown> }).properties ?? {};
      const rawEdgeId =
        typeof (edge as unknown as { id?: string }).id === 'string'
          ? (edge as unknown as { id?: string }).id
          : typeof edgeProps.mockId === 'string'
            ? (edgeProps.mockId as string)
            : undefined;
      directDeviceEdges.push({
        id: rawEdgeId ?? `device-edge-${index}`,
        fromDeviceId: edge.fromId,
        toDeviceId: edge.toId,
        fromDeviceLabel: deviceMap.get(edge.fromId)?.label ?? (fromNode?.label ?? edge.fromId),
        toDeviceLabel: deviceMap.get(edge.toId)?.label ?? (toNode?.label ?? edge.toId),
        properties: edge.properties ?? null
      });
      ensureLink(edge.fromId, edge.toId);
    }
  });

  const layout = computeV1TopologyLayout(devices, adjacency);
  const positions = layout.positions;
  const elements: ElementDefinition[] = devices.map((device) => {
    const metadata = deviceMap.get(device.id);
    const height = layout.nodeHeight + (showInterfaces ? 24 : 0);
    const classification: V1TopologyClassification =
      layout.classifications.get(device.id) ?? device.presentation;
    return {
      data: {
        id: device.id,
        label: device.label,
        type: 'device',
        icon: TOPOLOGY_ICON_BY_CLASS[classification.deviceClass] ?? TOPOLOGY_ICON_BY_CLASS.generic,
        tier: classification.tier,
        deviceClass: classification.deviceClass,
        width: layout.nodeWidth,
        height,
        labelWidth: layout.nodeWidth + 18,
        metadata: {
          id: device.id,
          canonicalId: metadata?.canonicalId ?? stripGraphNamespace(device.id),
          label: device.label,
          vendor: metadata?.vendor,
          model: metadata?.model,
          role: metadata?.role,
          serial: metadata?.serial,
          osName: metadata?.osName,
          osVersion: metadata?.osVersion,
          mgmtAddress: metadata?.mgmtAddress
        }
      },
      position: positions.get(device.id) ?? { x: 0, y: 0 },
      grabbable: true,
      selectable: true
    };
  });

  if (showInterfaces && interfaceLinks.length > 0) {
    const sortedInterfaceLinks = [...interfaceLinks].sort(
      (a, b) =>
        a.fromDeviceId.localeCompare(b.fromDeviceId) ||
        a.toDeviceId.localeCompare(b.toDeviceId) ||
        a.id.localeCompare(b.id)
    );
    const pairKey = (from: string, to: string) => [from, to].sort().join('::');
    const linksPerPair = new Map<string, number>();
    sortedInterfaceLinks.forEach((link) => {
      const key = pairKey(link.fromDeviceId, link.toDeviceId);
      linksPerPair.set(key, (linksPerPair.get(key) ?? 0) + 1);
    });
    sortedInterfaceLinks
      .forEach((link, index) => {
        const edgePresentation = v1TopologyEdgePresentation(
          link.fromDeviceId,
          link.toDeviceId,
          layout.levels,
          positions,
          index
        );
        elements.push({
          classes: `${edgePresentation.classes}${
            (linksPerPair.get(pairKey(link.fromDeviceId, link.toDeviceId)) ?? 0) > 1
              ? ' topology-edge--parallel-interface'
              : ''
          }`,
          data: {
            id: link.id ?? `interface-edge-${index}`,
            source: link.fromDeviceId,
            target: link.toDeviceId,
            label: '',
            detail: `${link.fromLabel} ⇄ ${link.toLabel}`,
            connections: [
              {
                fromLabel: link.fromLabel,
                toLabel: link.toLabel,
                fromDeviceLabel: link.fromDeviceLabel,
                toDeviceLabel: link.toDeviceLabel
              }
            ],
            type: 'interface',
            routeOffset: edgePresentation.routeOffset
          }
        });
      });
  } else {
    const aggregated =
      interfaceLinks.length > 0 ? aggregateDeviceLinks(interfaceLinks) : aggregateDirectDeviceEdges(directDeviceEdges);
    aggregated
      .sort((a, b) => a.deviceAId.localeCompare(b.deviceAId) || a.deviceBId.localeCompare(b.deviceBId))
      .forEach((summary, index) => {
        const detail = summary.connections.map((connection) => `${connection.fromLabel} ⇄ ${connection.toLabel}`).join('\\n');
        const edgePresentation = v1TopologyEdgePresentation(
          summary.deviceAId,
          summary.deviceBId,
          layout.levels,
          positions,
          index
        );
        elements.push({
          classes: edgePresentation.classes,
          data: {
            id: summary.id,
            source: summary.deviceAId,
            target: summary.deviceBId,
            label: summary.connections.length > 1 ? `${summary.connections.length} links` : '',
            detail,
            connections: summary.connections,
            type: 'device',
            routeOffset: edgePresentation.routeOffset
          }
        });
      });
  }

  return elements;
}

function resolveRoleFromLabels(labels?: Record<string, string>): string | undefined {
  if (!labels) {
    return undefined;
  }
  const roleKey = Object.keys(labels).find((key) => ['role', 'device_role', 'tier', 'device_tier', 'function'].includes(key.toLowerCase()));
  if (!roleKey) {
    return undefined;
  }
  const value = labels[roleKey];
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function aggregateDeviceLinks(interfaceLinks: InterfaceLinkMetadata[]): DeviceLinkSummary[] {
  const map = new Map<string, DeviceLinkSummary>();
  interfaceLinks.forEach((link) => {
    const deviceAId = link.fromDeviceId < link.toDeviceId ? link.fromDeviceId : link.toDeviceId;
    const deviceBId = deviceAId === link.fromDeviceId ? link.toDeviceId : link.fromDeviceId;
    const key = `${deviceAId}::${deviceBId}`;
    const deviceALabel = deviceAId === link.fromDeviceId ? link.fromDeviceLabel : link.toDeviceLabel;
    const deviceBLabel = deviceAId === link.fromDeviceId ? link.toDeviceLabel : link.fromDeviceLabel;

    let summary = map.get(key);
    if (!summary) {
      summary = {
        id: `device-link-${deviceAId}-${deviceBId}`,
        deviceAId,
        deviceBId,
        deviceALabel,
        deviceBLabel,
        connections: []
      };
      map.set(key, summary);
    }

    const fromLabel = deviceAId === link.fromDeviceId ? link.fromLabel : link.toLabel;
    const toLabel = deviceAId === link.fromDeviceId ? link.toLabel : link.fromLabel;
    const existing = summary.connections.some(
      (connection) => connection.fromLabel === fromLabel && connection.toLabel === toLabel
    );
    if (!existing) {
      summary.connections.push({
        fromLabel,
        toLabel,
        fromDeviceLabel: summary.deviceALabel,
        toDeviceLabel: summary.deviceBLabel
      });
    }
  });

  return Array.from(map.values());
}

function aggregateDirectDeviceEdges(edges: DeviceEdgeMetadata[]): DeviceLinkSummary[] {
  const map = new Map<string, DeviceLinkSummary>();
  edges.forEach((edge) => {
    const deviceAId = edge.fromDeviceId < edge.toDeviceId ? edge.fromDeviceId : edge.toDeviceId;
    const deviceBId = deviceAId === edge.fromDeviceId ? edge.toDeviceId : edge.fromDeviceId;
    const key = `${deviceAId}::${deviceBId}`;
    const deviceALabel = deviceAId === edge.fromDeviceId ? edge.fromDeviceLabel : edge.toDeviceLabel;
    const deviceBLabel = deviceAId === edge.fromDeviceId ? edge.toDeviceLabel : edge.fromDeviceLabel;

    let summary = map.get(key);
    if (!summary) {
      summary = {
        id: `device-edge-${deviceAId}-${deviceBId}`,
        deviceAId,
        deviceBId,
        deviceALabel,
        deviceBLabel,
        connections: []
      };
      map.set(key, summary);
    }

    const edgeConnections = Array.isArray(edge.properties?.connections)
      ? (edge.properties?.connections as Array<{ fromLabel?: string; toLabel?: string }>)
      : null;
    const additions = edgeConnections && edgeConnections.length > 0
      ? edgeConnections.map((connection) => ({
          fromLabel: typeof connection.fromLabel === 'string' ? connection.fromLabel : describeEdgeConnection(edge, 'from'),
          toLabel: typeof connection.toLabel === 'string' ? connection.toLabel : describeEdgeConnection(edge, 'to')
        }))
      : [{ fromLabel: describeEdgeConnection(edge, 'from'), toLabel: describeEdgeConnection(edge, 'to') }];

    additions.forEach(({ fromLabel, toLabel }) => {
      const orientedFromLabel = deviceAId === edge.fromDeviceId ? fromLabel : toLabel;
      const orientedToLabel = deviceAId === edge.fromDeviceId ? toLabel : fromLabel;
      const exists = summary.connections.some(
        (connection) => connection.fromLabel === orientedFromLabel && connection.toLabel === orientedToLabel
      );
      if (!exists) {
        summary.connections.push({
          fromLabel: orientedFromLabel,
          toLabel: orientedToLabel,
          fromDeviceLabel: summary.deviceALabel,
          toDeviceLabel: summary.deviceBLabel
        });
      }
    });
  });

  return Array.from(map.values());
}

function describeEdgeConnection(edge: DeviceEdgeMetadata, direction: 'from' | 'to'): string {
  const properties = edge.properties ?? {};
  const protocol = typeof properties?.protocol === 'string' ? properties.protocol : null;
  const remoteId = typeof properties?.remoteId === 'string' ? properties.remoteId : null;
  if (protocol && remoteId) {
    return `${protocol} → ${remoteId}`;
  }
  if (protocol) {
    return protocol;
  }
  if (remoteId) {
    return remoteId;
  }
  return direction === 'from' ? 'link' : 'peer';
}

function stripGraphNamespace(id?: string | null): string | undefined {
  if (!id) {
    return undefined;
  }
  const delimiter = id.indexOf(':');
  return delimiter === -1 ? id : id.slice(delimiter + 1);
}
interface LabelsEditorProps {
  values: KeyValuePair[];
  onChange: (next: KeyValuePair[]) => void;
}

const LabelsEditor = ({ values, onChange }: LabelsEditorProps) => {
  const handlePairChange = (index: number, key: keyof KeyValuePair, value: string) => {
    const next = values.map((pair, pairIndex) => (pairIndex === index ? { ...pair, [key]: value } : pair));
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...values, { key: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(values.filter((_, pairIndex) => pairIndex !== index));
  };

  return (
    <div className="labels-editor">
      {values.length === 0 && <p className="helper-text">No labels configured.</p>}
      {values.map((pair, index) => (
        <div key={`label-${index}`} className="label-row">
          <input
            type="text"
            placeholder="key"
            value={pair.key}
            onChange={(event) => handlePairChange(index, 'key', event.target.value)}
          />
          <input
            type="text"
            placeholder="value"
            value={pair.value}
            onChange={(event) => handlePairChange(index, 'value', event.target.value)}
          />
          <button type="button" className="link-button" onClick={() => handleRemove(index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="link-button" onClick={handleAdd}>
        + Add label
      </button>
    </div>
  );
};

interface SiteFormProps {
  initialValues: SiteFormValues;
  saving: boolean;
  submitLabel: string;
  onSubmit: (values: SiteFormValues) => void;
  onCancel?: () => void;
  showActions?: boolean;
  formRef?: RefObject<HTMLFormElement>;
}

interface SitePingMeshEndpointFormProps {
  siteId: string;
  siteType?: string;
  initialEndpoint: string;
  saving: boolean;
  onSubmit: (endpoint: string) => Promise<void>;
  searchPathEndpoints: (request: TopologyPathEndpointSearchRequest, signal: AbortSignal) => Promise<TopologyPathEndpointSearchResponse>;
}

const isCloudSiteType = (siteType: string | undefined): boolean =>
  ['awscloud', 'azurecloud'].includes(siteType?.trim().toLowerCase() ?? '');

const SitePingMeshEndpointForm = ({ siteId, siteType, initialEndpoint, saving, onSubmit, searchPathEndpoints }: SitePingMeshEndpointFormProps) => {
  const [endpoint, setEndpoint] = useState(initialEndpoint);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const hasSavedEndpoint = initialEndpoint.trim().length > 0;
  const suggestionsEnabled = !isCloudSiteType(siteType);

  useEffect(() => {
    setEndpoint(initialEndpoint);
    setValidationError(null);
    setEditing(false);
  }, [initialEndpoint]);

  const buildSearchRequest = useCallback((query: string): TopologyPathEndpointSearchRequest => {
    const context = {
      launchSurface: TopologyPathRequestContextschemaLaunchSurfaceEnum.PingMesh,
      siteHints: [siteId],
      siteType: siteType?.trim() || 'OnPrem'
    };
    return query
      ? {
          side: 'source',
          query,
          requestedMode: TopologyPathModeValues.L3Reachability,
          allowCrossSiteFallback: false,
          limit: 12,
          context
        }
      : {
          side: 'source',
          zeroStateMode: 'bounded_defaults',
          requestedMode: TopologyPathModeValues.L3Reachability,
          allowCrossSiteFallback: false,
          limit: 12,
          context
        };
  }, [siteId, siteType]);

  const selectEndpoint = useCallback((item: TopologyPathEndpointSearchResponse['items'][number]) => {
    const referenceText = typeof item.reference?.text === 'string' ? item.reference.text.trim() : '';
    const ip = referenceText || item.displayName.trim();
    setEndpoint(ip);
    setValidationError(null);
  }, []);

  const confirmReset = useCallback(async () => {
    await onSubmit('');
    setResetConfirmationOpen(false);
  }, [onSubmit]);

  const cancelEditing = useCallback(() => {
    setEndpoint(initialEndpoint);
    setValidationError(null);
    setEditing(false);
  }, [initialEndpoint]);

  return (
    <section className="section-card site-ping-mesh-endpoint">
      <header className="section-card__header">
        <div>
          <h3 className="section-card__title">Site ping endpoint IP</h3>
          {!hasSavedEndpoint && (
            <p className="section-card__subtitle">Configure an IP address for this site to include it in Ping Mesh monitoring.</p>
          )}
        </div>
      </header>
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmedEndpoint = endpoint.trim();
          if (trimmedEndpoint && !isPingMeshIPAddress(trimmedEndpoint)) {
            setValidationError('Enter a valid IPv4 or IPv6 address.');
            return;
          }
          onSubmit(endpoint);
        }}
      >
        <label className="form-field">
          <PingMeshEndpointAutocomplete
            side="source"
            label="Site ping endpoint IP"
            placeholder="10.20.30.8"
            value={endpoint}
            disabled={saving || !siteId || !editing}
            suggestionsEnabled={suggestionsEnabled}
            showLabel={false}
            inputClassName="input"
            buildRequest={buildSearchRequest}
            search={searchPathEndpoints}
            onInputChange={(nextValue) => {
              setEndpoint(nextValue);
              setValidationError(null);
            }}
            onSelect={selectEndpoint}
          />
          {validationError && <span className="form-field__error">{validationError}</span>}
        </label>
        <div className="form-actions">
          {hasSavedEndpoint && (
            <button type="button" className="ui-button ui-button--danger" disabled={saving} onClick={() => setResetConfirmationOpen(true)}>
              Reset IP
            </button>
          )}
          {editing ? (
            <>
              <button type="button" className="ui-button ui-button--ghost" disabled={saving} onClick={cancelEditing}>
                Cancel
              </button>
              <button type="submit" className="ui-button ui-button--primary" disabled={saving}>
                {saving ? 'Saving…' : hasSavedEndpoint ? 'Update IP' : 'Save IP'}
              </button>
            </>
          ) : (
            <button type="button" className="ui-button ui-button--primary" disabled={saving || !siteId} onClick={() => setEditing(true)}>
              {hasSavedEndpoint ? 'Edit IP' : 'Configure IP'}
            </button>
          )}
        </div>
      </form>
      <DeleteConfirmationModal
        open={resetConfirmationOpen}
        title="Reset Ping Mesh endpoint?"
        description="This removes the saved Ping Mesh endpoint IP for this site."
        deleting={saving}
        confirmLabel="Reset IP"
        deletingLabel="Resetting…"
        onCancel={() => setResetConfirmationOpen(false)}
        onConfirm={() => void confirmReset()}
      />
    </section>
  );
};

const SiteForm = ({ initialValues, saving, submitLabel, onSubmit, onCancel, showActions = true, formRef }: SiteFormProps) => {
  const [values, setValues] = useState<SiteFormValues>(initialValues);
  const [locationErrors, setLocationErrors] = useState<SiteLocationFieldErrors>({});

  useEffect(() => {
    setValues(initialValues);
    setLocationErrors({});
  }, [initialValues]);

  const handleAddressChange = (key: keyof SiteFormValues['address'], value: string) => {
    setValues((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value
      }
    }));
    if (key === 'postalCode' || key === 'country') {
      setLocationErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  return (
    <form
      ref={formRef}
      className="section-card form-grid"
      onSubmit={(event) => {
        event.preventDefault();
        const errors = validateSiteLocationFields(values);
        setLocationErrors(errors);
        if (Object.keys(errors).length > 0) {
          return;
        }
        onSubmit(values);
      }}
    >
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Name</span>
          <input
            type="text"
            required
            className="input"
            value={values.name}
            onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Site type</span>
          <select
            className="input"
            value={values.siteType}
            onChange={(event) => {
              setLocationErrors({});
              const siteType = event.target.value as SiteType;
              setValues((prev) => ({
                ...prev,
                siteType,
                awsRegion: siteType === SiteSiteTypeEnum.AwsCloud ? prev.awsRegion : '',
                awsAccountId: siteType === SiteSiteTypeEnum.AwsCloud ? prev.awsAccountId : '',
                azureSubscriptionId: siteType === SiteSiteTypeEnum.AzureCloud ? prev.azureSubscriptionId : '',
                azureLocation: siteType === SiteSiteTypeEnum.AzureCloud ? prev.azureLocation : '',
                address:
                  siteType === SiteSiteTypeEnum.OnPrem
                    ? prev.address
                    : { line1: '', line2: '', city: '', state: '', postalCode: '', country: '' }
              }));
            }}
          >
            {SITE_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {values.siteType === SiteSiteTypeEnum.AwsCloud && (
          <>
            <label className="form-field">
              <span className="form-field__label">Region</span>
              <input
                type="text"
                required
                className="input"
                value={values.awsRegion ?? ''}
                onChange={(event) => setValues((prev) => ({ ...prev, awsRegion: event.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">AWS account ID</span>
              <input
                type="text"
                required
                className="input"
                value={values.awsAccountId ?? ''}
                onChange={(event) => setValues((prev) => ({ ...prev, awsAccountId: event.target.value }))}
              />
            </label>
          </>
        )}
        {values.siteType === SiteSiteTypeEnum.AzureCloud && (
          <>
            <label className="form-field">
              <span className="form-field__label">Azure location code</span>
              <input
                type="text"
                required
                className="input"
                value={values.azureLocation ?? ''}
                onChange={(event) => {
                  setValues((prev) => ({ ...prev, azureLocation: event.target.value }));
                  setLocationErrors((prev) => ({ ...prev, azureLocation: undefined }));
                }}
                placeholder="eastus"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={locationErrors.azureLocation ? 'true' : undefined}
                aria-describedby={locationErrors.azureLocation ? 'site-azure-location-error' : undefined}
              />
              {locationErrors.azureLocation && (
                <span id="site-azure-location-error" className="helper-text" role="alert">
                  {locationErrors.azureLocation}
                </span>
              )}
            </label>
            <label className="form-field">
              <span className="form-field__label">Azure subscription ID</span>
              <input
                type="text"
                required
                className="input"
                value={values.azureSubscriptionId ?? ''}
                onChange={(event) => {
                  setValues((prev) => ({ ...prev, azureSubscriptionId: event.target.value }));
                  setLocationErrors((prev) => ({ ...prev, azureSubscriptionId: undefined }));
                }}
                placeholder="12345678-1234-4abc-8def-1234567890ab"
                aria-invalid={locationErrors.azureSubscriptionId ? 'true' : undefined}
                aria-describedby={locationErrors.azureSubscriptionId ? 'site-azure-subscription-id-error' : undefined}
              />
              {locationErrors.azureSubscriptionId && (
                <span id="site-azure-subscription-id-error" className="helper-text" role="alert">
                  {locationErrors.azureSubscriptionId}
                </span>
              )}
            </label>
          </>
        )}
        {values.siteType === SiteSiteTypeEnum.OnPrem && (
          <>
            <label className="form-field">
              <span className="form-field__label">Address line 1</span>
              <input
                type="text"
                className="input"
                value={values.address.line1 ?? ''}
                onChange={(event) => handleAddressChange('line1', event.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">Address line 2</span>
              <input
                type="text"
                className="input"
                value={values.address.line2 ?? ''}
                onChange={(event) => handleAddressChange('line2', event.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">City</span>
              <input
                type="text"
                className="input"
                value={values.address.city ?? ''}
                onChange={(event) => handleAddressChange('city', event.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">State</span>
              <input
                type="text"
                className="input"
                value={values.address.state ?? ''}
                onChange={(event) => handleAddressChange('state', event.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">Postal code</span>
              <input
                type="text"
                required
                className="input"
                value={values.address.postalCode ?? ''}
                onChange={(event) => handleAddressChange('postalCode', event.target.value)}
                aria-invalid={locationErrors.postalCode ? 'true' : undefined}
                aria-describedby={locationErrors.postalCode ? 'site-postal-code-error' : undefined}
              />
              {locationErrors.postalCode && (
                <span id="site-postal-code-error" className="helper-text" role="alert">
                  {locationErrors.postalCode}
                </span>
              )}
            </label>
            <label className="form-field">
              <span className="form-field__label">Country</span>
              <input
                type="text"
                required
                className="input"
                value={values.address.country ?? ''}
                onChange={(event) => handleAddressChange('country', event.target.value)}
                aria-invalid={locationErrors.country ? 'true' : undefined}
                aria-describedby={locationErrors.country ? 'site-country-error' : undefined}
              />
              {locationErrors.country && (
                <span id="site-country-error" className="helper-text" role="alert">
                  {locationErrors.country}
                </span>
              )}
            </label>
          </>
        )}
      </div>
      <label className="form-field">
        <span className="form-field__label">Notes</span>
        <textarea
          className="textarea"
          value={values.notes ?? ''}
          onChange={(event) => setValues((prev) => ({ ...prev, notes: event.target.value }))}
          rows={4}
        />
      </label>
      <div className="form-field">
        <span className="form-field__label">Labels</span>
        <LabelsEditor values={values.labels} onChange={(next) => setValues((prev) => ({ ...prev, labels: next }))} />
      </div>
      {showActions && (
        <div className="form-actions">
          {onCancel && (
            <button type="button" className="ui-button ui-button--ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="submit" className="ui-button ui-button--primary" disabled={saving}>
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
};

interface ClusterFormProps {
  initialValues: ClusterFormValues;
  saving: boolean;
  submitLabel: string;
  onSubmit: (values: ClusterFormValues) => void;
  onCancel?: () => void;
  showActions?: boolean;
  formRef?: RefObject<HTMLFormElement>;
}

const ClusterForm = ({ initialValues, saving, submitLabel, onSubmit, onCancel, showActions = true, formRef }: ClusterFormProps) => {
  const [values, setValues] = useState<ClusterFormValues>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  return (
    <form
      ref={formRef}
      className="section-card form-grid"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      <label className="form-field">
        <span className="form-field__label">Name</span>
        <input
          type="text"
          required
          className="input"
          value={values.name}
          onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
        />
      </label>
      <label className="form-field">
        <span className="form-field__label">Description</span>
        <textarea
          className="textarea"
          value={values.description ?? ''}
          onChange={(event) => setValues((prev) => ({ ...prev, description: event.target.value }))}
          rows={3}
        />
      </label>
      <div className="form-field">
        <span className="form-field__label">Labels</span>
        <LabelsEditor values={values.labels} onChange={(next) => setValues((prev) => ({ ...prev, labels: next }))} />
      </div>
      <div className="form-field">
        <label className="checkbox-row">
          <input
            type="checkbox"
            className="checkbox"
            checked={values.internalInspectionEnabled}
            onChange={(event) => setValues((prev) => ({ ...prev, internalInspectionEnabled: event.target.checked }))}
          />
          <span>Enable internal inspection endpoints</span>
        </label>
        <span className="form-field__hint">Allow connectors in this cluster to expose on-device debug REST endpoints for secrets and credentials.</span>
      </div>
      {showActions && (
        <div className="form-actions">
          {onCancel && (
            <button type="button" className="ui-button ui-button--ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="submit" className="ui-button ui-button--primary" disabled={saving}>
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
};

interface ConnectorFormProps {
  initialValues: ConnectorFormValues;
  clusterOptions: ClusterView[];
  saving: boolean;
  submitLabel: string;
  onSubmit: (values: ConnectorFormValues) => void;
  onCancel?: () => void;
  showActions?: boolean;
  formRef?: RefObject<HTMLFormElement>;
  clusterSelectionLocked?: boolean;
}

const ConnectorForm = ({
  initialValues,
  clusterOptions,
  saving,
  submitLabel,
  onSubmit,
  onCancel,
  showActions = true,
  formRef,
  clusterSelectionLocked = false
}: ConnectorFormProps) => {
  const [values, setValues] = useState<ConnectorFormValues>(initialValues);
  const sortedClusters = useMemo(() => [...clusterOptions].sort((a, b) => (a.name ?? a.id ?? '').localeCompare(b.name ?? b.id ?? '')), [clusterOptions]);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const selectedClusterLabel = useMemo(() => {
    if (!values.clusterId) {
      return '';
    }
    const match = sortedClusters.find((cluster) => cluster.id === values.clusterId);
    return match?.name ?? match?.id ?? values.clusterId;
  }, [sortedClusters, values.clusterId]);

  return (
    <form
      ref={formRef}
      className="section-card form-grid"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      <label className="form-field">
        <span className="form-field__label">Name</span>
        <input
          type="text"
          required
          className="input"
          value={values.name}
          onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
        />
      </label>
      {clusterSelectionLocked ? (
        <div className="form-field">
          <span className="form-field__label">Cluster</span>
          <input type="text" className="input" value={selectedClusterLabel || '—'} disabled />
        </div>
      ) : (
        <label className="form-field">
          <span className="form-field__label">Cluster</span>
          <select
            required
            className="select"
            value={values.clusterId}
            onChange={(event) => setValues((prev) => ({ ...prev, clusterId: event.target.value }))}
            disabled={clusterOptions.length === 0}
          >
            <option value="">{clusterOptions.length === 0 ? 'No clusters available' : 'Select cluster…'}</option>
            {sortedClusters.map((cluster) => (
              <option key={cluster.id ?? cluster.name ?? 'cluster-option'} value={cluster.id ?? ''}>
                {cluster.name ?? cluster.id}
              </option>
            ))}
          </select>
          {clusterOptions.length === 0 && <p className="form-field__hint">Create a cluster before adding connectors.</p>}
        </label>
      )}
      <label className="form-field">
        <span className="form-field__label">Description</span>
        <textarea
          className="textarea"
          value={values.description ?? ''}
          onChange={(event) => setValues((prev) => ({ ...prev, description: event.target.value }))}
          rows={3}
        />
      </label>
      <div className="form-field">
        <span className="form-field__label">Labels</span>
        <LabelsEditor values={values.labels} onChange={(next) => setValues((prev) => ({ ...prev, labels: next }))} />
      </div>
      {showActions && (
        <div className="form-actions">
          {onCancel && (
            <button type="button" className="ui-button ui-button--ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="submit" className="ui-button ui-button--primary" disabled={saving || clusterOptions.length === 0}>
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
};
